import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import {
    LogIn, UserPlus, Play, Pause, BarChart3, Video, Settings,
    Upload, Info, Plus, Save, Trash2, List, User, Mail, Lock,
    ChevronRight, X, Check, AlertCircle, Download, Columns, Table2,
    SkipBack, SkipForward, Volume2, VolumeX, Sun, Moon, Loader2, Palette,
    Banknote, Trophy, ChevronDown, ChevronUp, Calendar,
    Share2, Copy, Link2, Users, Archive, ArchiveRestore,
    Circle, Square, MessageSquare, Bug, Lightbulb, Pencil
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = "https://snipshotsports.onrender.com";

// A player can be rostered on this many teams at once (e.g. rec + travel + school)
const MAX_PLAYER_TEAMS = 3;

// Sport-specific markers
const SPORT_MARKERS = {
    "Ice Hockey": ["Goal", "Shot", "Save", "Faceoff", "Penalty", "Turnover", "Breakout", "Regroup", "Blocked Shot", "Dump In", "Hit", "Icing", "Offside"],
    "Football": ["Touchdown", "Run", "Completion", "Incompletion", "Interception", "Fumble", "Tackle", "Sack", "Punt", "Field Goal", "Flag", "Safety"],
    "Basketball": ["Made 2PT", "Missed 2PT", "Made 3PT", "Missed 3PT", "Made Free Throw", "Missed Free Throw", "Rebound", "Steal", "Block", "Turnover", "Foul"],
    "Soccer": ["Goal", "Shot", "Save", "Pass", "Cross", "Clearance", "Tackle", "Foul", "Corner Kick", "Free Kick", "Offside", "Yellow", "Red"],
    "Lacrosse": ["Goal", "Shot", "Save", "Faceoff", "Ground Ball", "Turnover", "Clear", "Ride", "Penalty", "Hit"],
    "Baseball/Softball": ["Hit", "Home Run", "Strike", "Ball", "Walk", "Strikeout", "Out", "Error", "Stolen Base", "Double Play", "Single", "Double", "Triple"],
    "Water Polo": ["Goal", "Shot", "Save", "Block", "Turnover", "Exclusionary Foul", "Ordinary Foul", "Jump Ball", "Roll", "2-Meter", "5-Meter", "2-Meter Pass", "Foul Outside 6"],
    "Volleyball": ["Kill", "Ace", "Block", "Dig", "Set", "Serve", "Error", "Hit"],
    "Wrestling": ["Takedown", "Escape", "Reversal", "Near Fall", "Pin", "Penalty", "Stalling", "Out of Bounds"]
};

// Sport-specific default stat sheets
const SPORT_STAT_SHEETS = {
    "Ice Hockey": [
        {
            id: 1,
            name: "Skaters",
            columns: ["#", "Name", "G", "A", "PTS", "+/-", "PIM", "SOG", "Hits", "Blocks"],
            rows: [
                { id: 1, values: ["", "", "0", "0", "0", "0", "0", "0", "0", "0"] },
                { id: 2, values: ["", "", "0", "0", "0", "0", "0", "0", "0", "0"] },
                { id: 3, values: ["", "", "0", "0", "0", "0", "0", "0", "0", "0"] },
            ]
        },
        {
            id: 2,
            name: "Goalies",
            columns: ["#", "Name", "SA", "GA", "SV", "SV%", "TOI", "W", "L"],
            rows: [
                { id: 1, values: ["", "", "0", "0", "0", "0", "", "0", "0"] },
            ]
        }
    ],
    "Football": [
        {
            id: 1,
            name: "Passing",
            columns: ["#", "Name", "Comp", "Att", "Yds", "TD", "INT", "Rating"],
            rows: [
                { id: 1, values: ["", "", "0", "0", "0", "0", "0", "0"] },
            ]
        },
        {
            id: 2,
            name: "Rushing",
            columns: ["#", "Name", "Car", "Yds", "Avg", "TD", "Fum", "Long"],
            rows: [
                { id: 1, values: ["", "", "0", "0", "0", "0", "0", "0"] },
                { id: 2, values: ["", "", "0", "0", "0", "0", "0", "0"] },
            ]
        },
        {
            id: 3,
            name: "Receiving",
            columns: ["#", "Name", "Tgt", "Rec", "Yds", "Avg", "TD", "Long"],
            rows: [
                { id: 1, values: ["", "", "0", "0", "0", "0", "0", "0"] },
                { id: 2, values: ["", "", "0", "0", "0", "0", "0", "0"] },
                { id: 3, values: ["", "", "0", "0", "0", "0", "0", "0"] },
            ]
        },
        {
            id: 4,
            name: "Defense",
            columns: ["#", "Name", "Tkl", "Solo", "Sacks", "INT", "FF", "FR", "PD"],
            rows: [
                { id: 1, values: ["", "", "0", "0", "0", "0", "0", "0", "0"] },
                { id: 2, values: ["", "", "0", "0", "0", "0", "0", "0", "0"] },
            ]
        },
        {
            id: 5,
            name: "Kicking",
            columns: ["#", "Name", "FGM", "FGA", "FG%", "Long", "XPM", "XPA", "Pts"],
            rows: [
                { id: 1, values: ["", "", "0", "0", "0", "0", "0", "0", "0"] },
            ]
        }
    ],
    "Basketball": [
        {
            id: 1,
            name: "Players",
            columns: ["#", "Name", "PTS", "FGM", "FGA", "FG%", "3PM", "3PA", "FTM", "FTA"],
            rows: [
                { id: 1, values: ["", "", "0", "0", "0", "0", "0", "0", "0", "0"] },
                { id: 2, values: ["", "", "0", "0", "0", "0", "0", "0", "0", "0"] },
                { id: 3, values: ["", "", "0", "0", "0", "0", "0", "0", "0", "0"] },
            ]
        },
        {
            id: 2,
            name: "Rebounds & Assists",
            columns: ["#", "Name", "OREB", "DREB", "REB", "AST", "STL", "BLK", "TO", "PF"],
            rows: [
                { id: 1, values: ["", "", "0", "0", "0", "0", "0", "0", "0", "0"] },
                { id: 2, values: ["", "", "0", "0", "0", "0", "0", "0", "0", "0"] },
                { id: 3, values: ["", "", "0", "0", "0", "0", "0", "0", "0", "0"] },
            ]
        }
    ],
    "Soccer": [
        {
            id: 1,
            name: "Field Players",
            columns: ["#", "Name", "G", "A", "Shots", "SOG", "Pass", "Tkl", "Fouls"],
            rows: [
                { id: 1, values: ["", "", "0", "0", "0", "0", "0", "0", "0"] },
                { id: 2, values: ["", "", "0", "0", "0", "0", "0", "0", "0"] },
                { id: 3, values: ["", "", "0", "0", "0", "0", "0", "0", "0"] },
            ]
        },
        {
            id: 2,
            name: "Goalkeepers",
            columns: ["#", "Name", "SV", "GA", "SV%", "CS", "Catches", "Punches", "Clears"],
            rows: [
                { id: 1, values: ["", "", "0", "0", "0", "0", "0", "0", "0"] },
            ]
        }
    ],
    "Lacrosse": [
        {
            id: 1,
            name: "Field Players",
            columns: ["#", "Name", "G", "A", "PTS", "GB", "CT", "Shots", "SOG"],
            rows: [
                { id: 1, values: ["", "", "0", "0", "0", "0", "0", "0", "0"] },
                { id: 2, values: ["", "", "0", "0", "0", "0", "0", "0", "0"] },
                { id: 3, values: ["", "", "0", "0", "0", "0", "0", "0", "0"] },
            ]
        },
        {
            id: 2,
            name: "Faceoffs",
            columns: ["#", "Name", "FO Won", "FO Lost", "FO%", "GB"],
            rows: [
                { id: 1, values: ["", "", "0", "0", "0", "0"] },
            ]
        },
        {
            id: 3,
            name: "Goalies",
            columns: ["#", "Name", "SV", "GA", "SV%", "GB", "Clears"],
            rows: [
                { id: 1, values: ["", "", "0", "0", "0", "0", "0"] },
            ]
        }
    ],
    "Baseball/Softball": [
        {
            id: 1,
            name: "Batting",
            columns: ["#", "Name", "AB", "R", "H", "RBI", "BB", "SO", "AVG"],
            rows: [
                { id: 1, values: ["", "", "0", "0", "0", "0", "0", "0", "0"] },
                { id: 2, values: ["", "", "0", "0", "0", "0", "0", "0", "0"] },
                { id: 3, values: ["", "", "0", "0", "0", "0", "0", "0", "0"] },
            ]
        },
        {
            id: 2,
            name: "Pitching",
            columns: ["#", "Name", "IP", "H", "R", "ER", "BB", "SO", "ERA"],
            rows: [
                { id: 1, values: ["", "", "0", "0", "0", "0", "0", "0", "0"] },
                { id: 2, values: ["", "", "0", "0", "0", "0", "0", "0", "0"] },
            ]
        }
    ],
    "Water Polo": [
        {
            id: 1,
            name: "Field Players",
            columns: ["#", "Name", "G", "A", "Sh", "Stl", "Ex", "TO"],
            rows: [
                { id: 1, values: ["", "", "0", "0", "0", "0", "0", "0"] },
                { id: 2, values: ["", "", "0", "0", "0", "0", "0", "0"] },
                { id: 3, values: ["", "", "0", "0", "0", "0", "0", "0"] },
            ]
        },
        {
            id: 2,
            name: "Goalies",
            columns: ["#", "Name", "Sv", "GA", "Sv%", "Ex"],
            rows: [
                { id: 1, values: ["", "", "0", "0", "0", "0"] },
            ]
        }
    ],
    "Volleyball": [
        {
            id: 1,
            name: "Attack",
            columns: ["#", "Name", "K", "E", "TA", "Pct", "Ast", "Blk"],
            rows: [
                { id: 1, values: ["", "", "0", "0", "0", "0", "0", "0"] },
                { id: 2, values: ["", "", "0", "0", "0", "0", "0", "0"] },
                { id: 3, values: ["", "", "0", "0", "0", "0", "0", "0"] },
            ]
        },
        {
            id: 2,
            name: "Serve & Defense",
            columns: ["#", "Name", "SA", "SE", "Dig", "RE", "Pts"],
            rows: [
                { id: 1, values: ["", "", "0", "0", "0", "0", "0"] },
                { id: 2, values: ["", "", "0", "0", "0", "0", "0"] },
            ]
        }
    ],
    "Wrestling": [
        {
            id: 1,
            name: "Matches",
            columns: ["#", "Name", "Weight", "TD", "Esc", "Rev", "NF", "Pin", "Result"],
            rows: [
                { id: 1, values: ["", "", "", "0", "0", "0", "0", "0", ""] },
                { id: 2, values: ["", "", "", "0", "0", "0", "0", "0", ""] },
                { id: 3, values: ["", "", "", "0", "0", "0", "0", "0", ""] },
            ]
        }
    ]
};

// Helper to get default tables for a sport
const getDefaultTables = (sport) => {
    const templates = SPORT_STAT_SHEETS[sport] || SPORT_STAT_SHEETS["Ice Hockey"];
    // Deep clone to avoid mutation
    return JSON.parse(JSON.stringify(templates.map((t, i) => ({
        ...t,
        id: Date.now() + i,
        rows: t.rows.map((r, j) => ({ ...r, id: Date.now() + i * 100 + j }))
    }))));
};

// Derive a deduped list of players ({ number, name }) from a team's stat tables
const getRosterPlayers = (tables) => {
    const seen = new Map();
    (tables || []).forEach((t) => {
        t.rows.forEach((r) => {
            const number = (r.values[0] || "").trim();
            const name = (r.values[1] || "").trim();
            if (number || name) {
                const key = `${number}|${name}`;
                if (!seen.has(key)) seen.set(key, { number, name });
            }
        });
    });
    return Array.from(seen.values());
};

// Find every stat-sheet row belonging to a given player (identified by "number|name")
// across a team's tables. A player can have a row in more than one table (e.g. a
// football player showing up in both Rushing and Receiving), so this returns all of them.
const getPlayerStatRows = (tables, playerKey) => {
    const rows = [];
    (tables || []).forEach((t) => {
        t.rows.forEach((r) => {
            const number = (r.values[0] || "").trim();
            const name = (r.values[1] || "").trim();
            if (`${number}|${name}` === playerKey) {
                rows.push({ tableId: t.id, tableName: t.name, columns: t.columns, rowId: r.id, values: r.values });
            }
        });
    });
    return rows;
};

// Format a clip's marker time as m:ss for display
const formatClipTime = (t) => {
    const total = Math.max(0, Math.floor(t || 0));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
};

// When a coach switches sport, the old tables' stat columns (hockey's G/A/PIM, say)
// no longer mean anything for the new sport's tables (basketball's PTS/REB). Rebuild
// from the new sport's template, but carry the player numbers/names into its first
// table so switching sports mid-season doesn't wipe out the roster the coach already
// entered — just the old sport's now-meaningless stat columns.
const remapTablesToSport = (oldTables, newSport) => {
    const freshTables = getDefaultTables(newSport);
    const players = getRosterPlayers(oldTables);
    if (players.length === 0 || freshTables.length === 0) return freshTables;

    const [firstTable, ...restTables] = freshTables;
    const rows = players.map((p, i) => {
        const values = firstTable.columns.map(() => "");
        values[0] = p.number;
        values[1] = p.name;
        return { id: Date.now() + i, values };
    });
    return [{ ...firstTable, rows }, ...restTables];
};

const aggregateSeasonStats = (sessions, team) => {
    // Column sets can differ from game to game (a column added or removed for
    // one game, kept for others). Rather than assuming every game's table has
    // the same columns in the same positions — which crashes as soon as one
    // game's row is shorter/longer than the first game's column list — we key
    // each stat by its COLUMN NAME and build up the union of columns seen for
    // a table across all games. Any game missing a given column simply
    // doesn't contribute to that stat for that game.
    const tablesByName = new Map();

    (sessions || []).forEach((session) => {
        const tables = team === "home" ? session.homeTables : session.awayTables;
        (tables || []).forEach((table) => {
            if (!tablesByName.has(table.name)) {
                tablesByName.set(table.name, { statColumns: [], players: new Map() });
            }
            const entry = tablesByName.get(table.name);
            const statCols = table.columns.slice(2);

            // Grow the union of stat columns, preserving first-seen order
            statCols.forEach((col) => {
                if (!entry.statColumns.includes(col)) entry.statColumns.push(col);
            });

            table.rows.forEach((row) => {
                const number = (row.values[0] || "").trim();
                const name = (row.values[1] || "").trim();
                if (!number && !name) return;
                const key = `${number}|${name}`;

                if (!entry.players.has(key)) {
                    entry.players.set(key, { number, name, stats: new Map() });
                }
                const player = entry.players.get(key);

                statCols.forEach((col, i) => {
                    const val = row.values[i + 2];
                    const num = parseFloat(val);
                    if (isNaN(num)) return;
                    if (!player.stats.has(col)) player.stats.set(col, { total: 0, count: 0 });
                    const s = player.stats.get(col);
                    s.total += num;
                    s.count += 1;
                });
            });
        });
    });

    return Array.from(tablesByName.entries()).map(([name, entry]) => ({
        name,
        columns: ["#", "Name", ...entry.statColumns],
        players: Array.from(entry.players.values())
            .map((p) => ({
                number: p.number,
                name: p.name,
                totals: entry.statColumns.map((col) => {
                    const s = p.stats.get(col);
                    if (!s) return 0;
                    const isPercent = col.includes("%");
                    if (isPercent) return s.count > 0 ? Math.round(s.total / s.count) : 0;
                    return s.total;
                }),
            }))
            .sort((a, b) => {
                const an = parseFloat(a.number), bn = parseFloat(b.number);
                if (!isNaN(an) && !isNaN(bn)) return an - bn;
                return a.name.localeCompare(b.name);
            }),
    }));
};

// Compute win/loss/tie record from saved sessions that include a final score
const computeTeamRecord = (sessions) => {
    return (sessions || []).reduce((acc, s) => {
        if (!s.includeScore || s.homeScore === null || s.homeScore === undefined || s.awayScore === null || s.awayScore === undefined) return acc;
        if (s.homeScore > s.awayScore) acc.wins++;
        else if (s.homeScore < s.awayScore) acc.losses++;
        else acc.ties++;
        return acc;
    }, { wins: 0, losses: 0, ties: 0 });
};

// Shared game-card formatting helpers (used by both the coach's Saved Games list
// and the player's per-game cards, so the two views stay visually consistent)
const formatSessionDate = (ts) => {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const getSessionResult = (s) => {
    if (!s.includeScore || s.homeScore === null || s.homeScore === undefined || s.awayScore === null || s.awayScore === undefined) return null;
    if (s.homeScore > s.awayScore) return "W";
    if (s.homeScore < s.awayScore) return "L";
    return "T";
};

const sessionResultBadgeClass = (r) => {
    if (r === "W") return "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30";
    if (r === "L") return "bg-red-500/15 text-red-400 border border-red-500/30";
    return "bg-[--bg-tertiary] text-[--text-muted] border border-[--border-default]";
};

// Generate a 6-char team join code (no 0/O/1/I ambiguity)
const generateTeamCode = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    return Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
};

// Create a team doc for a coach and return its id/code
const createTeam = async (coachUid, name, sport) => {
    const { doc, setDoc, collection, serverTimestamp } = window.firebaseFirestore;
    const code = generateTeamCode();
    const teamRef = doc(collection(firebaseDb, "teams"));
    await setDoc(teamRef, { name, sport, coachUid, code, createdAt: serverTimestamp(), players: {} });
    return { id: teamRef.id, code };
};

// Free tier: how many clip "sends" a non-premium coach gets per month (a send can include many clips/players)
const FREE_CLIP_LIMIT_PER_MONTH = 15;

// Firebase configuration
const firebaseConfig = {
    apiKey: "AIzaSyAypRrGQWCs31aJkWRcvZc1y8ky4_80OB8",
    authDomain: "snipshotsports-c49d5.firebaseapp.com",
    projectId: "snipshotsports-c49d5",
    storageBucket: "snipshotsports-c49d5.firebasestorage.app",
    messagingSenderId: "945753360006",
    appId: "1:945753360006:web:a39214c21da30595f67307"
};

let firebaseApp = null;
let firebaseAuth = null;
let firebaseDb = null;
let firebaseInitialized = false;
let firebaseStorage = null;

const initFirebase = async () => {
    if (firebaseInitialized) return { auth: firebaseAuth, db: firebaseDb };

    try {
        const { initializeApp } = await import("firebase/app");
        const { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword,
            signOut, GoogleAuthProvider, EmailAuthProvider, signInWithPopup, onAuthStateChanged,
            fetchSignInMethodsForEmail, reauthenticateWithCredential, reauthenticateWithPopup, deleteUser,
            sendPasswordResetEmail, updatePassword } = await import("firebase/auth");
        const { getFirestore, doc, setDoc, getDoc, updateDoc, deleteDoc, collection, query, orderBy, getDocs,
            where, arrayUnion, arrayRemove, serverTimestamp, deleteField, onSnapshot } = await import("firebase/firestore");
        const { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } = await import("firebase/storage");

        firebaseApp = initializeApp(firebaseConfig);
        firebaseAuth = getAuth(firebaseApp);
        firebaseDb = getFirestore(firebaseApp);
        firebaseStorage = getStorage(firebaseApp);
        firebaseInitialized = true;

        window.firebaseAuth = {
            signInWithEmailAndPassword, createUserWithEmailAndPassword,
            signOut, GoogleAuthProvider, EmailAuthProvider, signInWithPopup, onAuthStateChanged,
            fetchSignInMethodsForEmail, reauthenticateWithCredential, reauthenticateWithPopup, deleteUser,
            sendPasswordResetEmail, updatePassword
        };
        window.firebaseFirestore = { doc, setDoc, getDoc, updateDoc, deleteDoc, collection, query, orderBy, getDocs, where, arrayUnion, arrayRemove, serverTimestamp, deleteField, onSnapshot };
        window.firebaseStorage = { ref, uploadBytesResumable, getDownloadURL, deleteObject };

        return { auth: firebaseAuth, db: firebaseDb };
    } catch (error) {
        console.error("Firebase initialization error:", error);
        return null;
    }
};

// Logo Component
const Logo = () => (
    <div className="flex items-center gap-3" data-testid="logo">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[var(--accent-home)] to-[color-mix(in_srgb,var(--accent-home)_80%,black)] flex items-center justify-center shadow-lg" style={{ boxShadow: '0 4px 20px var(--accent-home-glow)' }}>
            <Play className="w-6 h-6 text-white ml-1" />
        </div>
        <span className="heading-primary text-2xl text-[--text-primary]">Snipshot Sports</span>
    </div>
);

// Theme Toggle Component
const ThemeToggle = ({ theme, setTheme }) => (
    <div className="theme-toggle" data-testid="theme-toggle">
        <button
            onClick={() => setTheme("dark")}
            className={`theme-toggle-btn ${theme === "dark" ? "active" : ""}`}
            data-testid="theme-dark-btn"
        >
            <Moon className="w-4 h-4" />
        </button>
        <button
            onClick={() => setTheme("light")}
            className={`theme-toggle-btn ${theme === "light" ? "active" : ""}`}
            data-testid="theme-light-btn"
        >
            <Sun className="w-4 h-4" />
        </button>
    </div>
);

// Header Component
const Header = ({ user, onLogout, theme, setTheme, autosaveStatus }) => (
    <header className="flex items-center justify-between px-6 py-4 border-b border-[--border-default]" data-testid="header">
        <Logo />
        <div className="flex items-center gap-4">
            {autosaveStatus === "saving" && <span className="text-xs text-[--text-muted]" data-testid="autosave-status">Saving...</span>}
            {autosaveStatus === "saved" && <span className="text-xs text-[--text-muted]" data-testid="autosave-status">Saved</span>}
            {autosaveStatus === "error" && <span className="text-xs text-[--text-muted]" data-testid="autosave-status">Autosave failed</span>}
            <ThemeToggle theme={theme} setTheme={setTheme} />
            {user && (
                <button
                    onClick={onLogout}
                    className="btn-secondary text-sm"
                    data-testid="logout-btn"
                >
                    Sign Out
                </button>
            )}
        </div>
    </header>
);

// ---- Legal (Terms of Service / Privacy Policy) ----
// Bump this whenever the text below changes in a way that matters (new data use,
// new fees, etc). Existing users whose stored acceptance version doesn't match
// this get re-gated on next login (see requiresLegalReacceptance in App).
const LEGAL_VERSION = "2026-10-24";

// TODO: Replace with your actual, lawyer-reviewed (or Termly/Rocket Lawyer generated)
// Terms of Service. This is placeholder structure only, not legal advice.
const TERMS_OF_SERVICE_TEXT = `Last updated: October 24, 2026

Please read these Terms of Service (“Terms”) carefully. They are a binding agreement between you and Snipshot Sports LLC (“SnipShot Sports,” “we,” “us,” or “our”) governing your use of the SnipShot Sports website and app (the “Service”).

1. Acceptance of Terms
By creating an account, checking the agreement box at signup, or otherwise using the Service, you agree to these Terms and to our Privacy Policy. If you do not agree, do not use the Service. If you use the Service on behalf of a team, school, club, or other organization, you confirm that you have authority to accept these Terms on its behalf.

2. Eligibility and Minors
You must be at least 13 years old to create an account. If you are under 18, you may use the Service only with the permission of a parent or legal guardian, who is responsible for your use of it and agrees to these Terms on your behalf. Coach accounts must be held by adults (18 or older). The Service is not intended for children under 13, and we do not allow them to hold accounts; if we learn that one does, we may delete the account.

3. The Service
SnipShot Sports lets coaches and players upload or record game video, mark events, track stats, build clip compilations, and share clips and stats with teammates and other coaches. There are two account types: Coach accounts, which manage teams, rosters, video, and sharing, and Player accounts, which join a team with a team code and view the stats and clips a coach shares. Features differ between the free plan and paid plans, and we may add, change, or remove features over time.

4. Accounts and Security
You are responsible for the accuracy of the information you provide, for keeping your login credentials confidential, and for all activity under your account. Do not share your login with others or let others use your account. Tell us promptly at snipshotsportsllc@gmail.com if you believe your account has been compromised. Team codes grant access to a team, so share them only with people you intend to join it.

5. Subscriptions and Billing
Some features require a paid subscription (“Snipshot+”). Subscriptions are billed through our payment processor, Stripe, at the price and billing interval shown at checkout. Unless you cancel, your subscription renews automatically at the end of each billing period and you authorize us and Stripe to charge your payment method for each renewal. You can cancel at any time from Settings by choosing Manage Subscription. Cancellation takes effect at the end of your current paid period, and you keep paid features until then. All payments are non-refundable, and we do not provide refunds or credits for partial billing periods or unused time, except where a refund is required by law. We may change prices or plan features with advance notice; changes apply from your next renewal, and you may cancel before then. You are responsible for any applicable taxes. If a payment fails, we may retry it, and we may downgrade your account to the free plan. We do not store your full card number; payment details are handled by Stripe under its own terms and privacy policy. The free plan may carry usage limits, such as a monthly cap on clip shares.

6. Your Content
“Your Content” means the video, clips, markers, notes, stats, rosters, names, feedback, and other material you upload, record, or enter into the Service. You keep ownership of Your Content. You grant us a limited, non-exclusive, worldwide, royalty-free license to host, store, copy, process, transmit, and display Your Content solely as needed to operate, secure, and provide the Service to you and to the people you choose to share it with (for example, cutting video into clips and compilations, and making shared clips viewable by recipients). We do not use Your Content for advertising and do not sell it.

7. Your Responsibilities for Content and People in Your Videos
You are solely responsible for Your Content and for having every right, permission, and consent needed to upload, record, store, and share it. Game video often shows other people, including minors, opponents, officials, and spectators. Before you upload, record, or share video or personal information, you must make sure you have obtained any consents required by law and by your school, league, club, or venue, including parent or guardian consent for players under 18 where required. Laws on recording, especially audio recording, vary by location, and you must follow them. Do not upload content that you do not have the right to use, that infringes anyone’s rights, or that violates the law.

8. Sharing
You control what you share and with whom. When you share clips, stats, or a game session, the people you share with can view that content and, for coach-to-coach session sharing, may be able to copy it into their own account. Recipients may keep copies they have already claimed even if you later delete your account or the original. Choose recipients carefully. Coaches are responsible for sharing player information and video only with appropriate people. We are not responsible for what recipients do with content you share.

9. Acceptable Use
You agree not to:

upload, share, or request content that is unlawful, harassing, threatening, hateful, sexually explicit, or exploitative, or that depicts or targets minors in an inappropriate way;
use the Service to stalk, bully, embarrass, or surveil any person, or to record people where they have a reasonable expectation of privacy;
upload malware, or attempt to disrupt, overload, probe, or gain unauthorized access to the Service, its servers, or other users’ accounts or data;
scrape, copy, resell, or build a competing product from the Service, or reverse engineer or bypass any technical limits, security measures, or paywalls;
misuse free-plan limits through multiple accounts or other workarounds;
use the Service to store or distribute large amounts of content that is unrelated to sports, team analysis, or coaching; or
impersonate anyone, or provide false information about who you are or your authority over a team.

10. Our Rights and Intellectual Property
The Service, including its software, design, text, graphics, and branding (other than Your Content), is owned by us or our licensors and is protected by intellectual property laws. We grant you a limited, revocable, non-transferable license to use the Service for its intended purpose in accordance with these Terms. If you send us feedback or suggestions, you grant us permission to use them without restriction or compensation.

11. Copyright Complaints
If you believe content on the Service infringes your copyright, email snipshotsportsllc@gmail.com with your contact information, a description of the work and where the material appears, a statement that you have a good-faith belief the use is not authorized, and a statement, under penalty of perjury, that your information is accurate and that you are the owner or authorized to act for the owner. We may remove the material and may terminate accounts of repeat infringers.

12. Third-Party Services
The Service relies on third-party providers, including Google Firebase (authentication, database, and file storage), Stripe (payments), and cloud hosting providers. Your use of those services may be subject to their own terms. We are not responsible for third-party services or for outages or errors that originate with them.

13. Storage, Availability, and Backups
We work to keep the Service available and your data safe, but the Service is not guaranteed to be uninterrupted or error-free, and it is not a backup service. Keep your own copies of important video and data. We may apply reasonable limits on storage, file size, and usage, and may perform maintenance or changes that temporarily affect availability.

14. Content Removal, Suspension, and Termination
You may stop using the Service and delete your account at any time from Settings. We may remove content, suspend, or terminate your access if we reasonably believe you have violated these Terms or the law, if your use creates risk or harm for others or for us, if payments are not made, or if required by law. We will try to give notice when reasonable, but we may act without notice where needed to protect people or the Service. When an account is deleted, we delete or de-identify its data as described in the Privacy Policy. Provisions that by their nature should survive termination (including sections on Your Content, Disclaimers, Limitation of Liability, Indemnification, and Dispute Resolution) will survive.

15. Disclaimers
THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE,” WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT, TO THE FULLEST EXTENT PERMITTED BY LAW. WE DO NOT WARRANT THAT THE SERVICE WILL BE SECURE, ACCURATE, OR FREE OF ERRORS OR DATA LOSS. Stats, tallies, and clip timing are based on information entered or marked by users and may contain errors. The Service is a tool to support coaching and review; it does not replace professional judgment, and we are not responsible for coaching, roster, playing-time, recruiting, or other decisions made using it.

16. Limitation of Liability
TO THE FULLEST EXTENT PERMITTED BY LAW, WE WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR ANY LOSS OF PROFITS, REVENUE, DATA, OR GOODWILL, ARISING OUT OF OR RELATED TO THE SERVICE OR THESE TERMS, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. OUR TOTAL LIABILITY FOR ANY CLAIM RELATING TO THE SERVICE OR THESE TERMS WILL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID US IN THE 12 MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM AND (B) US$100. Some jurisdictions do not allow certain limitations, so some of the above may not apply to you, and nothing in these Terms limits liability that cannot be limited under applicable law.

17. Indemnification
You agree to defend, indemnify, and hold harmless SnipShot Sports and its owners, operators, and contractors from claims, damages, losses, and expenses (including reasonable attorneys’ fees) arising out of Your Content, your use of the Service, your sharing of video or personal information of others, or your violation of these Terms or of any law or third party’s rights.

18. Dispute Resolution and Governing Law
Before filing a claim, you agree to contact us at snipshotsportsllc@gmail.com and give us at least 30 days to try to resolve the dispute informally. These Terms are governed by the laws of the Commonwealth of Virginia, without regard to conflict-of-law rules. Any dispute that is not resolved informally will be brought exclusively in the state or federal courts located in Virginia, and you and we consent to their jurisdiction and venue, except that either party may seek relief in small claims court where eligible. To the extent permitted by law, claims must be brought within one year after they arise.

19. Changes to the Service and These Terms
We may update these Terms from time to time. When we make material changes, we will update the date above and ask you to accept the new Terms the next time you sign in; continuing to use the Service after that means you accept them. If you do not agree to the changes, you should stop using the Service and delete your account.

20. General
These Terms and the Privacy Policy are the entire agreement between you and us about the Service. If any part is found unenforceable, the rest remains in effect. Our failure to enforce a provision is not a waiver. You may not assign these Terms without our consent; we may assign them in connection with a merger, sale, or reorganization. We may give you notices by email or through the Service.

21. Contact
Questions about these Terms? Contact us at snipshotsportsllc@gmail.com.`;

// TODO: Replace with your actual Privacy Policy (what you collect, why, and who
// you share it with — e.g. Firebase/Google Cloud, Stripe). This is placeholder only.
const PRIVACY_POLICY_TEXT = `Last updated: October 24, 2026

This Privacy Policy explains how Snipshot Sports LLC (“SnipShot Sports,” “we,” “us,” or “our”) collects, uses, shares, and protects information when you use the SnipShot Sports website and app (the “Service”). By using the Service you agree to this policy.

1. Information We Collect
Information you give us:

Account information: your email address and password (passwords are handled by our authentication provider and we do not see them in plain text), or, if you sign in with Google, the basic profile information Google provides (such as name and email).
Profile information: your first and last name, your role (coach or player), and your sport.
Team information: team names and join codes, and for players, the display name you provide when you join a team.
Your Content: game video you upload or record, clip markers and timestamps, notes on clips and games, tags linking clips to players, rosters (player names and numbers), and stat sheets and other stats you enter. This can include information about other people, such as teammates, opponents, and minors.
Feedback: messages you send through the Feedback tab, along with your account ID, email address, the page you were on, and your browser and device type.
Communications: information you include when you contact us.

Information collected automatically:

Subscription information: whether you have a paid plan and related billing identifiers from Stripe. Our payment processor collects your payment details; we do not receive or store your full card number.
Acceptance records: the version of our Terms and Privacy Policy you accepted and when.
Usage and technical data: basic information such as IP address, browser and device type, and logs generated by our servers and hosting providers when you use the Service, used to operate and secure it.
Browser storage: small pieces of data stored on your device, for example to keep you signed in and remember your layout and display preferences.

2. How We Use Information
We use information to:

create and maintain your account and authenticate you;
host, process, and display your video, clips, stats, and notes, and build the clip compilations and downloads you request;
let you share clips, stats, and game sessions with the teammates and coaches you choose;
process subscriptions and payments, enforce plan limits, and manage your plan;
respond to support requests and feedback;
send service messages, such as account, security, billing, and policy notices;
keep the Service secure, prevent fraud and abuse, and troubleshoot problems;
improve and develop the Service; and
comply with legal obligations and enforce our Terms.

We do not use Your Content or the information of minors for advertising, and we do not sell personal information.

3. How We Share Information
We share information only in the following ways:

With people you choose: When you share clips, stats, or a game session, the recipients can see that content. Players on a team can see the stats and clips their coach shares with them, and coaches can see the names that players provide when they join a team. If you share a game session with another coach, they may be able to copy it into their own account, and copies they claim remain theirs even if you later delete your account.
With service providers: Companies that help us run the Service, under obligations to protect the information and use it only for our purposes. These currently include Google Firebase (authentication, database, and file storage), Stripe (payments and subscription management), and our cloud hosting and database providers (for example, Render for backend servers). When you download a compilation, your video is sent to our servers to cut and assemble the clips.
For legal and safety reasons: If we believe in good faith that disclosure is required by law or legal process, or is needed to protect the rights, safety, or property of our users, the public, or us, or to investigate fraud, abuse, or violations of our Terms.
In a business transfer: If we are involved in a merger, acquisition, financing, or sale of assets, information may be transferred to the successor, subject to this policy.
With your consent: When you ask us to share information or otherwise agree to it.

4. Video Processing
Video you upload is stored in your account’s cloud storage. When you request a compilation download, the video (or a link to it) is sent to our backend to produce the file; we use it only for that purpose, and temporary working copies are deleted after processing.

5. Cookies and Similar Technologies
We use browser storage and similar technologies that are needed to sign you in, keep your session, and remember preferences such as theme, layout, and cached plan status. We do not use advertising cookies. Our service providers may use similar technologies to deliver their services (for example, Stripe’s checkout for fraud prevention). Most browsers let you block or clear storage, but parts of the Service may not work if you do. The Service does not respond to “Do Not Track” signals.

6. Data Retention
We keep your information for as long as your account is active and as needed to provide the Service. When you delete your account from Settings, we delete your account, profile, uploaded video, sessions, and associated data promptly, except that we may retain:

billing and transaction records for as long as tax, accounting, and legal rules require;
records of your acceptance of our Terms and Privacy Policy, to demonstrate consent;
information needed to resolve disputes, prevent fraud and abuse, or enforce our agreements;
feedback you sent us, which we may keep in a form that is not linked to you; and
residual copies in backups or logs, until they are overwritten in the ordinary course.

Content that other users have already claimed or copied into their own accounts is theirs to manage and is not deleted when you delete yours.

7. Security
We use reasonable administrative, technical, and organizational safeguards, including encrypted connections and access controls through our cloud providers, to protect your information. However, no system is completely secure, and we cannot guarantee absolute security. Please use a strong, unique password and keep your login confidential. If a breach affects your information, we will notify you and regulators as required by law.

8. Children and Minors
The Service is not intended for children under 13, and we do not knowingly collect personal information from children under 13 or allow them to hold accounts. If you believe a child under 13 has given us personal information, contact us at snipshotsportsllc@gmail.com and we will delete it. Coach accounts must be held by adults (18 or older). Players aged 13 to 17 may use the Service only with the permission of a parent or legal guardian. Coaches and other users who upload or share video, names, or stats of players who are minors are responsible for obtaining any parent or guardian consent required by law or by their school, league, or club. Parents and guardians may contact us to review or request deletion of information about their child, and we may need to verify your relationship to the child first.

9. Your Rights and Choices
You can review and update much of your profile information in the Service, delete uploaded sessions and clips, cancel your subscription from Settings, and delete your account from Settings. You may also contact us at snipshotsportsllc@gmail.com to ask us to:

tell you what personal information we hold about you, and provide a copy;
correct inaccurate information;
delete your information; or
limit or stop certain uses of it.

We will respond within the time required by applicable law and may need to verify your identity first. We will not discriminate against you for exercising your rights.

Residents of certain U.S. states, including Virginia, California, Colorado, Connecticut, and others, may have additional rights under their state privacy laws, such as the right to know, access, correct, and delete personal information, to obtain a portable copy, and to opt out of the sale of personal information, targeted advertising, or certain profiling. We do not sell personal information or use it for targeted advertising. If we decline a request, you may appeal by replying to our decision, and you may also contact your state attorney general. You may use an authorized agent to make a request on your behalf, and we may ask for proof of authorization.

10. International Users
We operate in the United States, and your information is processed and stored in the United States and other countries where our service providers operate, which may have different data protection laws than your country. By using the Service, you understand your information will be transferred to and processed in those places. If you are in the European Economic Area, United Kingdom, or a similar region, our legal bases for processing are: performing our contract with you, our legitimate interests in running and securing the Service, your consent where requested, and compliance with legal obligations. You may also have the right to object to or restrict processing, to data portability, and to complain to your local data protection authority.

11. Third-Party Links and Services
The Service may link to or rely on third-party sites and services, such as Stripe and Google, that have their own privacy practices. We are not responsible for them, and we encourage you to read their policies.

12. Changes to This Policy
We may update this policy from time to time. When we make material changes, we will update the date above and ask you to accept the revised policy the next time you sign in.

13. Contact
Questions or requests about privacy? Contact us at snipshotsportsllc@gmail.com.`;

// Scrollable modal for viewing ToS/Privacy text. Used both during signup (via
// inline links) and anytime afterward from Settings.
const LegalModal = ({ type, onClose }) => {
    const title = type === "privacy" ? "Privacy Policy" : "Terms of Service";
    const text = type === "privacy" ? PRIVACY_POLICY_TEXT : TERMS_OF_SERVICE_TEXT;
    return (
        <div className="modal-overlay" onClick={onClose} data-testid={`legal-modal-${type}`}>
            <div className="modal max-w-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{title}</h2>
                    <button onClick={onClose} className="modal-close" data-testid="legal-modal-close">
                        <X size={20} />
                    </button>
                </div>
                <div className="text-sm text-[--text-secondary] whitespace-pre-wrap max-h-[60vh] overflow-y-auto pr-2">
                    {text}
                </div>
                <div className="modal-buttons mt-4">
                    <button onClick={onClose} className="btn-primary w-full">Close</button>
                </div>
            </div>
        </div>
    );
};

// ---- Announcement banner ----
// Controlled by the Firestore doc appConfig/announcement:
//   { enabled: boolean, message: string, id?: string }
// Small dismissible bar at the top. Once a user clicks X it stays gone for that
// announcement (remembered per device in localStorage). To show a NEW announcement to
// people who dismissed the last one, change `id` (or just change the message if you
// leave `id` out).
const DISMISSED_ANNOUNCEMENT_KEY = "snipshotDismissedAnnouncement";

const AnnouncementBanner = ({ announcement, onDismiss }) => {
    if (!announcement?.enabled || !announcement.message) return null;
    return (
        <div className="flex items-center gap-3 px-4 py-2 text-sm bg-[--accent-home] text-white" role="status" data-testid="announcement-banner">
            <Info className="w-4 h-4 shrink-0" />
            <p className="flex-1 min-w-0 break-words">{announcement.message}</p>
            <button onClick={onDismiss} className="shrink-0 p-1 rounded hover:bg-white/20" aria-label="Dismiss announcement" data-testid="announcement-dismiss">
                <X size={16} />
            </button>
        </div>
    );
};

// ---- Maintenance lock ----
// Controlled by the Firestore doc appConfig/maintenance: { enabled: boolean, message?: string }.
// Everyone except this account sees the lock screen while enabled. This UID is the same
// app-owner UID used by isAppOwner() in firestore.rules. Change it if you want a different
// account to bypass the lock.
const MAINTENANCE_BYPASS_UID = "i0a9nFJ6D0caTBBBzUwCm60lsdT2";
const DEFAULT_MAINTENANCE_MESSAGE = "SnipShot Sports is down for scheduled maintenance. We'll be back shortly.";

const MaintenanceScreen = ({ message, signedIn, onLogout, onStaffLogin, theme, setTheme, user }) => (
    <div className="min-h-screen flex flex-col" data-testid="maintenance-screen">
        <Header user={user} onLogout={onLogout} theme={theme} setTheme={setTheme} />
        <main className="flex-1 flex items-center justify-center p-6">
            <div className="text-center max-w-md">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-[--bg-tertiary]">
                    <Settings className="w-8 h-8 text-[--accent-home]" />
                </div>
                <h1 className="text-2xl font-bold text-[--text-primary] mb-2">Down for maintenance</h1>
                <p className="text-[--text-secondary] mb-6 whitespace-pre-wrap">{message || DEFAULT_MAINTENANCE_MESSAGE}</p>
                {signedIn
                    ? <button onClick={onLogout} className="btn-secondary" data-testid="maintenance-logout">Log out</button>
                    : <button onClick={onStaffLogin} className="text-xs text-[--text-muted] hover:underline" data-testid="maintenance-staff-login">Staff login</button>}
            </div>
        </main>
    </div>
);

// Blocking re-acceptance prompt shown when LEGAL_VERSION changes. No X button and
// no backdrop-click-to-close: the user either accepts or logs out.
const LegalReacceptModal = ({ onAccept, onLogout }) => {
    const [view, setView] = useState("tos"); // "tos" | "privacy"
    const [agreed, setAgreed] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const text = view === "privacy" ? PRIVACY_POLICY_TEXT : TERMS_OF_SERVICE_TEXT;

    const handleAccept = async () => {
        setSaving(true); setError("");
        try { await onAccept(); }
        catch (e) { console.error("Legal acceptance failed:", e); setError("Couldn't save your acceptance. Please try again."); setSaving(false); }
    };

    return (
        <div className="modal-overlay" data-testid="legal-reaccept-modal">
            <div className="modal max-w-2xl">
                <div className="modal-header">
                    <h2>We've updated our Terms</h2>
                </div>
                <p className="text-sm text-[--text-secondary] mb-3">
                    Our Terms of Service and Privacy Policy have changed. Please review and accept them to keep using SnipShot Sports.
                </p>
                <div className="flex gap-2 mb-3">
                    <button onClick={() => setView("tos")} className={view === "tos" ? "btn-primary" : "btn-secondary"}>Terms of Service</button>
                    <button onClick={() => setView("privacy")} className={view === "privacy" ? "btn-primary" : "btn-secondary"}>Privacy Policy</button>
                </div>
                <div className="text-sm text-[--text-secondary] whitespace-pre-wrap max-h-[40vh] overflow-y-auto pr-2 mb-3">
                    {text}
                </div>
                <label className="flex items-start gap-2 text-sm text-[--text-primary] mb-3">
                    <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="legal-reaccept-checkbox" className="mt-1" />
                    <span>I have read and agree to the updated Terms of Service and Privacy Policy.</span>
                </label>
                {error && <p className="text-sm text-red-500 mb-2">{error}</p>}
                <div className="modal-buttons flex gap-2">
                    <button onClick={handleAccept} disabled={!agreed || saving} className="btn-primary flex-1" data-testid="legal-reaccept-confirm">
                        {saving ? "Saving..." : "Accept & Continue"}
                    </button>
                    <button onClick={onLogout} disabled={saving} className="btn-secondary" data-testid="legal-reaccept-logout">
                        Log out
                    </button>
                </div>
            </div>
        </div>
    );
};

// Small footer on the logged-out login/signup screen so anyone can read the
// legal text without needing an account first.
const AuthFooter = () => {
    const [legalModal, setLegalModal] = useState(null); // "tos" | "privacy" | null
    return (
        <footer className="p-4 text-center text-xs text-[--text-muted]">
            <button onClick={() => setLegalModal("tos")} className="hover:underline" data-testid="footer-tos-link">Terms of Service</button>
            <span className="mx-2">·</span>
            <button onClick={() => setLegalModal("privacy")} className="hover:underline" data-testid="footer-privacy-link">Privacy Policy</button>
            {legalModal && <LegalModal type={legalModal} onClose={() => setLegalModal(null)} />}
        </footer>
    );
};

// Login Component
const LoginForm = ({ onSwitchToSignup, onLogin, onForgotPassword, error }) => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [resetMode, setResetMode] = useState(false);
    const [resetEmail, setResetEmail] = useState("");
    const [resetStatus, setResetStatus] = useState(""); // "", "sending", "sent", "error"

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        await onLogin(email, password);
        setLoading(false);
    };

    const handleGoogleLogin = async () => {
        setLoading(true);
        await onLogin(null, null, true);
        setLoading(false);
    };

    const handleResetSubmit = async (e) => {
        e.preventDefault();
        setResetStatus("sending");
        await onForgotPassword(resetEmail);
        // Always show the same confirmation regardless of whether the email exists,
        // so this can't be used to enumerate registered accounts.
        setResetStatus("sent");
    };

    if (resetMode) {
        return (
            <div className="w-full max-w-md mx-auto animate-in" data-testid="reset-password-form">
                <div className="card-base p-8">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[var(--accent-home)] to-[color-mix(in_srgb,var(--accent-home)_80%,black)] flex items-center justify-center">
                            <Lock className="w-8 h-8 text-white" />
                        </div>
                        <h1 className="heading-primary text-3xl text-[--text-primary]">Reset Password</h1>
                        <p className="text-[--text-secondary] mt-2">We'll email you a link to reset it</p>
                    </div>

                    {resetStatus === "sent" ? (
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 text-green-400 text-sm bg-green-400/10 p-3 rounded-lg" data-testid="reset-sent">
                                <Check className="w-4 h-4" />
                                If an account exists for {resetEmail}, a reset link is on its way.
                            </div>
                            <p className="text-xs text-[--text-muted] text-center" data-testid="reset-spam-hint">
                                Don't see it in a few minutes? Check your spam or junk folder.
                            </p>
                            <button onClick={() => { setResetMode(false); setResetStatus(""); }} className="btn-primary w-full" data-testid="reset-back-to-login">Back to Sign In</button>
                        </div>
                    ) : (
                        <form onSubmit={handleResetSubmit} className="space-y-4">
                            <input type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} placeholder="Email address" className="input-base" required data-testid="reset-email" />
                            <button type="submit" className="btn-primary w-full" disabled={resetStatus === "sending"} data-testid="reset-submit">{resetStatus === "sending" ? "Sending..." : "Send Reset Link"}</button>
                            <button type="button" onClick={() => { setResetMode(false); setResetStatus(""); }} className="btn-secondary w-full" data-testid="reset-cancel">Cancel</button>
                        </form>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-md mx-auto animate-in" data-testid="login-form">
            <div className="card-base p-8">
                <div className="text-center mb-8">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[var(--accent-home)] to-[color-mix(in_srgb,var(--accent-home)_80%,black)] flex items-center justify-center">
                        <LogIn className="w-8 h-8 text-white" />
                    </div>
                    <h1 className="heading-primary text-3xl text-[--text-primary]">Welcome Back</h1>
                    <p className="text-[--text-secondary] mt-2">Sign in to your account</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" className="input-base" required data-testid="login-email" />
                    <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="input-base" required data-testid="login-password" />
                    <div className="text-right -mt-2">
                        <button type="button" onClick={() => { setResetEmail(email); setResetMode(true); }} className="text-sm text-[--accent-home] hover:underline" data-testid="forgot-password-link">Forgot password?</button>
                    </div>
                    {error && <div className="flex items-center gap-2 text-red-400 text-sm bg-red-400/10 p-3 rounded-lg" data-testid="login-error"><AlertCircle className="w-4 h-4" />{error}</div>}
                    <button type="submit" className="btn-primary w-full" disabled={loading} data-testid="login-submit">{loading ? "Signing in..." : "Sign In"}</button>
                </form>

                <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[--border-default]"></div></div>
                    <div className="relative flex justify-center text-sm"><span className="px-4 bg-[--bg-card] text-[--text-muted]">or continue with</span></div>
                </div>

                <button onClick={handleGoogleLogin} className="btn-secondary w-full flex items-center justify-center gap-3" disabled={loading} data-testid="google-login-btn">
                    <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" /><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" /><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" /><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" /></svg>Google
                </button>
                <p className="text-center text-[--text-secondary] mt-6">Don't have an account? <button onClick={onSwitchToSignup} className="text-[--accent-home] hover:underline font-medium" data-testid="switch-to-signup">Sign Up</button></p>
            </div>
        </div>
    );
};

// Signup Component
const SignupForm = ({ onSwitchToLogin, onSignup, error }) => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [agreed, setAgreed] = useState(false);
    const [legalModal, setLegalModal] = useState(null); // "tos" | "privacy" | null

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!agreed) return;
        setLoading(true);
        await onSignup(email, password);
        setLoading(false);
    };

    const agreementText = (
        <p className="text-xs text-[--text-secondary] leading-relaxed">
            <label className="flex items-start gap-2 cursor-pointer">
                <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-0.5"
                    required
                    data-testid="signup-agree-checkbox"
                />
                <span>
                    I agree to the{" "}
                    <button type="button" onClick={() => setLegalModal("tos")} className="text-[--accent-home] hover:underline" data-testid="signup-tos-link">Terms of Service</button>
                    {" "}and{" "}
                    <button type="button" onClick={() => setLegalModal("privacy")} className="text-[--accent-home] hover:underline" data-testid="signup-privacy-link">Privacy Policy</button>.
                </span>
            </label>
        </p>
    );

    return (
        <div className="w-full max-w-md mx-auto animate-in" data-testid="signup-form">
            <div className="card-base p-8">
                <div className="text-center mb-8">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center"><UserPlus className="w-8 h-8 text-white" /></div>
                    <h1 className="heading-primary text-3xl text-[--text-primary]">Create Account</h1>
                    <p className="text-[--text-secondary] mt-2">Start analyzing your sports videos</p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" className="input-base" required data-testid="signup-email" />
                    <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password (min 6 characters)" className="input-base" required minLength={6} data-testid="signup-password" />
                    {agreementText}
                    {error && <div className="flex items-center gap-2 text-red-400 text-sm bg-red-400/10 p-3 rounded-lg"><AlertCircle className="w-4 h-4" />{error}</div>}
                    <button type="submit" className="btn-primary w-full" disabled={loading || !agreed}>{loading ? "Creating account..." : "Sign Up"}</button>
                </form>
                <div className="relative my-6"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[--border-default]"></div></div><div className="relative flex justify-center text-sm"><span className="px-4 bg-[--bg-card] text-[--text-muted]">or continue with</span></div></div>
                <button onClick={() => { setLoading(true); onSignup(null, null, true).finally(() => setLoading(false)); }} className="btn-secondary w-full flex items-center justify-center gap-3" disabled={loading || !agreed} data-testid="signup-google-btn">
                    <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" /><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" /><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" /><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" /></svg>Google
                </button>
                {!agreed && <p className="text-center text-xs text-[--text-muted] mt-2">Check the box above to continue with Google.</p>}
                <p className="text-center text-[--text-secondary] mt-6">Already have an account? <button onClick={onSwitchToLogin} className="text-[--accent-home] hover:underline font-medium">Sign In</button></p>
            </div>
            {legalModal && <LegalModal type={legalModal} onClose={() => setLegalModal(null)} />}
        </div>
    );
};

// Survey Component
// Role choice shown right after signup, before the sport survey
const RoleForm = ({ onSelect }) => (
    <div className="w-full max-w-md mx-auto animate-in" data-testid="role-form">
        <div className="card-base p-8 text-center">
            <h1 className="heading-primary text-3xl text-[--text-primary] mb-2">How will you use SnipShot?</h1>
            <p className="text-[--text-secondary] mb-8">You can't switch later without contacting support, so pick the one that fits today.</p>
            <div className="grid gap-4">
                <button onClick={() => onSelect("coach")} className="card-base p-6 text-left hover:border-[--accent-home] border-2 border-transparent transition-colors" data-testid="role-coach">
                    <Users className="w-8 h-8 text-[--accent-home] mb-2" />
                    <h3 className="heading-secondary text-lg text-[--text-primary]">I'm a Coach</h3>
                    <p className="text-sm text-[--text-secondary]">Mark video, track stats, and share clips with your team.</p>
                </button>
                <button onClick={() => onSelect("player")} className="card-base p-6 text-left hover:border-[--accent-home] border-2 border-transparent transition-colors" data-testid="role-player">
                    <User className="w-8 h-8 text-[--accent-home] mb-2" />
                    <h3 className="heading-secondary text-lg text-[--text-primary]">I'm a Player</h3>
                    <p className="text-sm text-[--text-secondary]">Join your team with a code to see your stats and clips from your coach. Free.</p>
                </button>
            </div>
        </div>
    </div>
);

// Player signup: name + join code, links them to a team, writes their user doc
const PlayerOnboardForm = ({ userId, userEmail, onComplete }) => {
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [code, setCode] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(""); setLoading(true);
        try {
            const { doc, getDoc, setDoc, collection, query, where, getDocs, updateDoc, arrayUnion, serverTimestamp } = window.firebaseFirestore;
            const q = query(collection(firebaseDb, "teams"), where("code", "==", code.trim().toUpperCase()));
            const snap = await getDocs(q);
            if (snap.empty) { setError("No team found for that code."); setLoading(false); return; }
            const teamDoc = snap.docs[0];

            await updateDoc(teamDoc.ref, {
                [`players.${userId}`]: { displayName: `${firstName} ${lastName}`.trim(), joinedAt: serverTimestamp(), rosterKey: null }
            });
            // Pull the acceptance recorded at signup into the profile doc, best-effort —
            // a missing/failed read here shouldn't block joining the team.
            let legal;
            try {
                const legalDoc = await getDoc(doc(firebaseDb, "legalAcceptance", userId));
                if (legalDoc.exists()) legal = legalDoc.data();
            } catch (e) { console.error("Failed to read legal acceptance:", e); }
            // coachUids lets Firestore security rules grant this player read access to
            // the coach's gameSessions (team stats/videos) without a broader, less safe rule.
            const profileData = { first: firstName, last: lastName, email: userEmail, role: "player", teamIds: arrayUnion(teamDoc.id), coachUids: arrayUnion(teamDoc.data().coachUid), ...(legal && { legal }) };
            await setDoc(doc(firebaseDb, "users", userId), profileData, { merge: true });

            onComplete({ first: firstName, last: lastName, email: userEmail, role: "player", teamIds: [teamDoc.id] });
        } catch (err) {
            console.error("Join team error:", err);
            setError("Something went wrong joining the team. Check the code and try again.");
        }
        setLoading(false);
    };

    return (
        <div className="w-full max-w-md mx-auto animate-in" data-testid="join-team-form">
            <div className="card-base p-8">
                <h1 className="heading-primary text-3xl text-[--text-primary] mb-2">Join Your Team</h1>
                <p className="text-[--text-secondary] mb-6">Get this code from your coach.</p>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" className="input-base" required />
                    <input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name" className="input-base" required />
                    <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Team code" className="input-base text-center tracking-widest font-mono" maxLength={6} required />
                    {error && <div className="flex items-center gap-2 text-red-400 text-sm bg-red-400/10 p-3 rounded-lg"><AlertCircle className="w-4 h-4" />{error}</div>}
                    <button type="submit" className="btn-primary w-full" disabled={loading}>{loading ? "Joining..." : "Join Team"}</button>
                </form>
            </div>
        </div>
    );
};

// Lets an already-onboarded player join another team (up to MAX_PLAYER_TEAMS total).
// Shares the same join-by-code Firestore logic as PlayerOnboardForm, but updates an
// existing user doc instead of creating one.
const AddTeamForm = ({ userId, userProfile, onJoined }) => {
    const [code, setCode] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        const teamIds = userProfile?.teamIds || [];
        if (teamIds.length >= MAX_PLAYER_TEAMS) {
            setError(`You can only join up to ${MAX_PLAYER_TEAMS} teams.`);
            return;
        }
        setLoading(true);
        try {
            const { doc, setDoc, collection, query, where, getDocs, updateDoc, arrayUnion, serverTimestamp } = window.firebaseFirestore;
            const trimmedCode = code.trim().toUpperCase();
            const q = query(collection(firebaseDb, "teams"), where("code", "==", trimmedCode));
            const snap = await getDocs(q);
            if (snap.empty) { setError("No team found for that code."); setLoading(false); return; }
            const teamDoc = snap.docs[0];
            if (teamIds.includes(teamDoc.id)) { setError("You've already joined this team."); setLoading(false); return; }

            const coachUid = teamDoc.data().coachUid;
            await updateDoc(teamDoc.ref, {
                [`players.${userId}`]: { displayName: `${userProfile.first} ${userProfile.last}`.trim(), joinedAt: serverTimestamp(), rosterKey: null }
            });
            await setDoc(doc(firebaseDb, "users", userId), { teamIds: arrayUnion(teamDoc.id), coachUids: arrayUnion(coachUid) }, { merge: true });

            onJoined(teamDoc.id, coachUid);
            setCode("");
        } catch (err) {
            console.error("Join team error:", err);
            setError("Something went wrong joining the team. Check the code and try again.");
        }
        setLoading(false);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-3">
            <div className="flex gap-2">
                <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Team code" className="input-base text-center tracking-widest font-mono flex-1" maxLength={6} />
                <button type="submit" className="btn-primary px-4 shrink-0" disabled={loading}>{loading ? "Joining..." : "Join Team"}</button>
            </div>
            {error && <div className="flex items-center gap-2 text-red-400 text-sm bg-red-400/10 p-3 rounded-lg"><AlertCircle className="w-4 h-4" />{error}</div>}
        </form>
    );
};

const SurveyForm = ({ onComplete }) => {
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [sport, setSport] = useState("Ice Hockey");
    const [loading, setLoading] = useState(false);
    const sports = Object.keys(SPORT_MARKERS);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        await onComplete(firstName, lastName, sport);
        setLoading(false);
    };

    return (
        <div className="w-full max-w-md mx-auto animate-in" data-testid="survey-form">
            <div className="card-base p-8">
                <div className="text-center mb-8">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center"><User className="w-8 h-8 text-white" /></div>
                    <h1 className="heading-primary text-3xl text-[--text-primary]">Complete Your Profile</h1>
                </div>
                <form onSubmit={handleSubmit} className="space-y-5">
                    <div><label className="block text-sm font-medium text-[--text-secondary] mb-2">First Name</label><input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="input-base" required /></div>
                    <div><label className="block text-sm font-medium text-[--text-secondary] mb-2">Last Name</label><input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} className="input-base" required /></div>
                    <div><label className="block text-sm font-medium text-[--text-secondary] mb-2">Choose Your Sport</label><select value={sport} onChange={(e) => setSport(e.target.value)} className="input-base cursor-pointer">{sports.map((s) => <option key={s} value={s} className="bg-[--bg-card]">{s}</option>)}</select></div>
                    <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2" disabled={loading}>{loading ? "Setting up..." : <>Continue <ChevronRight className="w-5 h-5" /></>}</button>
                </form>
            </div>
        </div>
    );
};

// Tab Navigation
const TabNav = ({ activeTab, onTabChange, role }) => {
    const tabs = [{ id: "marking", label: "Marking", icon: Play }, { id: "live", label: "Live", icon: Circle }, { id: "stats", label: "Stats", icon: BarChart3 }, { id: "videos", label: "Videos", icon: Video }];
    if (role === "coach") tabs.push({ id: "team", label: "Team", icon: Users });
    tabs.push({ id: "settings", label: "Settings", icon: Settings }, { id: "games", label: "Games", icon: Save }, { id: "feedback", label: "Feedback", icon: MessageSquare });
    return (
        <nav className="flex gap-2 px-4 py-3 border-b border-[--border-default] overflow-x-auto" data-testid="tab-nav">
            {tabs.map((tab) => <button key={tab.id} onClick={() => onTabChange(tab.id)} className={`tab-base flex items-center gap-2 whitespace-nowrap ${activeTab === tab.id ? "tab-active" : ""}`} data-testid={`tab-${tab.id}`}><tab.icon className="w-5 h-5" />{tab.label}</button>)}
        </nav>
    );
};

// Marking Tab
// Shared "log this event" logic - player tagging, per-clip notes, per-clip offset -
// used by both the Marking tab (scrubbing an existing video) and the Live tab
// (recording), so the exact same premium extras behave identically in both places.
// Each tab supplies its own way of getting "the current time" and just calls openMarker.
const NOTE_MAX_LENGTH = 300;

// Resolves the before/after duration to use for a given event type. In "categorical"
// mode each marker name can have its own configured duration (set in Settings →
// Advanced Clip Length); any marker without its own entry falls back to the general
// offsetBefore/offsetAfter, same as "classic"/"general" mode.
const resolveOffsets = (markerName, offsetMode, offsetBefore, offsetAfter, categoricalOffsets) => {
    if (offsetMode === "categorical" && categoricalOffsets && categoricalOffsets[markerName]) {
        const { before, after } = categoricalOffsets[markerName];
        return { offsetBefore: before ?? offsetBefore, offsetAfter: after ?? offsetAfter };
    }
    return { offsetBefore, offsetAfter };
};

// Fingerprint of everything editable in the details modal. Edit mode compares the
// current form against the one taken when it opened, to know if there are unsaved
// changes (disables "Save Changes" until something changes, and guards "Go to
// timestamp" from silently throwing edits away).
const markerEditSnapshot = (note, before, after, players, stats) =>
    JSON.stringify([(note || "").trim(), before, after, [...players].sort(), stats]);

const useMarkerTagging = ({ isPremium, playerTaggingEnabled, notesEnabled, offsetMode, offsetBefore, offsetAfter, categoricalOffsets, homeTables, awayTables, setHomeTables, setAwayTables, onAddMarker, onUpdateMarker, onGoToTimestamp }) => {
    const [pendingMarker, setPendingMarker] = useState(null); // { team, markerName, time, id, showPlayerPicker, showNotesField, showOffsetPicker }
    // Selected players are stored as compound keys "team::number|name" so a coach can tag
    // players from EITHER roster on a single event - e.g. marking something as the
    // opponent's event but still pulling in one of their own players (and vice versa).
    const [selectedPlayers, setSelectedPlayers] = useState([]);
    // Which roster the player picker is currently browsing. Independent from
    // pendingMarker.team (which side the event itself is attributed to) - the coach can
    // toggle this freely without changing who the event happened for.
    const [pickerTeam, setPickerTeam] = useState("home");
    const [noteText, setNoteText] = useState("");
    const [clipBefore, setClipBefore] = useState(offsetBefore);
    const [clipAfter, setClipAfter] = useState(offsetAfter);
    const [statEdits, setStatEdits] = useState({}); // `${team}::${tableId}|${rowId}` -> edited values array, for players tagged in this event

    const rosterFor = (team) => getRosterPlayers(team === "home" ? homeTables : awayTables);

    // Opens the details modal if any premium extra applies; otherwise logs the event
    // immediately. Returns true if a modal was opened (callers can use this to decide
    // whether "logging" already fully happened or is still pending).
    const openMarker = (team, markerName, time) => {
        const id = Date.now();
        // Either roster having players is enough to show the picker - a coach may want to
        // tag one of THEIR players on an event attributed to the opponent, or vice versa.
        const showPlayerPicker = playerTaggingEnabled && isPremium && (rosterFor("home").length > 0 || rosterFor("away").length > 0);
        const showNotesField = notesEnabled && isPremium;
        const showOffsetPicker = offsetMode === "individual" && isPremium;
        if (showPlayerPicker || showNotesField || showOffsetPicker) {
            setSelectedPlayers([]);
            setNoteText("");
            setStatEdits({});
            setClipBefore(offsetBefore);
            setClipAfter(offsetAfter);
            setPickerTeam(team); // default the roster toggle to whichever side the event was logged for
            setPendingMarker({ team, markerName, time, id, showPlayerPicker, showNotesField, showOffsetPicker });
            return true;
        }
        const resolved = resolveOffsets(markerName, offsetMode, offsetBefore, offsetAfter, categoricalOffsets);
        onAddMarker(team, { name: markerName, time, id, offsetBefore: resolved.offsetBefore, offsetAfter: resolved.offsetAfter });
        return false;
    };

    // Opens the same details modal pre-filled from an existing clip so it can be edited
    // in place (used by the Videos tab). The event's name, time and team stay fixed -
    // only the extras (tagged players, clip length, notes) are editable, and each one
    // follows the same availability rules as when the clip was first marked.
    const openEditor = (team, marker) => {
        const { __team, ...original } = marker; // __team is a Videos-tab-only grouping tag, never stored
        const showPlayerPicker = playerTaggingEnabled && isPremium && (rosterFor("home").length > 0 || rosterFor("away").length > 0);
        const showNotesField = notesEnabled && isPremium;
        const showOffsetPicker = offsetMode === "individual" && isPremium;

        const initialPlayers = (original.players || []).map((p) => `${p.team || team}::${p.number}|${p.name}`);
        // Seed the editable stat lines for already-tagged players, same as togglePlayer does
        const initialStats = {};
        initialPlayers.forEach((compoundKey) => {
            const [t, key] = compoundKey.split("::");
            getPlayerStatRows(t === "home" ? homeTables : awayTables, key).forEach((r) => {
                initialStats[`${t}::${r.tableId}|${r.rowId}`] = [...r.values];
            });
        });
        const initialNote = original.note || "";
        const initialBefore = original.offsetBefore ?? offsetBefore;
        const initialAfter = original.offsetAfter ?? offsetAfter;

        setSelectedPlayers(initialPlayers);
        setStatEdits(initialStats);
        setNoteText(initialNote);
        setClipBefore(initialBefore);
        setClipAfter(initialAfter);
        setPickerTeam(team);
        setPendingMarker({
            team, markerName: original.name, time: original.time, id: original.id,
            isEdit: true, original, showPlayerPicker, showNotesField, showOffsetPicker,
            snapshot: markerEditSnapshot(initialNote, initialBefore, initialAfter, initialPlayers, initialStats),
        });
    };

    const togglePlayer = (team, key) => {
        const compoundKey = `${team}::${key}`;
        setSelectedPlayers((prev) => {
            const alreadySelected = prev.includes(compoundKey);
            if (!alreadySelected) {
                // Seed the editable stat line(s) for this player from their current stat sheet
                // row(s), so the modal shows real numbers the coach can adjust right away.
                const tables = team === "home" ? homeTables : awayTables;
                const rows = getPlayerStatRows(tables, key);
                if (rows.length) {
                    setStatEdits((se) => {
                        const next = { ...se };
                        rows.forEach((r) => { next[`${team}::${r.tableId}|${r.rowId}`] = [...r.values]; });
                        return next;
                    });
                }
            }
            return alreadySelected ? prev.filter((k) => k !== compoundKey) : [...prev, compoundKey];
        });
    };

    const updateStatEdit = (editKey, colIndex, value) => {
        setStatEdits((se) => ({ ...se, [editKey]: se[editKey].map((v, i) => (i === colIndex ? value : v)) }));
    };

    const closePendingMarker = () => {
        setPendingMarker(null);
        setSelectedPlayers([]);
        setNoteText("");
        setStatEdits({});
    };

    const isDirty = !!pendingMarker?.isEdit
        && markerEditSnapshot(noteText, clipBefore, clipAfter, selectedPlayers, statEdits) !== pendingMarker.snapshot;

    // Jump to this clip's moment in the Marking tab's video. Leaving the Videos tab
    // unmounts this modal, so warn first if that would throw away unsaved edits.
    const goToTimestamp = () => {
        if (!pendingMarker) return;
        if (isDirty && !window.confirm("You have unsaved changes to this clip. Leave without saving?")) return;
        const time = pendingMarker.time;
        closePendingMarker();
        if (onGoToTimestamp) onGoToTimestamp(time);
    };

    // Selected players can span both rosters - resolve each compound key back to
    // its actual roster entry, tagging which team it came from. When editing, a
    // tagged player who has since been removed from the roster is kept as-is
    // rather than silently dropped from the clip.
    const resolveSelectedPlayers = () => selectedPlayers.map((compoundKey) => {
        const [team, key] = compoundKey.split("::");
        const p = rosterFor(team).find((pl) => `${pl.number}|${pl.name}` === key);
        if (p) return { ...p, team };
        const existing = (pendingMarker.original?.players || []).find((ep) => (ep.team || pendingMarker.team) === team && `${ep.number}|${ep.name}` === key);
        return existing ? { ...existing, team } : null;
    }).filter(Boolean);

    // Push any edited stat lines back into each roster's tables so the Stats tab
    // reflects them immediately - grouped by team since edits can touch both.
    const applyStatEdits = () => {
        const editsByTeam = { home: {}, away: {} };
        Object.entries(statEdits).forEach(([editKey, values]) => {
            const [team, rest] = editKey.split("::");
            if (editsByTeam[team]) editsByTeam[team][rest] = values;
        });
        if (Object.keys(editsByTeam.home).length) {
            setHomeTables((tables) => tables.map((t) => ({
                ...t,
                rows: t.rows.map((r) => {
                    const editKey = `${t.id}|${r.id}`;
                    return editsByTeam.home[editKey] ? { ...r, values: editsByTeam.home[editKey] } : r;
                }),
            })));
        }
        if (Object.keys(editsByTeam.away).length) {
            setAwayTables((tables) => tables.map((t) => ({
                ...t,
                rows: t.rows.map((r) => {
                    const editKey = `${t.id}|${r.id}`;
                    return editsByTeam.away[editKey] ? { ...r, values: editsByTeam.away[editKey] } : r;
                }),
            })));
        }
    };

    const confirmPendingMarker = () => {
        if (!pendingMarker) return;

        // Editing an existing clip: only touch the fields this modal actually offered,
        // so anything hidden by current settings (e.g. an old note while notes are
        // turned off) is preserved untouched.
        if (pendingMarker.isEdit) {
            const updated = { ...pendingMarker.original };
            if (pendingMarker.showOffsetPicker) {
                updated.offsetBefore = clipBefore;
                updated.offsetAfter = clipAfter;
            }
            if (pendingMarker.showPlayerPicker) {
                const players = resolveSelectedPlayers();
                if (players.length) updated.players = players; else delete updated.players;
                if (selectedPlayers.length) applyStatEdits();
            }
            if (pendingMarker.showNotesField) {
                const trimmed = noteText.trim();
                if (trimmed) updated.note = trimmed; else delete updated.note;
            }
            if (onUpdateMarker) onUpdateMarker(pendingMarker.team, pendingMarker.id, updated);
            closePendingMarker();
            return;
        }

        const resolved = resolveOffsets(pendingMarker.markerName, offsetMode, offsetBefore, offsetAfter, categoricalOffsets);
        const marker = {
            name: pendingMarker.markerName,
            time: pendingMarker.time,
            id: pendingMarker.id,
            offsetBefore: pendingMarker.showOffsetPicker ? clipBefore : resolved.offsetBefore,
            offsetAfter: pendingMarker.showOffsetPicker ? clipAfter : resolved.offsetAfter,
        };
        if (pendingMarker.showPlayerPicker && selectedPlayers.length) {
            const players = resolveSelectedPlayers();
            if (players.length) marker.players = players;
            applyStatEdits();
        }
        if (pendingMarker.showNotesField && noteText.trim()) marker.note = noteText.trim();
        onAddMarker(pendingMarker.team, marker);
        closePendingMarker();
    };

    return {
        pendingMarker, selectedPlayers, pickerTeam, setPickerTeam, noteText, setNoteText, clipBefore, setClipBefore, clipAfter, setClipAfter,
        statEdits, rosterFor, openMarker, openEditor, togglePlayer, updateStatEdit, closePendingMarker, confirmPendingMarker,
        isDirty, goToTimestamp,
    };
};

// Shared "log event" modal - player picker + per-player stat edits + per-clip offset +
// notes field. Rendered by both the Marking tab and the Live tab off a `useMarkerTagging`
// instance, so both get identical behavior for free.
const MarkerDetailsModal = ({ tagging, homeTables, awayTables }) => {
    const {
        pendingMarker, selectedPlayers, pickerTeam, setPickerTeam, noteText, setNoteText, clipBefore, clipAfter, setClipBefore, setClipAfter,
        statEdits, rosterFor, togglePlayer, updateStatEdit, closePendingMarker, confirmPendingMarker, isDirty, goToTimestamp,
    } = tagging;

    if (!pendingMarker) return null;

    const isEdit = !!pendingMarker.isEdit;
    const hasEditableFields = pendingMarker.showPlayerPicker || pendingMarker.showOffsetPicker || pendingMarker.showNotesField;

    return (
        <div className="modal-overlay" data-testid="player-tag-modal" style={{ overflowY: "auto", padding: "24px 0" }}>
            <div className="modal" style={{ display: "flex", flexDirection: "column", maxHeight: "85vh" }}>
                <div className="modal-header" style={{ flexShrink: 0 }}>
                    <div>
                        <h2>{isEdit ? "Edit" : "Log"} "{pendingMarker.markerName}"</h2>
                        <p className="text-xs text-[--text-muted] mt-0.5">
                            Event for {pendingMarker.team === "home" ? "Your Team" : "Opponent"}{isEdit ? ` · marked at ${formatClipTime(pendingMarker.time)}` : ""}
                        </p>
                    </div>
                    <button onClick={closePendingMarker} className="modal-close"><X size={20} /></button>
                </div>

                <div style={{ overflowY: "auto", flex: "1 1 auto", minHeight: 0 }}>
                    {isEdit && (
                        <div className="mb-4">
                            <button
                                type="button"
                                onClick={goToTimestamp}
                                className="btn-secondary w-full flex items-center justify-center gap-2"
                                data-testid="go-to-timestamp-btn"
                            >
                                <Play className="w-4 h-4" />Go to {formatClipTime(pendingMarker.time)} in Marking tab
                            </button>
                            {!hasEditableFields && (
                                <p className="text-xs text-[--text-muted] mt-3">
                                    There's nothing else to edit for this clip with your current settings. Player tagging, notes, and per-clip length are premium options you turn on in Settings.
                                </p>
                            )}
                        </div>
                    )}

                    {pendingMarker.showPlayerPicker && (
                        <>
                            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                                <p className="text-sm text-[--text-muted]">
                                    Select any players tagged in this event
                                </p>
                                <div className="flex rounded-lg border border-[--border-default] overflow-hidden shrink-0" data-testid="marking-roster-toggle">
                                    <button
                                        type="button"
                                        onClick={() => setPickerTeam("home")}
                                        className={`px-3 py-1.5 text-xs font-semibold transition-colors ${pickerTeam === "home" ? "bg-[--accent-home] text-white" : "text-[--text-secondary]"}`}
                                        data-testid="marking-roster-toggle-home"
                                    >
                                        Your Team
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPickerTeam("away")}
                                        className={`px-3 py-1.5 text-xs font-semibold transition-colors ${pickerTeam === "away" ? "bg-[--accent-away] text-white" : "text-[--text-secondary]"}`}
                                        data-testid="marking-roster-toggle-away"
                                    >
                                        Opponent
                                    </button>
                                </div>
                            </div>

                            {rosterFor(pickerTeam).length === 0 ? (
                                <p className="text-xs text-[--text-muted] italic mb-2">
                                    No roster entered yet for {pickerTeam === "home" ? "your team" : "the opponent"}.
                                </p>
                            ) : (
                                <div className="grid grid-cols-3 gap-2 max-h-[300px] overflow-y-auto">
                                    {rosterFor(pickerTeam).map((p) => {
                                        const key = `${p.number}|${p.name}`;
                                        const compoundKey = `${pickerTeam}::${key}`;
                                        const active = selectedPlayers.includes(compoundKey);
                                        return (
                                            <button
                                                key={compoundKey}
                                                onClick={() => togglePlayer(pickerTeam, key)}
                                                className={`px-3 py-2 rounded-lg border-2 text-sm text-left transition-all ${active ? "border-[--accent-home] bg-[--accent-home]/10" : "border-[--border-default]"}`}
                                            >
                                                <div className="font-semibold text-[--text-primary]">#{p.number || "-"}</div>
                                                <div className="text-[--text-secondary] truncate">{p.name || "Unnamed"}</div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}

                    {pendingMarker.showPlayerPicker && selectedPlayers.length > 0 && (
                        <div className="mt-4" data-testid="marking-stat-edit-section">
                            <p className="text-sm text-[--text-muted] mb-2">
                                Stats for {selectedPlayers.length === 1 ? "this player" : "these players"} this game
                            </p>
                            <div className="space-y-3" data-testid="marking-stat-edit-players">
                                {selectedPlayers.map((compoundKey) => {
                                    const [team, key] = compoundKey.split("::");
                                    const [num, ...nameParts] = key.split("|");
                                    const name = nameParts.join("|");
                                    const rows = getPlayerStatRows(team === "home" ? homeTables : awayTables, key);
                                    return (
                                        <div key={compoundKey} className="rounded-lg border border-[--border-default] bg-[--bg-secondary] p-3">
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold shrink-0 ${team === "home" ? "bg-[--accent-home]/20 text-[--accent-home]" : "bg-[--accent-away]/20 text-[--accent-away]"}`}>
                                                    {team === "home" ? "Your Team" : "Opponent"}
                                                </span>
                                                <p className="text-sm font-semibold text-[--text-primary] truncate">#{num || "-"} {name || "Unnamed"}</p>
                                            </div>
                                            {rows.length === 0 ? (
                                                <p className="text-xs text-[--text-muted]">No stat row found for this player.</p>
                                            ) : rows.map((r) => {
                                                const editKey = `${team}::${r.tableId}|${r.rowId}`;
                                                const values = statEdits[editKey] || r.values;
                                                return (
                                                    <div key={editKey} className="mb-3 last:mb-0">
                                                        {rows.length > 1 && <p className="text-xs text-[--text-muted] mb-1">{r.tableName}</p>}
                                                        <div className="flex gap-3 overflow-x-auto pb-1">
                                                            {r.columns.map((col, i) => i < 2 ? null : (
                                                                <label key={i} className="flex flex-col items-center gap-1 text-xs shrink-0 w-16">
                                                                    <span className="text-[--text-muted] truncate w-full text-center">{col}</span>
                                                                    <input
                                                                        type="number"
                                                                        value={values[i]}
                                                                        onChange={(e) => updateStatEdit(editKey, i, e.target.value)}
                                                                        className="input-base w-full text-center px-1 py-1 text-sm"
                                                                        data-testid={`marking-stat-input-${editKey}-${i}`}
                                                                    />
                                                                </label>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {pendingMarker.showOffsetPicker && (
                        <div className={pendingMarker.showPlayerPicker ? "mt-4" : ""}>
                            <p className="text-sm text-[--text-muted] mb-2">Clip length for this event</p>
                            <OffsetDualSlider before={clipBefore} after={clipAfter} setBefore={setClipBefore} setAfter={setClipAfter} />
                        </div>
                    )}

                    {pendingMarker.showNotesField && (
                        <div className={(pendingMarker.showPlayerPicker || pendingMarker.showOffsetPicker) ? "mt-4" : ""}>
                            <p className="text-sm text-[--text-muted] mb-2">Notes for this clip</p>
                            <textarea
                                value={noteText}
                                onChange={(e) => setNoteText(e.target.value.slice(0, NOTE_MAX_LENGTH))}
                                maxLength={NOTE_MAX_LENGTH}
                                rows={4}
                                placeholder="What happened here? Anything to review later..."
                                className="input-base w-full resize-none"
                                data-testid="clip-note-textarea"
                            />
                            <p className="text-xs text-[--text-muted] text-right mt-1">{noteText.length}/{NOTE_MAX_LENGTH}</p>
                        </div>
                    )}
                </div>

                <div className="modal-buttons flex gap-2 mt-4" style={{ flexShrink: 0 }}>
                    {isEdit && !hasEditableFields ? (
                        <button onClick={closePendingMarker} className="btn-secondary flex-1">Close</button>
                    ) : (
                        <>
                            <button
                                onClick={confirmPendingMarker}
                                disabled={isEdit && !isDirty}
                                className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
                                data-testid={isEdit ? "save-clip-edit-btn" : undefined}
                            >
                                <Check className="w-4 h-4" />{isEdit ? "Save Changes" : `Log Event${selectedPlayers.length > 0 ? ` (${selectedPlayers.length})` : ""}`}
                            </button>
                            <button onClick={closePendingMarker} className="btn-secondary">Cancel</button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

// Wraps a Marking-tab section so its position in the stack can be adjusted
// by the user. Reordering uses simple up/down buttons rather than
// drag-and-drop so it works reliably on touch devices too. Order is saved
// to localStorage so it survives tab switches and page refreshes.
const ResizableSection = ({ id, moveUp, moveDown, canMoveUp, canMoveDown, className = "", children }) => {
    return (
        <div className={`${className} relative`} data-testid={`resizable-section-${id}`}>
            <div className="flex items-center justify-end gap-1 mb-2" data-testid={`section-controls-${id}`}>
                <button type="button" onClick={moveUp} disabled={!canMoveUp} className="p-1 rounded hover:bg-[--bg-tertiary] text-[--text-muted] disabled:opacity-30 disabled:cursor-not-allowed" title="Move section up" aria-label="Move section up" data-testid={`move-up-${id}`}>
                    <ChevronUp className="w-4 h-4" />
                </button>
                <button type="button" onClick={moveDown} disabled={!canMoveDown} className="p-1 rounded hover:bg-[--bg-tertiary] text-[--text-muted] disabled:opacity-30 disabled:cursor-not-allowed" title="Move section down" aria-label="Move section down" data-testid={`move-down-${id}`}>
                    <ChevronDown className="w-4 h-4" />
                </button>
            </div>
            {children}
        </div>
    );
};

// Lets the user drag a divider between the video pane and the markers pane
// inside the Marking tab's video+markers section, so growing one shrinks
// the other. Only active at the `lg` breakpoint and up, since below that
// the two panes stack vertically and a horizontal split doesn't apply.
// The split ratio is saved to localStorage so it survives tab switches and
// page refreshes.
const VIDEO_MARKERS_SPLIT_STORAGE_KEY = "snipshot_video_markers_split";
const VIDEO_MARKERS_SPLIT_MIN = 0.2;
const VIDEO_MARKERS_SPLIT_MAX = 0.8;
const LG_BREAKPOINT_QUERY = "(min-width: 1024px)";

const VideoMarkersSplit = ({ videoPane, markersPane }) => {
    const containerRef = useRef(null);
    const videoWrapperRef = useRef(null);
    const draggingRef = useRef(false);

    const [ratio, setRatio] = useState(() => {
        try {
            const saved = parseFloat(localStorage.getItem(VIDEO_MARKERS_SPLIT_STORAGE_KEY));
            if (!Number.isNaN(saved) && saved >= VIDEO_MARKERS_SPLIT_MIN && saved <= VIDEO_MARKERS_SPLIT_MAX) return saved;
        } catch (e) { /* ignore malformed/unavailable storage */ }
        return 0.75;
    });

    const [isLgScreen, setIsLgScreen] = useState(() =>
        typeof window !== "undefined" && window.matchMedia ? window.matchMedia(LG_BREAKPOINT_QUERY).matches : true
    );

    // The video side sets the height for the whole row — it sizes itself
    // naturally (its aspect ratio against its current width). The markers
    // side is capped to match via this measured height and scrolls
    // internally, rather than the row growing to fit every marker button.
    const [videoHeight, setVideoHeight] = useState(null);

    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return;
        const mql = window.matchMedia(LG_BREAKPOINT_QUERY);
        const handler = (e) => setIsLgScreen(e.matches);
        if (mql.addEventListener) mql.addEventListener("change", handler); else mql.addListener(handler);
        return () => { if (mql.removeEventListener) mql.removeEventListener("change", handler); else mql.removeListener(handler); };
    }, []);

    useEffect(() => {
        const el = videoWrapperRef.current;
        if (!el || typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) return;
            setVideoHeight(entry.contentRect.height);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const clampRatio = (value) => Math.min(VIDEO_MARKERS_SPLIT_MAX, Math.max(VIDEO_MARKERS_SPLIT_MIN, value));

    const updateRatioFromClientX = useCallback((clientX) => {
        const el = containerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (rect.width <= 0) return;
        setRatio(clampRatio((clientX - rect.left) / rect.width));
    }, []);

    const handlePointerMove = useCallback((e) => {
        if (!draggingRef.current) return;
        updateRatioFromClientX(e.clientX);
    }, [updateRatioFromClientX]);

    const persistRatio = useCallback((value) => {
        try { localStorage.setItem(VIDEO_MARKERS_SPLIT_STORAGE_KEY, String(value)); } catch (e) { /* storage full/unavailable — split still works, just won't persist */ }
    }, []);

    const stopDragging = useCallback(() => {
        if (!draggingRef.current) return;
        draggingRef.current = false;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        setRatio((current) => { persistRatio(current); return current; });
    }, [persistRatio]);

    useEffect(() => {
        window.addEventListener("pointermove", handlePointerMove);
        window.addEventListener("pointerup", stopDragging);
        return () => {
            window.removeEventListener("pointermove", handlePointerMove);
            window.removeEventListener("pointerup", stopDragging);
        };
    }, [handlePointerMove, stopDragging]);

    const startDragging = (e) => {
        if (!isLgScreen) return;
        e.preventDefault();
        draggingRef.current = true;
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
        updateRatioFromClientX(e.clientX);
    };

    const nudgeRatio = (delta) => {
        setRatio((r) => { const next = clampRatio(r + delta); persistRatio(next); return next; });
    };

    const handleKeyDown = (e) => {
        if (!isLgScreen) return;
        if (e.key === "ArrowLeft") { nudgeRatio(-0.02); e.preventDefault(); }
        else if (e.key === "ArrowRight") { nudgeRatio(0.02); e.preventDefault(); }
    };

    const videoStyle = isLgScreen ? { flexBasis: `${ratio * 100}%` } : undefined;
    const markersStyle = isLgScreen
        ? { flexBasis: `${(1 - ratio) * 100}%`, height: videoHeight ? `${videoHeight}px` : undefined }
        : undefined;

    return (
        <div ref={containerRef} className="flex flex-col lg:flex-row lg:items-start relative">
            <div ref={videoWrapperRef} className="min-w-0 lg:pr-3 w-full" style={videoStyle}>{videoPane}</div>
            <div
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize video and markers"
                aria-valuenow={Math.round(ratio * 100)}
                aria-valuemin={Math.round(VIDEO_MARKERS_SPLIT_MIN * 100)}
                aria-valuemax={Math.round(VIDEO_MARKERS_SPLIT_MAX * 100)}
                tabIndex={isLgScreen ? 0 : -1}
                onPointerDown={startDragging}
                onKeyDown={handleKeyDown}
                className="hidden lg:flex items-center justify-center w-3 shrink-0 cursor-col-resize group touch-none"
                data-testid="video-markers-resize-handle"
            >
                <div className="w-1 h-16 rounded-full bg-[--border-default] group-hover:bg-[--accent-home] transition-colors" />
            </div>
            <div className="min-w-0 lg:pl-3 w-full overflow-hidden" style={markersStyle}>{markersPane}</div>
        </div>
    );
};


const MarkingTab = ({ markers, videoUrl, videoFile, onVideoChange, onRemoveVideo, homeMarkers, awayMarkers, onAddMarker, videoRef, setShowPremiumModal, isPremium, playerTaggingEnabled, homeTables, awayTables, setHomeTables, setAwayTables, notesEnabled, offsetMode, offsetBefore, offsetAfter, categoricalOffsets, eventCounterEnabled, gameNotes, setGameNotes, pauseOnMarkEnabled, onPositionChange }) => {
    const tagging = useMarkerTagging({ isPremium, playerTaggingEnabled, notesEnabled, offsetMode, offsetBefore, offsetAfter, categoricalOffsets, homeTables, awayTables, setHomeTables, setAwayTables, onAddMarker });

    const handleFileChange = (e) => { const file = e.target.files[0]; if (file) { onVideoChange(URL.createObjectURL(file), file); } };
    const [urlInputMode, setUrlInputMode] = useState(false);
    const [videoUrlInput, setVideoUrlInput] = useState("");
    const handleUrlSubmit = () => {
        const url = videoUrlInput.trim();
        if (!url) return;
        if (!/^https:\/\//i.test(url)) { alert("Please enter a video link starting with https://"); return; }
        // No File object here — videoFile stays null, which is exactly the
        // "loaded from a URL" case the download flow already handles via
        // hasRealFile in VideosTab (falls through to process-video-url).
        onVideoChange(url, null);
        setVideoUrlInput("");
        setUrlInputMode(false);
    };

    const addMarker = (team, markerName) => {
        if (!videoRef.current) return;
        const time = videoRef.current.currentTime;
        // Pause immediately on click if the setting is on — regardless of whether this
        // event needs the details modal or logs straight away.
        if (pauseOnMarkEnabled) videoRef.current.pause();
        tagging.openMarker(team, markerName, time);
    };

    // Marker positions on the timeline are a % of the video's duration, so the
    // timeline needs to re-render the moment that duration is known. Reading
    // videoRef.current.duration directly during render doesn't do that — the
    // metadata loads asynchronously, so the first render after a new video
    // loads (e.g. opening a saved game) would compute against an unset
    // duration, pushing every marker off the (overflow-hidden) timeline strip
    // until something else happened to force a re-render. Tracking it in
    // state, updated via onLoadedMetadata below, fixes that at the source.
    const [videoDuration, setVideoDuration] = useState(null);
    // The video element gets a fresh src on every game load, so a stale
    // duration from the previous video shouldn't briefly apply to the new
    // one's markers.
    useEffect(() => { setVideoDuration(null); }, [videoUrl]);

    const maxTime = videoDuration || 100;

    const showEventCounter = eventCounterEnabled && isPremium;
    const eventCounts = showEventCounter ? markers.map((name) => ({
        name,
        home: homeMarkers.filter((m) => m.name === name).length,
        away: awayMarkers.filter((m) => m.name === name).length,
    })) : [];

    // Which order the sections render in — user-adjustable (see ResizableSection),
    // persisted in localStorage so it survives tab switches and refreshes.
    const SECTION_ORDER_STORAGE_KEY = "snipshot_marking_section_order";
    const DEFAULT_SECTION_ORDER = ["videoMarkers", "timeline", "notes", "counter"];
    const [sectionOrder, setSectionOrder] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(SECTION_ORDER_STORAGE_KEY) || "null");
            if (Array.isArray(saved) && DEFAULT_SECTION_ORDER.every((id) => saved.includes(id))) return saved;
        } catch (e) { /* ignore malformed/unavailable storage */ }
        return DEFAULT_SECTION_ORDER;
    });
    useEffect(() => {
        try { localStorage.setItem(SECTION_ORDER_STORAGE_KEY, JSON.stringify(sectionOrder)); } catch (e) { /* ignore */ }
    }, [sectionOrder]);

    const visibleSectionOrder = sectionOrder.filter((id) => id !== "counter" || showEventCounter);
    const moveSection = (id, direction) => {
        const visIdx = visibleSectionOrder.indexOf(id);
        const swapWith = visibleSectionOrder[visIdx + direction];
        if (!swapWith) return;
        setSectionOrder((prev) => {
            const a = prev.indexOf(id);
            const b = prev.indexOf(swapWith);
            const next = [...prev];
            [next[a], next[b]] = [next[b], next[a]];
            return next;
        });
    };

    const sectionContent = {
        videoMarkers: (
            <VideoMarkersSplit
                videoPane={
                    <div>
                        <h3 className="heading-secondary text-lg text-[--text-primary] mb-4 flex items-center gap-2"><Upload className="w-5 h-5 text-[--accent-home]" />Video Upload</h3>

                        {videoUrl && !videoFile && (
                            <div className="flex items-start gap-2 p-3 rounded-lg border border-yellow-500/40 bg-yellow-500/10 text-sm text-yellow-500 mb-3" data-testid="url-video-warning">
                                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                                <span>This video was loaded from a link, so it won't autosave and can't be saved to Saved Games. Your markers will be lost if you refresh. Upload the video file itself to use those features.</span>
                            </div>
                        )}

                        {!videoUrl ? (
                            <div className="min-h-[320px] flex flex-col justify-center">
                                {!urlInputMode ? (
                                    <div>
                                        <label className="block"><input type="file" accept="video/*" onChange={handleFileChange} className="hidden" data-testid="video-input" />
                                            <div className="border-2 border-dashed border-[--border-default] rounded-lg p-12 text-center cursor-pointer hover:border-[--accent-home] transition-colors"><Upload className="w-10 h-10 mx-auto mb-3 text-[--text-muted]" /><p className="text-[--text-secondary]">Click to upload a video</p></div>
                                        </label>
                                        <button type="button" onClick={() => setUrlInputMode(true)} className="mt-3 w-full flex items-center justify-center gap-1.5 text-sm text-[--accent-home] hover:underline" data-testid="show-video-url-input">
                                            <Link2 className="w-4 h-4" /> Or paste a video link instead
                                        </button>
                                    </div>
                                ) : (
                                    <div className="space-y-3" data-testid="video-url-form">
                                        <input
                                            type="url"
                                            value={videoUrlInput}
                                            onChange={(e) => setVideoUrlInput(e.target.value)}
                                            placeholder="https://..."
                                            className="w-full px-3 py-2 rounded-lg border border-[--border-default] bg-[--bg-secondary] text-[--text-primary]"
                                            data-testid="video-url-input"
                                            autoFocus
                                        />
                                        <p className="text-xs text-[--text-muted]">
                                            Direct video file link only. Google Drive share links and YouTube links won't work here.
                                        </p>
                                        <p className="text-xs text-yellow-500 flex items-start gap-1.5">
                                            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                            <span>Videos loaded from a link don't autosave and can't be saved to Saved Games. Upload the file itself to use those features.</span>
                                        </p>
                                        <div className="flex gap-2">
                                            <button type="button" onClick={handleUrlSubmit} className="btn-primary flex-1" data-testid="submit-video-url">Load video</button>
                                            <button type="button" onClick={() => { setUrlInputMode(false); setVideoUrlInput(""); }} className="btn-secondary">Cancel</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="video-container relative h-full">
                                <button onClick={onRemoveVideo} className="absolute top-2 right-2 z-10 bg-red-500 hover:bg-red-600 text-white rounded-full p-2" data-testid="remove-video-btn"><X className="w-5 h-5" /></button>
                                <video ref={videoRef} src={videoUrl} controls className="w-full h-full rounded-lg" data-testid="video-player" onTimeUpdate={(e) => onPositionChange && onPositionChange(e.target.currentTime)} onLoadedMetadata={(e) => setVideoDuration(e.target.duration)} onDurationChange={(e) => setVideoDuration(e.target.duration)} />
                                {videoFile && <p className="text-sm text-[--text-muted] mt-2 truncate">{videoFile.name}</p>}
                            </div>
                        )}
                    </div>
                }
                markersPane={
                    <div className="relative h-full flex flex-col">
                        <h3 className="heading-secondary text-lg text-[--text-primary] mb-4 shrink-0">Markers</h3>
                        <div className="grid grid-cols-2 gap-4 mb-6 shrink-0"><div className="text-center"><div className="badge-home mb-3 inline-block">Your Team</div></div><div className="text-center"><div className="badge-away mb-3 inline-block">Opponent</div></div></div>
                        <div className="grid grid-cols-2 gap-3 flex-1 min-h-0 content-start overflow-y-auto">
                            {markers.map((marker) => <div key={marker} className="contents"><button onClick={() => addMarker("home", marker)} className="marker-btn marker-btn-home" disabled={!videoUrl}>{marker}</button><button onClick={() => addMarker("away", marker)} className="marker-btn marker-btn-away" disabled={!videoUrl}>{marker}</button></div>)}
                        </div>

                        <MarkerDetailsModal tagging={tagging} homeTables={homeTables} awayTables={awayTables} />
                    </div>
                }
            />
        ),
        timeline: (
            <>
                <h3 className="heading-secondary text-lg text-[--text-primary] mb-4">Timeline</h3>
                <div className="relative h-12 bg-[--bg-tertiary] rounded-lg overflow-hidden">
                    {homeMarkers.map((m) => <div key={m.id} className="absolute top-0 w-1 h-1/2 bg-[--accent-home] rounded-b cursor-pointer hover:w-2 transition-all" style={{ left: `${(m.time / maxTime) * 100}%` }} onClick={() => videoRef.current && (videoRef.current.currentTime = m.time)} title={`${m.name} - ${m.time.toFixed(1)}s`} />)}
                    {awayMarkers.map((m) => <div key={m.id} className="absolute bottom-0 w-1 h-1/2 bg-[--accent-away] rounded-t cursor-pointer hover:w-2 transition-all" style={{ left: `${(m.time / maxTime) * 100}%` }} onClick={() => videoRef.current && (videoRef.current.currentTime = m.time)} title={`${m.name} - ${m.time.toFixed(1)}s`} />)}
                    {!homeMarkers.length && !awayMarkers.length && <div className="flex items-center justify-center h-full text-[--text-muted]">Markers will appear here</div>}
                </div>
                <div className="flex justify-between mt-2 text-sm text-[--text-muted]"><span className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-[--accent-home]" />Your Team ({homeMarkers.length})</span><span className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-[--accent-away]" />Opponent ({awayMarkers.length})</span></div>
            </>
        ),
        notes: (
            <>
                <h3 className="heading-secondary text-lg text-[--text-primary] mb-2">Game Notes</h3>
                <p className="text-sm text-[--text-muted] mb-3">Overall notes about this game — not tied to any single clip. Saved along with the game session.</p>
                <textarea
                    value={gameNotes || ""}
                    onChange={(e) => setGameNotes(e.target.value)}
                    rows={5}
                    placeholder="e.g. Slow start in the first period, power play needs work, good effort on the penalty kill..."
                    className="input-base w-full resize-none"
                    data-testid="game-notes-textarea"
                />
            </>
        ),
        counter: showEventCounter ? (
            <>
                <h3 className="heading-secondary text-lg text-[--text-primary] mb-4">Event Counter</h3>
                {eventCounts.length ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {eventCounts.map(({ name, home, away }) => (
                            <div key={name} className="rounded-lg border border-[--border-default] px-3 py-2">
                                <div className="text-sm text-[--text-secondary] truncate">{name}</div>
                                <div className="text-2xl font-bold text-[--text-primary]">{home + away}</div>
                                <div className="text-xs text-[--text-muted] flex gap-2">
                                    <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-[--accent-home]" />{home}</span>
                                    <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-[--accent-away]" />{away}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-[--text-muted] text-sm">No event types configured yet.</div>
                )}
            </>
        ) : null,
    };

    const sectionTestIds = { videoMarkers: undefined, timeline: undefined, notes: "game-notes-card", counter: "event-counter-card" };

    return (
        <div className="p-6 space-y-6 animate-in" data-testid="marking-tab">
            {visibleSectionOrder.map((id, i) => (
                <ResizableSection
                    key={id}
                    id={id}
                    className={`card-base p-6 ${id === "videoMarkers" ? "relative" : ""}`}
                    canMoveUp={i > 0}
                    canMoveDown={i < visibleSectionOrder.length - 1}
                    moveUp={() => moveSection(id, -1)}
                    moveDown={() => moveSection(id, 1)}
                >
                    <div data-testid={sectionTestIds[id]}>{sectionContent[id]}</div>
                </ResizableSection>
            ))}
        </div>
    );
};

// Live Tab - record video from the device camera and mark events in real time.
// Uses MediaRecorder's pause()/resume() so stop-and-start during the recording
// stays one continuous timeline (no stitching needed), which keeps marker times
// simple offsets into a single final video - same as the uploaded-file flow.
const formatClock = (totalSeconds) => {
    const s = Math.max(0, Math.floor(totalSeconds));
    const mm = String(Math.floor(s / 60)).padStart(2, "0");
    const ss = String(s % 60).padStart(2, "0");
    return `${mm}:${ss}`;
};

const pickRecorderMimeType = () => {
    if (typeof MediaRecorder === "undefined") return "";
    const candidates = [
        "video/mp4;codecs=h264,aac",
        "video/webm;codecs=vp9,opus",
        "video/webm;codecs=vp8,opus",
        "video/webm",
    ];
    return candidates.find((t) => MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(t)) || "";
};

const LiveTab = ({ markers, homeMarkers, awayMarkers, onAddMarker, onVideoChange, offsetBefore, offsetAfter, offsetMode, categoricalOffsets, isPremium, playerTaggingEnabled, notesEnabled, homeTables, awayTables, setHomeTables, setAwayTables, eventCounterEnabled, gameNotes, setGameNotes }) => {
    // idle -> requesting -> recording <-> paused -> stopped
    const [status, setStatus] = useState("idle");
    const [errorMsg, setErrorMsg] = useState("");
    const [, forceTick] = useState(0);
    const [finalizedUrl, setFinalizedUrl] = useState(null);

    const previewRef = useRef(null);
    const streamRef = useRef(null);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const mimeTypeRef = useRef("");
    const segmentStartRef = useRef(0); // ms timestamp the current active segment began
    const accumulatedMsRef = useRef(0); // total elapsed ms from completed segments

    // Same player-tagging / notes / per-clip-offset modal the Marking tab uses,
    // so events logged live get exactly the same detail as events logged after the fact.
    const tagging = useMarkerTagging({ isPremium, playerTaggingEnabled, notesEnabled, offsetMode, offsetBefore, offsetAfter, categoricalOffsets, homeTables, awayTables, setHomeTables, setAwayTables, onAddMarker });

    const isSupported = typeof navigator !== "undefined" && navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== "undefined";

    const getElapsedSeconds = () => (accumulatedMsRef.current + (status === "recording" ? Date.now() - segmentStartRef.current : 0)) / 1000;

    // Tick the displayed clock while actively recording
    useEffect(() => {
        if (status !== "recording") return;
        const interval = setInterval(() => forceTick((t) => t + 1), 250);
        return () => clearInterval(interval);
    }, [status]);

    // Release the camera and any object URL on unmount
    useEffect(() => () => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        if (finalizedUrl) URL.revokeObjectURL(finalizedUrl);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const startCamera = async () => {
        setErrorMsg("");
        setStatus("requesting");
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: true });
            streamRef.current = stream;
            if (previewRef.current) {
                previewRef.current.srcObject = stream;
                previewRef.current.play?.().catch(() => { });
            }
            const type = pickRecorderMimeType();
            mimeTypeRef.current = type;
            const recorder = type ? new MediaRecorder(stream, { mimeType: type }) : new MediaRecorder(stream);
            chunksRef.current = [];
            recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
            recorder.onerror = (e) => { console.error("MediaRecorder error:", e); setErrorMsg("Something went wrong while recording. You can stop and try again."); };
            recorderRef.current = recorder;
            accumulatedMsRef.current = 0;
            segmentStartRef.current = Date.now();
            recorder.start();
            setStatus("recording");
        } catch (err) {
            console.error("getUserMedia failed:", err);
            setErrorMsg("Couldn't access the camera/microphone. Check your browser permissions and try again.");
            setStatus("idle");
        }
    };

    const pauseRecording = () => {
        const recorder = recorderRef.current;
        if (!recorder || status !== "recording") return;
        accumulatedMsRef.current += Date.now() - segmentStartRef.current;
        recorder.pause();
        setStatus("paused");
    };

    const resumeRecording = () => {
        const recorder = recorderRef.current;
        if (!recorder || status !== "paused") return;
        segmentStartRef.current = Date.now();
        recorder.resume();
        setStatus("recording");
    };

    const stopRecording = () => {
        const recorder = recorderRef.current;
        if (!recorder) return;
        if (status === "recording") accumulatedMsRef.current += Date.now() - segmentStartRef.current;
        recorder.onstop = () => {
            const blob = new Blob(chunksRef.current, { type: mimeTypeRef.current || "video/webm" });
            const ext = mimeTypeRef.current.includes("mp4") ? "mp4" : "webm";
            const file = new File([blob], `live-recording-${Date.now()}.${ext}`, { type: blob.type });
            const url = URL.createObjectURL(blob);
            setFinalizedUrl(url);
            streamRef.current?.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
            setStatus("stopped");
            // Collapse into the same videoFile/videoUrl the upload flow uses, so
            // Marking, Stats, Videos, and Games all pick it up exactly as normal.
            onVideoChange(url, file);
        };
        recorder.stop();
    };

    const recordAnother = () => {
        if (finalizedUrl) URL.revokeObjectURL(finalizedUrl);
        setFinalizedUrl(null);
        chunksRef.current = [];
        accumulatedMsRef.current = 0;
        setErrorMsg("");
        setStatus("idle");
    };

    const addMarker = (team, markerName) => {
        if (status !== "recording") return;
        // openMarker opens the player/notes/offset modal when any of those extras are
        // on; otherwise it logs the event immediately - recording keeps rolling either way.
        tagging.openMarker(team, markerName, getElapsedSeconds());
    };

    const elapsed = getElapsedSeconds();

    const showEventCounter = eventCounterEnabled && isPremium;
    const eventCounts = showEventCounter ? markers.map((name) => ({
        name,
        home: homeMarkers.filter((m) => m.name === name).length,
        away: awayMarkers.filter((m) => m.name === name).length,
    })) : [];

    // Which order the sections render in — user-adjustable, persisted in
    // localStorage so it survives tab switches and refreshes. Mirrors the
    // Marking tab's section reordering (ids are prefixed "live" so they
    // don't collide with the Marking tab's test ids, since that tab stays
    // mounted — just hidden — while this one is active).
    const LIVE_SECTION_ORDER_STORAGE_KEY = "snipshot_live_section_order";
    const DEFAULT_LIVE_SECTION_ORDER = ["liveVideoMarkers", "liveNotes", "liveCounter"];
    const [sectionOrder, setSectionOrder] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(LIVE_SECTION_ORDER_STORAGE_KEY) || "null");
            if (Array.isArray(saved) && DEFAULT_LIVE_SECTION_ORDER.every((id) => saved.includes(id))) return saved;
        } catch (e) { /* ignore malformed/unavailable storage */ }
        return DEFAULT_LIVE_SECTION_ORDER;
    });
    useEffect(() => {
        try { localStorage.setItem(LIVE_SECTION_ORDER_STORAGE_KEY, JSON.stringify(sectionOrder)); } catch (e) { /* ignore */ }
    }, [sectionOrder]);

    const visibleSectionOrder = sectionOrder.filter((id) => id !== "liveCounter" || showEventCounter);
    const moveSection = (id, direction) => {
        const visIdx = visibleSectionOrder.indexOf(id);
        const swapWith = visibleSectionOrder[visIdx + direction];
        if (!swapWith) return;
        setSectionOrder((prev) => {
            const a = prev.indexOf(id);
            const b = prev.indexOf(swapWith);
            const next = [...prev];
            [next[a], next[b]] = [next[b], next[a]];
            return next;
        });
    };

    const sectionContent = {
        liveVideoMarkers: (
            <VideoMarkersSplit
                videoPane={
                    <div>
                        <h3 className="heading-secondary text-lg text-[--text-primary] mb-4 flex items-center gap-2">
                            <Circle className="w-5 h-5 text-[--accent-away] fill-current" />Live Recording
                        </h3>

                        {!isSupported ? (
                            <div className="text-sm text-[--text-secondary]">
                                This device or browser doesn't support live camera recording. Try uploading a video from the Marking tab instead.
                            </div>
                        ) : status === "stopped" ? (
                            <div className="space-y-3">
                                <div className="video-container relative">
                                    <video src={finalizedUrl} controls className="w-full rounded-lg" data-testid="live-final-video" />
                                </div>
                                <p className="text-sm text-[--accent-green]">Recording saved - {formatClock(elapsed)} long. It's now loaded as your game video, markers and all.</p>
                                <button onClick={recordAnother} className="btn-secondary flex items-center gap-2">
                                    <Circle className="w-4 h-4" />Record Another
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="video-container relative bg-black rounded-lg overflow-hidden">
                                    <video ref={previewRef} muted autoPlay playsInline className="w-full rounded-lg" data-testid="live-preview" />
                                    {(status === "recording" || status === "paused") && (
                                        <div className={`absolute top-3 left-3 flex items-center gap-2 px-3 py-1.5 rounded-full text-white text-sm font-semibold ${status === "recording" ? "bg-red-600" : "bg-[--text-muted]"}`}>
                                            <Circle className={`w-3 h-3 fill-current ${status === "recording" ? "animate-pulse" : ""}`} />
                                            {status === "recording" ? "REC" : "PAUSED"} {formatClock(elapsed)}
                                        </div>
                                    )}
                                </div>

                                {errorMsg && <p className="text-sm text-red-400 flex items-center gap-2"><AlertCircle className="w-4 h-4" />{errorMsg}</p>}

                                <div className="flex flex-wrap gap-2">
                                    {status === "idle" && (
                                        <button onClick={startCamera} className="btn-primary flex items-center gap-2" data-testid="live-start-btn">
                                            <Circle className="w-4 h-4 fill-current" />Start Recording
                                        </button>
                                    )}
                                    {status === "requesting" && (
                                        <button disabled className="btn-primary flex items-center gap-2 opacity-60">
                                            <Loader2 className="w-4 h-4 animate-spin" />Requesting Camera...
                                        </button>
                                    )}
                                    {status === "recording" && (
                                        <>
                                            <button onClick={pauseRecording} className="btn-secondary flex items-center gap-2" data-testid="live-pause-btn">
                                                <Pause className="w-4 h-4" />Pause
                                            </button>
                                            <button onClick={stopRecording} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold" data-testid="live-stop-btn">
                                                <Square className="w-4 h-4 fill-current" />Stop &amp; Save
                                            </button>
                                        </>
                                    )}
                                    {status === "paused" && (
                                        <>
                                            <button onClick={resumeRecording} className="btn-primary flex items-center gap-2" data-testid="live-resume-btn">
                                                <Play className="w-4 h-4" />Resume
                                            </button>
                                            <button onClick={stopRecording} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold" data-testid="live-stop-btn">
                                                <Square className="w-4 h-4 fill-current" />Stop &amp; Save
                                            </button>
                                        </>
                                    )}
                                </div>
                                <p className="text-xs text-[--text-muted]">
                                    Pause any time the action stops (between periods, injuries, etc.) - Resume picks the same recording back up. Marker buttons work while you're actively recording.
                                </p>
                            </div>
                        )}
                    </div>
                }
                markersPane={
                    <div className="relative h-full flex flex-col">
                        <h3 className="heading-secondary text-lg text-[--text-primary] mb-4 shrink-0">Markers</h3>
                        <div className="grid grid-cols-2 gap-4 mb-6 shrink-0">
                            <div className="text-center"><div className="badge-home mb-3 inline-block">Your Team</div><p className="text-sm text-[--text-muted]">{homeMarkers.length} logged</p></div>
                            <div className="text-center"><div className="badge-away mb-3 inline-block">Opponent</div><p className="text-sm text-[--text-muted]">{awayMarkers.length} logged</p></div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 flex-1 min-h-0 content-start overflow-y-auto">
                            {markers.map((marker) => (
                                <div key={marker} className="contents">
                                    <button onClick={() => addMarker("home", marker)} className="marker-btn marker-btn-home" disabled={status !== "recording"}>{marker}</button>
                                    <button onClick={() => addMarker("away", marker)} className="marker-btn marker-btn-away" disabled={status !== "recording"}>{marker}</button>
                                </div>
                            ))}
                        </div>
                        {status !== "recording" && (
                            <p className="text-xs text-[--text-muted] mt-3 shrink-0">Marker buttons unlock once you're actively recording.</p>
                        )}

                        <MarkerDetailsModal tagging={tagging} homeTables={homeTables} awayTables={awayTables} />
                    </div>
                }
            />
        ),
        liveNotes: (
            <>
                <h3 className="heading-secondary text-lg text-[--text-primary] mb-2">Game Notes</h3>
                <p className="text-sm text-[--text-muted] mb-3">Overall notes about this game — not tied to any single clip. Saved along with the game session.</p>
                <textarea
                    value={gameNotes || ""}
                    onChange={(e) => setGameNotes(e.target.value)}
                    rows={5}
                    placeholder="e.g. Slow start in the first period, power play needs work, good effort on the penalty kill..."
                    className="input-base w-full resize-none"
                    data-testid="live-game-notes-textarea"
                />
            </>
        ),
        liveCounter: showEventCounter ? (
            <>
                <h3 className="heading-secondary text-lg text-[--text-primary] mb-4">Event Counter</h3>
                {eventCounts.length ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {eventCounts.map(({ name, home, away }) => (
                            <div key={name} className="rounded-lg border border-[--border-default] px-3 py-2">
                                <div className="text-sm text-[--text-secondary] truncate">{name}</div>
                                <div className="text-2xl font-bold text-[--text-primary]">{home + away}</div>
                                <div className="text-xs text-[--text-muted] flex gap-2">
                                    <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-[--accent-home]" />{home}</span>
                                    <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-[--accent-away]" />{away}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-[--text-muted] text-sm">No event types configured yet.</div>
                )}
            </>
        ) : null,
    };

    const sectionTestIds = { liveVideoMarkers: undefined, liveNotes: "live-game-notes-card", liveCounter: "live-event-counter-card" };

    return (
        <div className="p-6 space-y-6 animate-in" data-testid="live-tab">
            {visibleSectionOrder.map((id, i) => (
                <ResizableSection
                    key={id}
                    id={id}
                    className={`card-base p-6 ${id === "liveVideoMarkers" ? "relative" : ""}`}
                    canMoveUp={i > 0}
                    canMoveDown={i < visibleSectionOrder.length - 1}
                    moveUp={() => moveSection(id, -1)}
                    moveDown={() => moveSection(id, 1)}
                >
                    <div data-testid={sectionTestIds[id]}>{sectionContent[id]}</div>
                </ResizableSection>
            ))}
        </div>
    );
};

// Stats Tab - With sport-specific default tables
const StatsTab = ({ userId, sport, setShowPremiumModal, isPremium, homeTables, setHomeTables, awayTables, setAwayTables, hasRoster, setHasRoster }) => {
    const [activeTeam, setActiveTeam] = useState("home");
    const [editingTable, setEditingTable] = useState(null);
    const [newColumnName, setNewColumnName] = useState("");
    const [newTableName, setNewTableName] = useState("");
    const [saveStatus, setSaveStatus] = useState("");

    const currentTables = activeTeam === "home" ? homeTables : awayTables;
    const setCurrentTables = activeTeam === "home" ? setHomeTables : setAwayTables;

    // Rosters are saved/loaded per side: whichever side (Your Team / Opponent) is
    // active when you click Save is the side that gets saved, and whichever side is
    // active when you click Load is the side that saved roster gets pasted into —
    // it never jumps to the other side. Older saves (before this change) stored a
    // single combined { homeTables, awayTables } snapshot; we still read those, picking
    // whichever half matches the side you're currently viewing.
    const saveRoster = async () => {
        if (!isPremium) { setShowPremiumModal(true); return; }

        if (!userId || !window.firebaseFirestore) { setSaveStatus("Error: Not logged in"); return; }
        try {
            setSaveStatus("Saving...");
            const { doc, setDoc, serverTimestamp } = window.firebaseFirestore;
            await setDoc(doc(firebaseDb, "rosters", userId), { side: activeTeam, tables: currentTables, sport, updatedAt: serverTimestamp() });
            setHasRoster(true); setSaveStatus("Saved!"); setTimeout(() => setSaveStatus(""), 2000);
        } catch (e) { console.error("Error saving:", e); setSaveStatus("Error saving"); }
    };

    const loadRoster = async () => {
        if (!isPremium) { setShowPremiumModal(true); return; }

        if (!userId || !window.firebaseFirestore) return;
        try {
            setSaveStatus("Loading...");
            const { doc, getDoc } = window.firebaseFirestore;
            const rosterDoc = await getDoc(doc(firebaseDb, "rosters", userId));
            if (rosterDoc.exists()) {
                const data = rosterDoc.data();
                if (data.sport && data.sport !== sport && !window.confirm(`This saved roster was for ${data.sport}, not ${sport}. Loading it will bring back ${data.sport}'s stat columns. Continue?`)) {
                    setSaveStatus("");
                    return;
                }
                // New format: a single roster of tables, pasted into whichever side is active.
                // Legacy format: fall back to the half that matches the active side.
                const tables = data.tables || (activeTeam === "home" ? data.homeTables : data.awayTables);
                if (tables) setCurrentTables(tables);
                setSaveStatus("Loaded!");
            }
            else setSaveStatus("No saved roster");
            setTimeout(() => setSaveStatus(""), 2000);
        } catch (e) { setSaveStatus("Error loading"); }
    };

    const deleteRoster = async () => {
        if (!isPremium) { setShowPremiumModal(true); return; }
        if (!userId || !window.firebaseFirestore || !window.confirm("Delete your saved roster?")) return;
        try {
            setSaveStatus("Deleting...");
            const { doc, deleteDoc } = window.firebaseFirestore;
            await deleteDoc(doc(firebaseDb, "rosters", userId));
            setHasRoster(false); setSaveStatus("Deleted!"); setTimeout(() => setSaveStatus(""), 2000);
        } catch (e) { setSaveStatus("Error deleting"); }
    };

    const addTable = () => { setCurrentTables([...currentTables, { id: Date.now(), name: newTableName || `Table ${currentTables.length + 1}`, columns: ["#", "Name"], rows: [{ id: 1, values: ["1", ""] }] }]); setNewTableName(""); };
    const deleteTable = (id) => { if (currentTables.length > 1) setCurrentTables(currentTables.filter(t => t.id !== id)); };
    const addColumn = (tableId) => { if (!newColumnName.trim()) return; setCurrentTables(currentTables.map(t => t.id === tableId ? { ...t, columns: [...t.columns, newColumnName], rows: t.rows.map(r => ({ ...r, values: [...r.values, "0"] })) } : t)); setNewColumnName(""); setEditingTable(null); };
    const removeColumn = (tableId, colIndex) => { setCurrentTables(currentTables.map(t => t.id === tableId && t.columns.length > 2 ? { ...t, columns: t.columns.filter((_, i) => i !== colIndex), rows: t.rows.map(r => ({ ...r, values: r.values.filter((_, i) => i !== colIndex) })) } : t)); };
    const addRow = (tableId) => { setCurrentTables(currentTables.map(t => t.id === tableId ? { ...t, rows: [...t.rows, { id: Date.now(), values: t.columns.map((_, i) => i === 0 ? String(t.rows.length + 1) : i === 1 ? "" : "0") }] } : t)); };
    const removeRow = (tableId, rowId) => { setCurrentTables(currentTables.map(t => t.id === tableId && t.rows.length > 1 ? { ...t, rows: t.rows.filter(r => r.id !== rowId) } : t)); };
    const updateCell = (tableId, rowId, colIndex, value) => { setCurrentTables(currentTables.map(t => t.id === tableId ? { ...t, rows: t.rows.map(r => r.id === rowId ? { ...r, values: r.values.map((v, i) => i === colIndex ? value : v) } : r) } : t)); };
    const clearAllStats = () => { setCurrentTables(currentTables.map(t => ({ ...t, rows: t.rows.map(r => ({ ...r, values: r.values.map((v, i) => i < 2 ? v : "0") })) }))); };

    // Exports the full stat sheet (both Your Team and Opponent tables) as a single CSV file,
    // with each table's name as a section header followed by its column headers and rows.
    const exportStatsCSV = () => {
        const escapeCell = (val) => {
            const s = String(val ?? "");
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const lines = [];
        [["Your Team", homeTables], ["Opponent", awayTables]].forEach(([label, tables]) => {
            (tables || []).forEach((t) => {
                lines.push(escapeCell(`${label} - ${t.name}`));
                lines.push(t.columns.map(escapeCell).join(","));
                t.rows.forEach((r) => lines.push(r.values.map(escapeCell).join(",")));
                lines.push("");
            });
        });
        const csvContent = lines.join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `stat_sheet_${sport.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    return (
        <div className="p-6 space-y-6 animate-in" data-testid="stats-tab">
            <div className="flex gap-2">
                <button onClick={() => setActiveTeam("home")} className={`flex-1 py-3 rounded-lg font-semibold transition-all ${activeTeam === "home" ? "bg-[--accent-home] text-white shadow-lg" : "border-2 border-[--accent-home] text-[--accent-home]"}`} style={activeTeam === "home" ? { boxShadow: '0 4px 20px var(--accent-home-glow)' } : {}}>Your Team</button>
                <button onClick={() => setActiveTeam("away")} className={`flex-1 py-3 rounded-lg font-semibold transition-all ${activeTeam === "away" ? "bg-[--accent-away] text-white shadow-lg" : "border-2 border-[--accent-away] text-[--accent-away]"}`} style={activeTeam === "away" ? { boxShadow: '0 4px 20px var(--accent-away-glow)' } : {}}>Opponent</button>
            </div>

            {currentTables.map((table) => (
                <div key={table.id} className={`card-base overflow-hidden ${activeTeam === "home" ? "card-home" : "card-away"}`}>
                    <div className="flex items-center justify-between p-4 border-b border-[--border-default]">
                        <input type="text" value={table.name} onChange={(e) => setCurrentTables(currentTables.map(t => t.id === table.id ? { ...t, name: e.target.value } : t))} className="bg-transparent text-lg font-semibold text-[--text-primary] border-b border-transparent hover:border-[--border-default] focus:border-[--accent-home] focus:outline-none" />
                        <div className="flex gap-2">
                            <button onClick={() => setEditingTable(editingTable === table.id ? null : table.id)} className="btn-secondary text-sm flex items-center gap-1"><Columns className="w-4 h-4" />Edit Columns</button>
                            {currentTables.length > 1 && <button onClick={() => deleteTable(table.id)} className="text-red-400 hover:text-red-300 p-2"><Trash2 className="w-4 h-4" /></button>}
                        </div>
                    </div>
                    {editingTable === table.id && (
                        <div className="p-4 bg-[--bg-tertiary] border-b border-[--border-default]">
                            <div className="flex gap-2 mb-3"><input type="text" value={newColumnName} onChange={(e) => setNewColumnName(e.target.value)} placeholder="New column name" className="input-base flex-1" onKeyDown={(e) => e.key === "Enter" && addColumn(table.id)} /><button onClick={() => addColumn(table.id)} className="btn-primary"><Plus className="w-5 h-5" /></button></div>
                            <div className="flex flex-wrap gap-2">{table.columns.map((col, i) => <div key={i} className="flex items-center gap-1 bg-[--bg-card] px-3 py-1 rounded-lg"><span className="text-sm">{col}</span>{i >= 2 && <button onClick={() => removeColumn(table.id, i)} className="text-red-400 hover:text-red-300"><X className="w-3 h-3" /></button>}</div>)}</div>
                        </div>
                    )}
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="bg-[--bg-tertiary]">
                                    {table.columns.map((col, i) => <th key={i} className="px-4 py-3 text-center text-xs font-semibold text-[--text-secondary] uppercase tracking-wider border-b border-[--border-default]" style={{ minWidth: i === 1 ? '150px' : '80px' }}>{col}</th>)}
                                    <th className="w-10 border-b border-[--border-default]"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {table.rows.map((row) => (
                                    <tr key={row.id} className="hover:bg-[--bg-tertiary] transition-colors">
                                        {row.values.map((val, i) => <td key={i} className="px-4 py-2 border-b border-[--border-default] text-center"><input type={i < 2 ? "text" : "number"} value={val} onChange={(e) => updateCell(table.id, row.id, i, e.target.value)} className="w-full bg-transparent focus:outline-none focus:bg-[--bg-secondary] rounded px-2 py-1 text-center" /></td>)}
                                        <td className="px-2 py-2 border-b border-[--border-default] text-center"><button onClick={() => removeRow(table.id, row.id)} className="text-red-400 hover:text-red-300 p-1"><X className="w-4 h-4" /></button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <button onClick={() => addRow(table.id)} className="w-full py-3 text-[--text-muted] hover:text-[--text-primary] hover:bg-[--bg-tertiary] transition-colors flex items-center justify-center gap-2"><Plus className="w-4 h-4" />Add Row</button>
                </div>
            ))}

            <div className="card-base p-4"><div className="flex gap-2"><input type="text" value={newTableName} onChange={(e) => setNewTableName(e.target.value)} placeholder="New table name" className="input-base flex-1" /><button onClick={addTable} className="btn-secondary flex items-center gap-2"><Table2 className="w-4 h-4" />Add Table</button></div></div>

            <div className="flex flex-wrap gap-3 items-center">
                <button onClick={saveRoster} className="btn-primary flex items-center gap-2" title={`Saves the ${activeTeam === "home" ? "Your Team" : "Opponent"} roster shown below`}><Save className="w-4 h-4" />Save Roster {!isPremium && <Lock className="w-4 h-4" />}</button>
                <button onClick={loadRoster} className="btn-secondary flex items-center gap-2" disabled={!hasRoster} title={`Pastes the saved roster into ${activeTeam === "home" ? "Your Team" : "Opponent"}`}><List className="w-4 h-4" />Load Roster</button>
                <button onClick={deleteRoster} className="btn-secondary flex items-center gap-2" disabled={!hasRoster}><Trash2 className="w-4 h-4" />Delete Roster</button>
                <button onClick={() => { if (window.confirm(`Reset to default ${sport} stat sheets?`)) { setHomeTables(getDefaultTables(sport)); setAwayTables(getDefaultTables(sport)); } }} className="btn-secondary flex items-center gap-2">Reset to Defaults</button>
                <button onClick={exportStatsCSV} className="btn-secondary flex items-center gap-2" title="Export both teams' stat sheets as a CSV file" data-testid="export-stats-csv-btn"><Download className="w-4 h-4" />Export CSV</button>
                <button onClick={clearAllStats} className="btn-secondary flex items-center gap-2 ml-auto">Clear Stats</button>
                {saveStatus && <span className={`text-sm ${saveStatus.includes("Error") ? "text-red-400" : "text-green-400"}`}>{saveStatus}</span>}
            </div>
        </div>
    );
};

// Clip Player
// Two sliders sharing a center point: left one (before) fills outward to the left,
// right one (after) fills outward to the right - so both "point away" from the marker.
const OffsetDualSlider = ({ before, after, setBefore, setAfter, min = 3, max = 15 }) => (
    <div>
        <div className="flex items-center gap-3">
            <span className="text-[--accent-home] font-semibold w-16 text-right text-sm">{before}s</span>
            <input
                type="range"
                min={min}
                max={max}
                value={before}
                onChange={(e) => setBefore(parseInt(e.target.value))}
                className="settings-slider flex-1"
                style={{ direction: "rtl" }}
                data-testid="offset-before-slider"
            />
            <div className="w-px h-6 bg-[--border-default]" />
            <input
                type="range"
                min={min}
                max={max}
                value={after}
                onChange={(e) => setAfter(parseInt(e.target.value))}
                className="settings-slider flex-1"
                data-testid="offset-after-slider"
            />
            <span className="text-[--accent-home] font-semibold w-16 text-sm">{after}s</span>
        </div>
        <div className="flex justify-between text-xs text-[--text-muted] mt-1 px-1">
            <span>Before</span>
            <span>After</span>
        </div>
    </div>
);

const ClipPlayer = ({ videoUrl, startTime, offsetBefore, offsetAfter, clipNumber, totalClips }) => {
    const videoRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const actualStart = Math.max(0, startTime - offsetBefore);
    const actualEnd = startTime + offsetAfter;

    useEffect(() => { if (videoRef.current) videoRef.current.currentTime = actualStart; }, [actualStart]);
    const handleTimeUpdate = () => { if (videoRef.current && videoRef.current.currentTime >= actualEnd) { videoRef.current.pause(); setIsPlaying(false); } };
    const togglePlay = () => { if (videoRef.current) { if (isPlaying) videoRef.current.pause(); else { videoRef.current.currentTime = actualStart; videoRef.current.play(); } setIsPlaying(!isPlaying); } };

    return (
        <div className="relative bg-black rounded-lg overflow-hidden">
            <video ref={videoRef} src={videoUrl} onTimeUpdate={handleTimeUpdate} onEnded={() => setIsPlaying(false)} className="w-full" muted={isMuted} />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <button onClick={() => videoRef.current && (videoRef.current.currentTime = actualStart)} className="text-white hover:text-[--accent-home]"><SkipBack className="w-5 h-5" /></button>
                        <button onClick={togglePlay} className="text-white hover:text-[--accent-home]">{isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}</button>
                        <button onClick={() => { if (videoRef.current) { videoRef.current.muted = !videoRef.current.muted; setIsMuted(!isMuted); } }} className="text-white hover:text-[--accent-home]">{isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}</button>
                    </div>
                    <span className="text-white text-sm">Clip {clipNumber} of {totalClips}</span>
                </div>
            </div>
        </div>
    );
};

// Compilation Card — lives outside VideosTab (as a stable top-level component)
// rather than being defined inside VideosTab's render body. Defining a component
// inside another component's function means React sees a brand-new component
// type on every render of the parent — so every progress tick during a download
// (setDownloadProgress) was unmounting and remounting every card from scratch,
// including the embedded video player. That's what caused the flicker. Passing
// everything in as props instead keeps this component's identity stable across
// VideosTab re-renders, so only the parts that actually changed update.
const CompilationCard = ({
    team, markerName, clips, color,
    videoUrl, videoFile, offsetBefore, offsetAfter,
    activeCompilation, setActiveCompilation,
    currentClipIndex, setCurrentClipIndex,
    downloading, downloadProgress, onDownload,
    onShareClip, onDeleteMarker, onEditClip,
}) => {
    const isActive = activeCompilation === `${team}-${markerName}`;
    const isDownloading = downloading === `${team}-${markerName}`;

    // If a delete brings the clip count below the current index, snap back
    // into range instead of reading past the end of the (now shorter) array.
    // currentClipIndex is shared state across every card in this tab, so this
    // must only run for the card that's actually open — otherwise an inactive
    // card with fewer clips would clamp the index for the active one too,
    // making Next/Prev look broken.
    useEffect(() => {
        if (isActive && currentClipIndex >= clips.length) setCurrentClipIndex(Math.max(0, clips.length - 1));
    }, [isActive, clips.length]);

    const activeClip = clips[currentClipIndex];

    const handleDeleteClip = () => {
        if (!onDeleteMarker || !activeClip) return;
        if (!window.confirm(`Delete this "${activeClip.name}" event? This can't be undone.`)) return;
        const clipTeam = team === "player" ? activeClip.__team : team;
        onDeleteMarker(clipTeam, activeClip.id);
    };

    return (
        <div className={`bg-[--bg-tertiary] rounded-lg overflow-hidden border-l-4 ${color === 'home' ? 'border-[--accent-home]' : color === 'away' ? 'border-[--accent-away]' : 'border-purple-400'}`}>
            <div className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-[--bg-secondary] transition-colors" onClick={() => { setActiveCompilation(isActive ? null : `${team}-${markerName}`); setCurrentClipIndex(0); }}>
                <div><h4 className="font-semibold text-[--text-primary]">{markerName}</h4><p className="text-sm text-[--text-muted]">{clips.length} clip{clips.length !== 1 ? 's' : ''}</p></div>
                <div className="flex items-center gap-2">
                    <button onClick={(e) => { e.stopPropagation(); onDownload(team, markerName, clips); }} disabled={isDownloading || (!videoFile && !videoUrl)} className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors disabled:opacity-50 ${color === 'home' ? 'bg-[--accent-home]/20 text-[--accent-home] hover:bg-[--accent-home]/30' : color === 'away' ? 'bg-[--accent-away]/20 text-[--accent-away] hover:bg-[--accent-away]/30' : 'bg-purple-400/20 text-purple-400 hover:bg-purple-400/30'}`}>
                        {isDownloading ? <Loader2 className="w-4 h-4 spin" /> : <Download className="w-4 h-4" />}
                    </button>
                    <ChevronRight className={`w-5 h-5 text-[--text-muted] transition-transform ${isActive ? 'rotate-90' : ''}`} />
                </div>
            </div>
            {isDownloading && <div className="px-4 pb-3"><div className="progress-bar"><div className="progress-bar-fill" style={{ width: '100%' }}></div></div><p className="text-xs text-[--text-muted] mt-1">{downloadProgress}</p></div>}
            {isActive && videoUrl && activeClip && (
                <div className="border-t border-[--border-default]">
                    <ClipPlayer
                        videoUrl={videoUrl}
                        startTime={activeClip.time}
                        offsetBefore={activeClip.offsetBefore ?? offsetBefore}
                        offsetAfter={activeClip.offsetAfter ?? offsetAfter}
                        clipNumber={currentClipIndex + 1}
                        totalClips={clips.length}
                    />
                    {team === "player" && (
                        <div className="px-4 py-3 bg-[--bg-card] border-t border-[--border-default]">
                            <p className="text-xs text-[--text-muted] mb-1 uppercase tracking-wide">Event</p>
                            <p className="text-sm text-[--text-secondary]">{activeClip.name}</p>
                        </div>
                    )}
                    {activeClip.note && (
                        <div className="px-4 py-3 bg-[--bg-card] border-t border-[--border-default]">
                            <p className="text-xs text-[--text-muted] mb-1 uppercase tracking-wide">Note</p>
                            <p className="text-sm text-[--text-secondary] whitespace-pre-wrap">{activeClip.note}</p>
                        </div>
                    )}
                    <div className="flex items-center justify-between gap-2 p-3 bg-[--bg-card] border-t border-[--border-default]">
                        <div className="flex items-center gap-2">
                            {onEditClip && (
                                <button
                                    onClick={() => onEditClip(team === "player" ? activeClip.__team : team, activeClip)}
                                    className="btn-secondary text-sm flex items-center gap-2"
                                    title="Edit this clip's players, length, and notes"
                                    data-testid={`edit-clip-${team}-${markerName}-${currentClipIndex}`}
                                >
                                    <Pencil className="w-4 h-4" />Edit
                                </button>
                            )}
                            {onShareClip ? (
                                <button
                                    onClick={() => {
                                        // Player-grouped cards use "player" as a synthetic team label and
                                        // the player's name as markerName - neither is real. Resolve the
                                        // clip's actual team and event name (already tracked on the clip
                                        // via __team, the same way handleDeleteClip does) so sharing works
                                        // the same from a player's compilation as from an event's.
                                        const shareTeam = team === "player" ? activeClip.__team : team;
                                        const shareName = team === "player" ? activeClip.name : markerName;
                                        onShareClip(shareTeam, shareName, [activeClip]);
                                    }}
                                    className="btn-secondary text-sm flex items-center gap-2"
                                    title="Share only this clip with players"
                                    data-testid={`share-clip-${team}-${markerName}-${currentClipIndex}`}
                                >
                                    <Share2 className="w-4 h-4" />Share this clip
                                </button>
                            ) : <span />}
                            {onDeleteMarker && (
                                <button
                                    onClick={handleDeleteClip}
                                    className="text-red-400 hover:text-red-300 text-sm flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-red-400/10 transition-colors"
                                    title="Delete this event"
                                    data-testid={`delete-clip-${team}-${markerName}-${currentClipIndex}`}
                                >
                                    <Trash2 className="w-4 h-4" />Delete
                                </button>
                            )}
                        </div>
                        {clips.length > 1 && (
                            <div className="flex items-center gap-2">
                                <button onClick={() => setCurrentClipIndex(Math.max(0, currentClipIndex - 1))} disabled={currentClipIndex === 0} className="btn-secondary text-sm disabled:opacity-50"><SkipBack className="w-4 h-4" /></button>
                                <div className="flex gap-1">{clips.map((_, i) => <button key={i} onClick={() => setCurrentClipIndex(i)} className={`w-2 h-2 rounded-full transition-colors ${i === currentClipIndex ? (color === 'home' ? 'bg-[--accent-home]' : 'bg-[--accent-away]') : 'bg-[--border-default] hover:bg-[--border-hover]'}`} />)}</div>
                                <button onClick={() => setCurrentClipIndex(Math.min(clips.length - 1, currentClipIndex + 1))} disabled={currentClipIndex === clips.length - 1} className="btn-secondary text-sm disabled:opacity-50"><SkipForward className="w-4 h-4" /></button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Videos Tab with Backend Download
const VideosTab = ({ homeMarkers, awayMarkers, videoUrl, videoFile, offsetBefore, offsetAfter, setShowPremiumModal, isPremium, playerTaggingEnabled, onShareClip, onDeleteMarker, notesEnabled, offsetMode, categoricalOffsets, homeTables, awayTables, setHomeTables, setAwayTables, onUpdateMarker, onGoToTimestamp }) => {
    // Same details modal the Marking tab uses, opened in edit mode on an existing clip
    const tagging = useMarkerTagging({ isPremium, playerTaggingEnabled, notesEnabled, offsetMode, offsetBefore, offsetAfter, categoricalOffsets, homeTables, awayTables, setHomeTables, setAwayTables, onUpdateMarker, onGoToTimestamp });
    const handleEditClip = (team, clip) => tagging.openEditor(team, clip);
    const [viewMode, setViewMode] = useState("event"); // "event" | "player"
    const [activeCompilation, setActiveCompilation] = useState(null);
    const [currentClipIndex, setCurrentClipIndex] = useState(0);
    const [downloading, setDownloading] = useState(null);
    const [downloadProgress, setDownloadProgress] = useState("");

    const groupByName = (markers) => markers.reduce((acc, m) => { if (!acc[m.name]) acc[m.name] = []; acc[m.name].push(m); return acc; }, {});
    const homeGroups = groupByName(homeMarkers);
    const awayGroups = groupByName(awayMarkers);

    // Group every tagged event (home + away) by the players assigned to it.
    // Each clip keeps track of which side it came from (via __team) so a delete
    // triggered from the player view still removes it from the right marker list.
    const groupByPlayer = (homeList, awayList) => {
        const acc = {};
        [["home", homeList], ["away", awayList]].forEach(([team, list]) => {
            list.forEach((m) => {
                (m.players || []).forEach((p) => {
                    // A tagged player carries its own roster team (p.team) since players can
                    // now be pulled in from either side regardless of which team the event
                    // itself is attributed to - fall back to the event's team for older data
                    // saved before that was tracked, and key on it so same #/name on both
                    // rosters don't collide into one entry.
                    const playerTeam = p.team || team;
                    const key = `${playerTeam}|${p.number}|${p.name}`;
                    if (!acc[key]) acc[key] = { player: p, clips: [] };
                    acc[key].clips.push({ ...m, __team: team });
                });
            });
        });
        return acc;
    };
    const playerGroups = groupByPlayer(homeMarkers, awayMarkers);
    const hasTaggedEvents = Object.keys(playerGroups).length > 0;

    const handleDownload = async (team, markerName, clips) => {
        if (!videoFile && !videoUrl) { alert("Please upload a video first"); return; }

        setDownloading(`${team}-${markerName}`);
        setDownloadProgress("Preparing video...");

        try {
            // videoFile is only a real File right after a fresh upload — if this session
            // was loaded from Saved Games it's just { name, size, type } metadata and the
            // real bytes live at videoUrl (a Firebase Storage URL). Rather than fetching
            // that URL from the browser (which Firebase Storage's CORS policy blocks for
            // this domain), we hand the URL to the backend and let it download the video
            // itself — server-to-server requests aren't subject to browser CORS at all.
            // These two cases hit separate backend endpoints (rather than one endpoint
            // with an optional file field) to avoid a known FastAPI quirk where an
            // Optional[UploadFile] parameter is unreliably still treated as required.
            const hasRealFile = videoFile instanceof Blob;
            if (!hasRealFile && !videoUrl) throw new Error("No video available to process.");

            const formData = new FormData();
            if (hasRealFile) {
                formData.append("video", videoFile, videoFile?.name || "video.mp4");
            } else {
                formData.append("video_url", videoUrl);
            }
            formData.append("clips_json", JSON.stringify(clips.map(c => ({
                time: c.time,
                name: c.name,
                offset_before: c.offsetBefore ?? offsetBefore,
                offset_after: c.offsetAfter ?? offsetAfter
            }))));
            formData.append("team", team);
            formData.append("marker_name", markerName);

            setDownloadProgress("Processing clips...");

            console.log(`Backend Url ${BACKEND_URL}`);

            const idToken = await firebaseAuth.currentUser?.getIdToken();
            const response = await axios.post(`${BACKEND_URL}/api/${hasRealFile ? "process-video" : "process-video-url"}`, formData, {
                responseType: 'blob',
                headers: { "Authorization": `Bearer ${idToken}` },
                onUploadProgress: (e) => { if (e.total) setDownloadProgress(`Uploading: ${Math.round((e.loaded / e.total) * 100)}%`); }
            });

            setDownloadProgress("Download ready!");

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.download = `${markerName.replace(/\s+/g, '_')}_${team}_compilation.mp4`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error("Download error:", error);
            alert(error.message || "Error processing video. Please try again.");
        } finally {
            setDownloading(null);
            setDownloadProgress("");
        }
    };

    return (
        <div className="p-6 animate-in" data-testid="videos-tab">
            {(hasTaggedEvents || (playerTaggingEnabled && isPremium)) && (
                <div className="flex gap-2 mb-6">
                    <button onClick={() => setViewMode("event")} className={`flex-1 py-3 rounded-lg font-semibold transition-all border-2 ${viewMode === "event" ? "bg-[--accent-home] border-[--accent-home] text-white" : "border-[--border-default] text-[--text-secondary]"}`}>By Event</button>
                    <button onClick={() => setViewMode("player")} className={`flex-1 py-3 rounded-lg font-semibold transition-all border-2 ${viewMode === "player" ? "bg-[--accent-home] border-[--accent-home] text-white" : "border-[--border-default] text-[--text-secondary]"}`} data-testid="videos-view-by-player">By Player</button>
                </div>
            )}

            {viewMode === "player" ? (
                <div className="card-base p-6">
                    <h3 className="heading-secondary text-xl text-[--text-primary] mb-4 flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-purple-400" />Player Compilations</h3>
                    {!hasTaggedEvents ? (
                        <div className="text-center py-12 text-[--text-muted]"><Video className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>No events tagged to players yet</p></div>
                    ) : (
                        <div className="width-75 mx-auto space-y-3">
                            {Object.entries(playerGroups).map(([key, { player, clips }]) => (
                                <CompilationCard
                                    key={key} team="player" markerName={`#${player.number || "-"} ${player.name || "Unnamed"}`} clips={clips} color="player"
                                    videoUrl={videoUrl} videoFile={videoFile} offsetBefore={offsetBefore} offsetAfter={offsetAfter}
                                    activeCompilation={activeCompilation} setActiveCompilation={setActiveCompilation}
                                    currentClipIndex={currentClipIndex} setCurrentClipIndex={setCurrentClipIndex}
                                    downloading={downloading} downloadProgress={downloadProgress} onDownload={handleDownload}
                                    onShareClip={onShareClip} onDeleteMarker={onDeleteMarker} onEditClip={handleEditClip}
                                />
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                <div className="grid md:grid-cols-2 gap-6">
                    <div className="card-base card-home p-6">
                        <h3 className="heading-secondary text-xl text-[--text-primary] mb-4 flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-[--accent-home]" />Your Team Videos</h3>
                        {Object.keys(homeGroups).length === 0 ? <div className="text-center py-12 text-[--text-muted]"><Video className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>No videos marked yet</p></div> : <div className="space-y-3">{Object.entries(homeGroups).map(([name, clips]) => (
                            <CompilationCard
                                key={name} team="home" markerName={name} clips={clips} color="home"
                                videoUrl={videoUrl} videoFile={videoFile} offsetBefore={offsetBefore} offsetAfter={offsetAfter}
                                activeCompilation={activeCompilation} setActiveCompilation={setActiveCompilation}
                                currentClipIndex={currentClipIndex} setCurrentClipIndex={setCurrentClipIndex}
                                downloading={downloading} downloadProgress={downloadProgress} onDownload={handleDownload}
                                onShareClip={onShareClip} onDeleteMarker={onDeleteMarker} onEditClip={handleEditClip}
                            />
                        ))}</div>}
                    </div>
                    <div className="card-base card-away p-6">
                        <h3 className="heading-secondary text-xl text-[--text-primary] mb-4 flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-[--accent-away]" />Opponent Videos</h3>
                        {Object.keys(awayGroups).length === 0 ? <div className="text-center py-12 text-[--text-muted]"><Video className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>No videos marked yet</p></div> : <div className="space-y-3">{Object.entries(awayGroups).map(([name, clips]) => (
                            <CompilationCard
                                key={name} team="away" markerName={name} clips={clips} color="away"
                                videoUrl={videoUrl} videoFile={videoFile} offsetBefore={offsetBefore} offsetAfter={offsetAfter}
                                activeCompilation={activeCompilation} setActiveCompilation={setActiveCompilation}
                                currentClipIndex={currentClipIndex} setCurrentClipIndex={setCurrentClipIndex}
                                downloading={downloading} downloadProgress={downloadProgress} onDownload={handleDownload}
                                onShareClip={onShareClip} onDeleteMarker={onDeleteMarker} onEditClip={handleEditClip}
                            />
                        ))}</div>}
                    </div>
                </div>
            )}
            <div className="mt-6 p-4 bg-[--bg-tertiary] rounded-lg border border-[--border-default]">
                <div className="flex items-start gap-3"><Info className="w-5 h-5 text-[--accent-home] mt-0.5" /><div><p className="text-[--text-secondary]"><strong className="text-[--text-primary]">Video Compilations:</strong> Click to preview clips, or download a combined video file.</p><p className="text-sm text-[--text-muted] mt-1">Before/after clip duration is set when each event is marked (Settings → Clip Length)</p></div></div>
            </div>

            <MarkerDetailsModal tagging={tagging} homeTables={homeTables} awayTables={awayTables} />
        </div>
    );
};

// Multi-Game Compilation Viewer — same swipeable clip UI as VideosTab's
// CompilationCard, but clips here belong to DIFFERENT saved games rather
// than one currently-loaded video, so it resolves a Storage download URL
// per unique videoPath instead of assuming a single already-known videoUrl.
const MultiGameCompilationView = ({ compilation, onClose, onDeleteClip, onShareClip, isPremium, setShowPremiumModal }) => {
    const { team, markerName, clips } = compilation;
    const [clipList, setClipList] = useState(clips);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [videoUrls, setVideoUrls] = useState({}); // videoPath -> url | null (failed)
    const [downloading, setDownloading] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState("");
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        const uniquePaths = [...new Set(clipList.map((c) => c.videoPath).filter(Boolean))];
        const missing = uniquePaths.filter((p) => !(p in videoUrls));
        if (!missing.length || !window.firebaseStorage) return;
        const { ref, getDownloadURL } = window.firebaseStorage;
        missing.forEach((path) => {
            getDownloadURL(ref(firebaseStorage, path))
                .then((url) => setVideoUrls((prev) => ({ ...prev, [path]: url })))
                .catch(() => setVideoUrls((prev) => ({ ...prev, [path]: null })));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clipList]);

    useEffect(() => {
        if (currentIndex >= clipList.length) setCurrentIndex(Math.max(0, clipList.length - 1));
    }, [clipList.length, currentIndex]);

    const activeClip = clipList[currentIndex];
    const activeUrl = activeClip ? videoUrls[activeClip.videoPath] : undefined;
    const gameCount = new Set(clipList.map((c) => c.sessionId)).size;

    const handleDelete = async () => {
        if (!activeClip || !onDeleteClip) return;
        if (!window.confirm(`Delete this "${activeClip.name}" event from "${activeClip.sessionName}"? This can't be undone.`)) return;
        setDeleting(true);
        try {
            await onDeleteClip(activeClip);
            setClipList((prev) => prev.filter((c) => !(c.sessionId === activeClip.sessionId && c.id === activeClip.id)));
        } catch (e) {
            console.error(e);
            alert("Couldn't delete that event. Try again.");
        } finally {
            setDeleting(false);
        }
    };

    // Groups clips by their source video so the backend downloads each
    // game's video exactly once, then extracts every clip that came from
    // it, in the order those games appear in this compilation.
    const handleDownloadAll = async () => {
        const ready = clipList.filter((c) => videoUrls[c.videoPath]);
        if (!ready.length) return;
        setDownloading(true);
        setDownloadProgress("Preparing compilation...");
        try {
            const order = [];
            const bySource = new Map();
            ready.forEach((c) => {
                if (!bySource.has(c.videoPath)) { bySource.set(c.videoPath, []); order.push(c.videoPath); }
                bySource.get(c.videoPath).push(c);
            });
            const sources = order.map((path) => ({
                video_url: videoUrls[path],
                clips: bySource.get(path).map((c) => ({
                    time: c.time,
                    offset_before: c.offsetBefore ?? 5,
                    offset_after: c.offsetAfter ?? 5,
                })),
            }));

            setDownloadProgress(`Processing clips from ${sources.length} game${sources.length !== 1 ? "s" : ""}...`);
            const idToken = await firebaseAuth.currentUser?.getIdToken();
            const response = await axios.post(`${BACKEND_URL}/api/process-compilation`, {
                sources,
                output_name: `${markerName}_${team}_multi_game_compilation`,
            }, {
                responseType: "blob",
                headers: { "Authorization": `Bearer ${idToken}` }
            });

            setDownloadProgress("Download ready!");
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.download = `${markerName.replace(/\s+/g, "_")}_${team}_multi_game_compilation.mp4`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Multi-game download error:", error);
            alert("Error processing this compilation. It may cover too much video at once — try fewer games or clips.");
        } finally {
            setDownloading(false);
            setDownloadProgress("");
        }
    };

    if (!clipList.length) {
        return (
            <div className="card-base p-6 text-center text-[--text-muted]" data-testid="multi-game-compilation-empty">
                <p>No clips left in this compilation.</p>
                <button onClick={onClose} className="btn-secondary mt-3">Close</button>
            </div>
        );
    }

    return (
        <div className="card-base overflow-hidden border-t border-[--border-default]" data-testid="multi-game-compilation">
            <div className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap border-b border-[--border-default]">
                <h4 className="font-semibold text-[--text-primary]">
                    {markerName} · {clipList.length} clip{clipList.length !== 1 ? "s" : ""} across {gameCount} game{gameCount !== 1 ? "s" : ""}
                </h4>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleDownloadAll}
                        disabled={downloading}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors disabled:opacity-50 ${team === "home" ? "bg-[--accent-home]/20 text-[--accent-home] hover:bg-[--accent-home]/30" : "bg-[--accent-away]/20 text-[--accent-away] hover:bg-[--accent-away]/30"}`}
                        title="Download every clip as one combined video"
                        data-testid="download-multi-game-compilation"
                    >
                        {downloading ? <Loader2 className="w-4 h-4 spin" /> : <Download className="w-4 h-4" />}
                    </button>
                    <button onClick={onClose} className="btn-secondary text-sm">Close</button>
                </div>
            </div>

            {downloading && (
                <div className="px-4 pt-3"><div className="progress-bar"><div className="progress-bar-fill" style={{ width: "100%" }}></div></div><p className="text-xs text-[--text-muted] mt-1">{downloadProgress}</p></div>
            )}

            {activeClip && (
                <div>
                    <div className="px-4 py-2 text-sm text-[--text-secondary] bg-[--bg-tertiary] flex items-center gap-2 truncate">
                        <Video className="w-3.5 h-3.5 shrink-0" /> {activeClip.sessionName}
                    </div>
                    {activeUrl === undefined && <div className="p-8 text-center text-[--text-muted] flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading video...</div>}
                    {activeUrl === null && <div className="p-8 text-center text-[--text-muted]">Couldn't load this game's video.</div>}
                    {activeUrl && (
                        <ClipPlayer
                            videoUrl={activeUrl}
                            startTime={activeClip.time}
                            offsetBefore={activeClip.offsetBefore ?? 5}
                            offsetAfter={activeClip.offsetAfter ?? 5}
                            clipNumber={currentIndex + 1}
                            totalClips={clipList.length}
                        />
                    )}
                    {activeClip.note && (
                        <div className="px-4 py-3 bg-[--bg-card] border-t border-[--border-default]">
                            <p className="text-xs text-[--text-muted] mb-1 uppercase tracking-wide">Note</p>
                            <p className="text-sm text-[--text-secondary] whitespace-pre-wrap">{activeClip.note}</p>
                        </div>
                    )}
                    <div className="flex items-center justify-between gap-2 p-3 bg-[--bg-card] border-t border-[--border-default]">
                        <div className="flex items-center gap-2">
                            {onShareClip && (
                                <button
                                    onClick={() => onShareClip(activeClip)}
                                    className="btn-secondary text-sm flex items-center gap-2"
                                    title="Share only this clip with players"
                                    data-testid="share-multi-game-clip"
                                >
                                    <Share2 className="w-4 h-4" />Share this clip
                                </button>
                            )}
                            <button
                                onClick={handleDelete}
                                disabled={deleting}
                                className="text-red-400 hover:text-red-300 text-sm flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-red-400/10 transition-colors disabled:opacity-50"
                                title="Delete this event from its original game"
                                data-testid="delete-multi-game-clip"
                            >
                                <Trash2 className="w-4 h-4" />Delete
                            </button>
                        </div>
                        {clipList.length > 1 && (
                            <div className="flex items-center gap-2">
                                <button onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))} disabled={currentIndex === 0} className="btn-secondary text-sm disabled:opacity-50"><SkipBack className="w-4 h-4" /></button>
                                <span className="text-xs text-[--text-muted] w-14 text-center">{currentIndex + 1} / {clipList.length}</span>
                                <button onClick={() => setCurrentIndex(Math.min(clipList.length - 1, currentIndex + 1))} disabled={currentIndex === clipList.length - 1} className="btn-secondary text-sm disabled:opacity-50"><SkipForward className="w-4 h-4" /></button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Settings Tab with Sport Change
const FEEDBACK_TYPES = [
    { id: "bug", label: "Bug", icon: Bug },
    { id: "suggestion", label: "Suggestion", icon: Lightbulb },
    { id: "other", label: "Other", icon: MessageSquare },
];

// Feedback tab, available to both coach and player accounts. Submissions go
// straight to Firestore's "feedback" collection — nothing here reads them
// back. Only the app owner can read that collection (enforced by Firestore
// security rules keyed to the owner's uid), so this stays a one-way mailbox
// from the app's point of view; responses are reviewed in the Firebase
// console, not in the app itself.
const FeedbackTab = ({ userId, userEmail }) => {
    const [type, setType] = useState("bug");
    const [message, setMessage] = useState("");
    const [status, setStatus] = useState(""); // "", "sending", "sent", "error"

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!message.trim()) return;
        setStatus("sending");
        try {
            const { doc, setDoc, collection, serverTimestamp } = window.firebaseFirestore;
            await setDoc(doc(collection(firebaseDb, "feedback")), {
                type,
                message: message.trim(),
                userId: userId || null,
                userEmail: userEmail || null,
                page: typeof window !== "undefined" ? window.location.href : null,
                userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
                createdAt: serverTimestamp(),
            });
            setStatus("sent");
            setMessage("");
            setTimeout(() => setStatus(""), 4000);
        } catch (err) {
            console.error("Feedback submit error:", err);
            setStatus("error");
        }
    };

    return (
        <div className="p-6 max-w-2xl mx-auto animate-in" data-testid="feedback-tab">
            <div className="card-base p-6">
                <h2 className="heading-secondary text-xl text-[--text-primary] mb-2">Send Feedback</h2>
                <p className="text-sm text-[--text-secondary] mb-6">Found a bug, or have an idea to make Snipshot better? Let us know — only the Snipshot team sees these.</p>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-3 gap-2">
                        {FEEDBACK_TYPES.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => setType(t.id)}
                                className={`p-3 rounded-lg border-2 text-center transition-all ${type === t.id ? "border-[--accent-home]" : "border-[--border-default] hover:border-[--border-hover]"}`}
                                data-testid={`feedback-type-${t.id}`}
                            >
                                <t.icon className="w-5 h-5 mx-auto mb-1 text-[--text-primary]" />
                                <p className="text-sm font-medium text-[--text-primary]">{t.label}</p>
                            </button>
                        ))}
                    </div>

                    <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={6}
                        placeholder="Tell us what happened, or what you'd like to see..."
                        className="input-base w-full resize-none"
                        required
                        data-testid="feedback-message"
                    />

                    {status === "error" && <div className="flex items-center gap-2 text-red-400 text-sm bg-red-400/10 p-3 rounded-lg" data-testid="feedback-error"><AlertCircle className="w-4 h-4" />Something went wrong sending that. Try again?</div>}
                    {status === "sent" && <div className="flex items-center gap-2 text-green-400 text-sm bg-green-400/10 p-3 rounded-lg" data-testid="feedback-sent"><Check className="w-4 h-4" />Thanks — we got it!</div>}

                    <button type="submit" className="btn-primary w-full" disabled={status === "sending" || !message.trim()} data-testid="feedback-submit">
                        {status === "sending" ? "Sending..." : "Send Feedback"}
                    </button>
                </form>
            </div>
        </div>
    );
};

const SettingsTab = ({ userProfile, setShowPremiumModal, markers, setMarkers, offsetBefore, setOffsetBefore, offsetAfter, setOffsetAfter, offsetMode, setOffsetMode, categoricalOffsets, setCategoricalOffsets, videoSize, setVideoSize, theme, setTheme, sport, onSportChange, onDeleteAccount, onSubscribe, onManageSubscription, isPremium, playerTaggingEnabled, setPlayerTaggingEnabled, homeTables, awayTables, notesEnabled, setNotesEnabled, eventCounterEnabled, setEventCounterEnabled, pauseOnMarkEnabled, setPauseOnMarkEnabled, authProviderId, onChangePassword }) => {
    const [activeSection, setActiveSection] = useState("video");
    const [newMarker, setNewMarker] = useState("");
    const sports = Object.keys(SPORT_MARKERS);

    const rosterIsEmpty = getRosterPlayers(homeTables).length === 0 && getRosterPlayers(awayTables).length === 0;

    const handleSportChange = async (newSport) => {
        await onSportChange(newSport);
    };

    const handleTogglePlayerTagging = () => {
        // Only gate turning the feature ON; a downgraded user must always be able to turn it off.
        if (!isPremium && !playerTaggingEnabled) { setShowPremiumModal(true); return; }
        if (!playerTaggingEnabled && rosterIsEmpty) return; // guarded in the UI below
        setPlayerTaggingEnabled(!playerTaggingEnabled);
    };

    const handleToggleNotes = () => {
        if (!isPremium && !notesEnabled) { setShowPremiumModal(true); return; }
        setNotesEnabled(!notesEnabled);
    };

    const handleToggleEventCounter = () => {
        if (!isPremium && !eventCounterEnabled) { setShowPremiumModal(true); return; }
        setEventCounterEnabled(!eventCounterEnabled);
    };

    // Free feature — no premium gate, unlike the toggles above.
    const handleTogglePauseOnMark = () => {
        setPauseOnMarkEnabled(!pauseOnMarkEnabled);
    };

    const handleOffsetModeChange = (mode) => {
        if (mode !== "classic" && !isPremium) { setShowPremiumModal(true); return; }
        setOffsetMode(mode);
    };

    const handleClassicOffsetChange = (v) => {
        setOffsetBefore(v);
        setOffsetAfter(v);
    };

    // Updates one marker's before/after in categoricalOffsets, seeding it from the
    // general offsetBefore/offsetAfter the first time a given marker is touched.
    const setCategoricalOffsetFor = (markerName, field, value) => {
        setCategoricalOffsets({
            ...categoricalOffsets,
            [markerName]: {
                before: offsetBefore,
                after: offsetAfter,
                ...categoricalOffsets[markerName],
                [field]: value,
            },
        });
    };

    return (
        <div className="p-6 animate-in" data-testid="settings-tab">
            <div className="flex gap-2 mb-6">
                <button onClick={() => setActiveSection("video")} className={`flex-1 py-3 rounded-lg font-semibold transition-all border-2 ${activeSection === "video" ? "bg-[--accent-home] border-[--accent-home] text-white" : "border-[--border-default] text-[--text-secondary]"}`}>Video Settings</button>
                <button onClick={() => setActiveSection("appearance")} className={`flex-1 py-3 rounded-lg font-semibold transition-all border-2 ${activeSection === "appearance" ? "bg-[--accent-home] border-[--accent-home] text-white" : "border-[--border-default] text-[--text-secondary]"}`}>Appearance</button>
                <button onClick={() => setActiveSection("profile")} className={`flex-1 py-3 rounded-lg font-semibold transition-all border-2 ${activeSection === "profile" ? "bg-[--accent-home] border-[--accent-home] text-white" : "border-[--border-default] text-[--text-secondary]"}`}>Profile</button>
            </div>

            {activeSection === "video" && (
                <div className="space-y-6">
                    <div className="card-base p-6">
                        <div className="flex items-center gap-2 mb-4"><h3 className="heading-secondary text-lg text-[--text-primary]">Clip Length</h3><span className="tooltip text-[--text-muted]" data-tooltip="Seconds before/after marker"><Info className="w-4 h-4" /></span></div>
                        {offsetMode === "general" && isPremium ? (
                            <OffsetDualSlider before={offsetBefore} after={offsetAfter} setBefore={setOffsetBefore} setAfter={setOffsetAfter} />
                        ) : (
                            <div className="flex items-center gap-4"><span className="text-[--text-muted] w-8">3s</span><input type="range" min="3" max="12" value={offsetBefore} onChange={(e) => handleClassicOffsetChange(parseInt(e.target.value))} className="settings-slider flex-1" /><span className="text-[--text-muted] w-8">12s</span><span className="text-[--accent-home] font-semibold w-12 text-right">{offsetBefore}s</span></div>
                        )}
                        {offsetMode === "individual" && isPremium && <p className="text-sm text-[--text-muted] mt-3">Individual clip length is on — this is just the starting point you'll see when marking each event, and you can adjust it per clip from there.</p>}
                        {offsetMode === "categorical" && isPremium && <p className="text-sm text-[--text-muted] mt-3">Categorical clip length is on — this is the fallback for any event type you haven't set a duration for below.</p>}
                    </div>

                    <div className="card-base p-6">
                        <div className="flex items-center gap-2 mb-2"><h3 className="heading-secondary text-lg text-[--text-primary]">Advanced Clip Length</h3>{!isPremium && <Lock className="w-4 h-4 text-[--text-muted]" />}</div>
                        <p className="text-sm text-[--text-secondary] mb-4">Choose how before/after duration is set. Each clip remembers its own duration once marked, so changing this later won't affect clips you've already logged.</p>
                        <div className="grid grid-cols-4 gap-2">
                            <button onClick={() => handleOffsetModeChange("classic")} className={`py-2 rounded-lg text-sm font-semibold border-2 transition-all ${offsetMode === "classic" ? "bg-[--accent-home] border-[--accent-home] text-white" : "border-[--border-default] text-[--text-secondary]"}`}>Off</button>
                            <button onClick={() => handleOffsetModeChange("general")} className={`py-2 rounded-lg text-sm font-semibold border-2 transition-all flex items-center justify-center gap-1 ${offsetMode === "general" ? "bg-[--accent-home] border-[--accent-home] text-white" : "border-[--border-default] text-[--text-secondary]"}`}>{!isPremium && <Lock className="w-3 h-3" />}General</button>
                            <button onClick={() => handleOffsetModeChange("individual")} className={`py-2 rounded-lg text-sm font-semibold border-2 transition-all flex items-center justify-center gap-1 ${offsetMode === "individual" ? "bg-[--accent-home] border-[--accent-home] text-white" : "border-[--border-default] text-[--text-secondary]"}`}>{!isPremium && <Lock className="w-3 h-3" />}Individual</button>
                            <button onClick={() => handleOffsetModeChange("categorical")} className={`py-2 rounded-lg text-sm font-semibold border-2 transition-all flex items-center justify-center gap-1 ${offsetMode === "categorical" ? "bg-[--accent-home] border-[--accent-home] text-white" : "border-[--border-default] text-[--text-secondary]"}`} data-testid="offset-mode-categorical">{!isPremium && <Lock className="w-3 h-3" />}Categorical</button>
                        </div>
                        <p className="text-xs text-[--text-muted] mt-3">
                            <strong className="text-[--text-secondary]">Off:</strong> one before/after duration for every clip.{" "}
                            <strong className="text-[--text-secondary]">General:</strong> set your own before and after duration, applied to every clip you mark.{" "}
                            <strong className="text-[--text-secondary]">Individual:</strong> choose before/after duration separately each time you mark an event.{" "}
                            <strong className="text-[--text-secondary]">Categorical:</strong> set a before/after duration per event type, applied automatically based on what you mark.
                        </p>

                        {offsetMode === "categorical" && isPremium && (
                            <div className="mt-5 pt-5 border-t border-[--border-default] space-y-4" data-testid="categorical-offsets-list">
                                {markers.length === 0 ? (
                                    <p className="text-sm text-[--text-muted]">No event types configured yet — add some below first.</p>
                                ) : markers.map((m) => {
                                    const cfg = categoricalOffsets[m] || {};
                                    return (
                                        <div key={m}>
                                            <p className="text-sm font-medium text-[--text-primary] mb-2">{m}</p>
                                            <OffsetDualSlider
                                                before={cfg.before ?? offsetBefore}
                                                after={cfg.after ?? offsetAfter}
                                                setBefore={(v) => setCategoricalOffsetFor(m, "before", v)}
                                                setAfter={(v) => setCategoricalOffsetFor(m, "after", v)}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="card-base p-6">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2"><h3 className="heading-secondary text-lg text-[--text-primary]">Tag Players in Events</h3>{!isPremium && <Lock className="w-4 h-4 text-[--text-muted]" />}</div>
                            <button
                                onClick={handleTogglePlayerTagging}
                                disabled={isPremium && !playerTaggingEnabled && rosterIsEmpty}
                                className={`relative w-14 h-8 rounded-full transition-colors ${playerTaggingEnabled ? "bg-[--accent-home]" : "bg-[--bg-tertiary]"} ${isPremium && !playerTaggingEnabled && rosterIsEmpty ? "opacity-50 cursor-not-allowed" : ""}`}
                                data-testid="player-tagging-toggle"
                            >
                                <span className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow transition-transform ${playerTaggingEnabled ? "translate-x-6" : ""}`} />
                            </button>
                        </div>
                        <p className="text-sm text-[--text-secondary] mb-1">When on, marking an event lets you assign it to players from your roster. Tagged players get their own compilation of the events they were involved in.</p>
                        {isPremium && rosterIsEmpty && <p className="text-sm text-yellow-500 flex items-center gap-2 mt-2"><AlertCircle className="w-4 h-4" />Add players to your roster in the Stats tab before enabling this.</p>}
                    </div>

                    <div className="card-base p-6">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2"><h3 className="heading-secondary text-lg text-[--text-primary]">Add Notes to Clips</h3>{!isPremium && <Lock className="w-4 h-4 text-[--text-muted]" />}</div>
                            <button
                                onClick={handleToggleNotes}
                                className={`relative w-14 h-8 rounded-full transition-colors ${notesEnabled ? "bg-[--accent-home]" : "bg-[--bg-tertiary]"}`}
                                data-testid="notes-toggle"
                            >
                                <span className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow transition-transform ${notesEnabled ? "translate-x-6" : ""}`} />
                            </button>
                        </div>
                        <p className="text-sm text-[--text-secondary] mb-1">When on, marking an event gives you a text box to jot down notes about that clip.</p>
                    </div>

                    <div className="card-base p-6">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2"><h3 className="heading-secondary text-lg text-[--text-primary]">Event Counter</h3>{!isPremium && <Lock className="w-4 h-4 text-[--text-muted]" />}</div>
                            <button
                                onClick={handleToggleEventCounter}
                                className={`relative w-14 h-8 rounded-full transition-colors ${eventCounterEnabled ? "bg-[--accent-home]" : "bg-[--bg-tertiary]"}`}
                                data-testid="event-counter-toggle"
                            >
                                <span className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow transition-transform ${eventCounterEnabled ? "translate-x-6" : ""}`} />
                            </button>
                        </div>
                        <p className="text-sm text-[--text-secondary] mb-1">When on, a card below the timeline on the Marking tab shows a running count of how many times each event has been marked in the game.</p>
                    </div>

                    <div className="card-base p-6">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="heading-secondary text-lg text-[--text-primary]">Pause Video on Mark</h3>
                            <button
                                onClick={handleTogglePauseOnMark}
                                className={`relative w-14 h-8 rounded-full transition-colors ${pauseOnMarkEnabled ? "bg-[--accent-home]" : "bg-[--bg-tertiary]"}`}
                                data-testid="pause-on-mark-toggle"
                            >
                                <span className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow transition-transform ${pauseOnMarkEnabled ? "translate-x-6" : ""}`} />
                            </button>
                        </div>
                        <p className="text-sm text-[--text-secondary] mb-1">When on, the video pauses automatically the moment you tap a marker on the Marking tab — handy for adding player tags or notes without the action running away from you. When off, the video keeps playing even if the details modal pops up. Doesn't apply to Live recording, since there's no video to pause.</p>
                    </div>

                    <div className="card-base p-6">
                        <div className="flex items-center gap-2 mb-4"><h3 className="heading-secondary text-lg text-[--text-primary]">Custom Markers</h3></div>
                        <div className="flex gap-2 mb-4"><input type="text" value={newMarker} onChange={(e) => setNewMarker(e.target.value)} placeholder="New marker name" className="input-base flex-1" onKeyDown={(e) => { if (e.key === "Enter" && newMarker.trim() && !markers.includes(newMarker.trim())) { setMarkers([...markers, newMarker.trim()]); setNewMarker(""); } }} /><button onClick={() => { if (!isPremium) { setShowPremiumModal(true); return; } if (newMarker.trim() && !markers.includes(newMarker.trim())) { setMarkers([...markers, newMarker.trim()]); setNewMarker(""); } }} className="btn-primary"><Plus className="w-5 h-5" /></button></div>
                        <div className="flex flex-wrap gap-2">{markers.map((m) => <div key={m} className="flex items-center gap-2 bg-[--bg-tertiary] px-3 py-2 rounded-lg group"><span>{m}</span><button onClick={() => { if (!isPremium) { setShowPremiumModal(true); return; } setMarkers(markers.filter(x => x !== m)) }} className="text-[--text-muted] hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-4 h-4" /></button></div>)}</div>
                    </div>
                </div>

            )}

            {activeSection === "appearance" && (
                <div className="space-y-6">
                    <div className="card-base p-6">
                        <h3 className="heading-secondary text-lg text-[--text-primary] mb-4 flex items-center gap-2"><Sun className="w-5 h-5" />Theme</h3>
                        <div className="flex gap-3">
                            <button onClick={() => setTheme("dark")} className={`flex-1 p-4 rounded-lg border-2 transition-all ${theme === "dark" ? "border-[--accent-home] bg-[--accent-home]/10" : "border-[--border-default]"}`}>
                                <Moon className="w-6 h-6 mx-auto mb-2" /><p className="font-medium">Dark Mode</p>
                            </button>
                            <button onClick={() => setTheme("light")} className={`flex-1 p-4 rounded-lg border-2 transition-all ${theme === "light" ? "border-[--accent-home] bg-[--accent-home]/10" : "border-[--border-default]"}`}>
                                <Sun className="w-6 h-6 mx-auto mb-2" /><p className="font-medium">Light Mode</p>
                            </button>
                        </div>
                    </div>
                    <div className="card-base p-6">
                        <h3 className="heading-secondary text-lg text-[--text-primary] mb-4 flex items-center gap-2"><Palette className="w-5 h-5" />Sport Theme Colors</h3>
                        <p className="text-[--text-secondary] mb-4">Colors change based on your selected sport:</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {sports.map((s) => (
                                <button key={s} onClick={() => handleSportChange(s)} className={`p-3 rounded-lg border-2 text-left transition-all ${sport === s ? "border-[--accent-home]" : "border-[--border-default] hover:border-[--border-hover]"}`} data-sport={s}>
                                    <p className="font-medium text-[--text-primary]">{s}</p>
                                    <div className="flex gap-2 mt-2">
                                        <div className="w-4 h-4 rounded-full" style={{ background: s === "Ice Hockey" ? "#3498db" : s === "Football" ? "#1e3a5f" : s === "Basketball" ? "#e67e22" : s === "Soccer" ? "#27ae60" : s === "Lacrosse" ? "#2980b9" : s === "Baseball/Softball" ? "#b91c1c" : s === "Water Polo" ? "#0891b2" : s === "Volleyball" ? "#eab308" : s === "Wrestling" ? "#7f1d1d" : "#2980b9" }} />
                                        <div className="w-4 h-4 rounded-full" style={{ background: s === "Ice Hockey" ? "#e74c3c" : s === "Football" ? "#c9a227" : s === "Basketball" ? "#8e44ad" : s === "Soccer" ? "#ecf0f1" : s === "Lacrosse" ? "#d35400" : s === "Baseball/Softball" ? "#f5deb3" : s === "Water Polo" ? "#fb7185" : s === "Volleyball" ? "#7c3aed" : s === "Wrestling" ? "#64748b" : "#d35400" }} />
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {activeSection === "profile" && (
                <div className="space-y-6">
                    <div className="card-base p-6">
                        <h3 className="heading-secondary text-xl text-[--text-primary] mb-6">Profile Information</h3>
                        <div className="space-y-4">
                            <div className="flex items-center gap-4 p-4 bg-[--bg-tertiary] rounded-lg"><User className="w-5 h-5 text-[--text-muted]" /><div><p className="text-sm text-[--text-muted]">First Name</p><p className="text-[--text-primary] font-medium">{userProfile?.first || "Not set"}</p></div></div>
                            <div className="flex items-center gap-4 p-4 bg-[--bg-tertiary] rounded-lg"><User className="w-5 h-5 text-[--text-muted]" /><div><p className="text-sm text-[--text-muted]">Last Name</p><p className="text-[--text-primary] font-medium">{userProfile?.last || "Not set"}</p></div></div>
                            <div className="flex items-center gap-4 p-4 bg-[--bg-tertiary] rounded-lg"><Mail className="w-5 h-5 text-[--text-muted]" /><div><p className="text-sm text-[--text-muted]">Email</p><p className="text-[--text-primary] font-medium">{userProfile?.email || "Not set"}</p></div></div>
                            <div className="flex items-center gap-4 p-4 bg-[--bg-tertiary] rounded-lg"><Play className="w-5 h-5 text-[--text-muted]" /><div><p className="text-sm text-[--text-muted]">Sport</p><p className="text-[--text-primary] font-medium">{sport || "Not set"}</p></div></div>
                        </div>
                    </div>

                    <div className="card-base p-6">
                        <h3 className="heading-secondary text-xl text-[--text-primary] mb-6">Subscription Status</h3>
                        <div className="flex items-center gap-4 p-4 bg-[--bg-tertiary] rounded-lg"><Banknote className="w-5 h-5 text-[--text-muted]" /> <div><p className="text-sm text-[--text-muted]">Current Plan</p><p className="text-[--text-primary] font-medium">{isPremium ? "Premium" : "Free"}</p></div></div>
                        <div className="mt-6 pt-6 border-t border-[--border-default]">
                            {isPremium ? (<button onClick={onManageSubscription} className="w-full bg-[--accent-home] text-white py-3 rounded-lg font-semibold">Manage Subscription</button>) : (<button onClick={onSubscribe} className="w-full bg-[--accent-home] text-white py-3 rounded-lg font-semibold">Get Snipshot+</button>)}
                        </div>
                    </div>

                    {authProviderId === "password" && (
                        <ChangePasswordCard onChangePassword={onChangePassword} />
                    )}

                    <LegalCard />

                    <div className="mt-6 pt-6 border-t border-[--border-default]">
                        <button onClick={onDeleteAccount} className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-semibold">Delete Account</button>
                    </div>
                </div>
            )}
        </div>
    );
};

// Lets a signed-in user re-read the Terms of Service / Privacy Policy anytime.
const LegalCard = () => {
    const [legalModal, setLegalModal] = useState(null); // "tos" | "privacy" | null
    return (
        <div className="card-base p-6">
            <h3 className="heading-secondary text-xl text-[--text-primary] mb-4">Legal</h3>
            <div className="flex gap-3">
                <button onClick={() => setLegalModal("tos")} className="btn-secondary flex-1" data-testid="settings-tos-link">Terms of Service</button>
                <button onClick={() => setLegalModal("privacy")} className="btn-secondary flex-1" data-testid="settings-privacy-link">Privacy Policy</button>
            </div>
            {legalModal && <LegalModal type={legalModal} onClose={() => setLegalModal(null)} />}
        </div>
    );
};

// Change Password card, shown in Settings > Profile for email/password accounts.
// Google-only accounts don't get this (there's no Snipshot-side password to change).
const ChangePasswordCard = ({ onChangePassword }) => {
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [status, setStatus] = useState(""); // "", "saving", "success"
    const [error, setError] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (newPassword.length < 6) { setError("New password must be 6+ characters"); return; }
        if (newPassword !== confirmPassword) { setError("New passwords don't match"); return; }

        setStatus("saving");
        const result = await onChangePassword(currentPassword, newPassword);
        setStatus("");

        if (result?.error) {
            setError(result.error);
        } else {
            setStatus("success");
            setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
            setTimeout(() => setStatus(""), 3000);
        }
    };

    return (
        <div className="card-base p-6 mt-6">
            <h3 className="heading-secondary text-xl text-[--text-primary] mb-6">Change Password</h3>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="change-password-form">
                <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Current password" className="input-base" required data-testid="current-password" />
                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New password (min 6 characters)" className="input-base" required minLength={6} data-testid="new-password" />
                <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm new password" className="input-base" required data-testid="confirm-password" />
                {error && <div className="flex items-center gap-2 text-red-400 text-sm bg-red-400/10 p-3 rounded-lg" data-testid="change-password-error"><AlertCircle className="w-4 h-4" />{error}</div>}
                {status === "success" && <div className="flex items-center gap-2 text-green-400 text-sm bg-green-400/10 p-3 rounded-lg" data-testid="change-password-success"><Check className="w-4 h-4" />Password updated</div>}
                <button type="submit" className="btn-primary w-full" disabled={status === "saving"} data-testid="change-password-submit">{status === "saving" ? "Updating..." : "Update Password"}</button>
            </form>
        </div>
    );
};

const GameSessionsTab = ({ userId, videoFile, draftSessionId, draftVideoPath, homeMarkers, awayMarkers, homeTables, awayTables, isPremium, setShowPremiumModal, onLoadSession, offsetBefore, offsetAfter, offsetMode, categoricalOffsets, playerTaggingEnabled, notesEnabled, eventCounterEnabled, videoSize, currentSession, setCurrentSession, gameNotes, onShareClip }) => {
    const [uploadProgress, setUploadProgress] = useState(null);
    const [status, setStatus] = useState("");
    const [sessions, setSessions] = useState([]);
    const [loadingList, setLoadingList] = useState(true);
    const [sessionName, setSessionName] = useState("");
    const [opponent, setOpponent] = useState("");
    const [includeScore, setIncludeScore] = useState(false);
    const [homeScore, setHomeScore] = useState("");
    const [awayScore, setAwayScore] = useState("");
    const [showSeasonStats, setShowSeasonStats] = useState(false);
    const [statsTeam, setStatsTeam] = useState("home");
    const [sharingId, setSharingId] = useState(null);
    const [shareStatus, setShareStatus] = useState("");
    const [shareResult, setShareResult] = useState(null); // { code, sessionName }
    const [claimCode, setClaimCode] = useState("");
    const [claiming, setClaiming] = useState(false);
    const [claimStatus, setClaimStatus] = useState("");
    const [copied, setCopied] = useState(false);
    const [showSaveChoice, setShowSaveChoice] = useState(false);
    const [savingExisting, setSavingExisting] = useState(false);
    const [showCompBuilder, setShowCompBuilder] = useState(false);
    const [compSelectedIds, setCompSelectedIds] = useState([]);
    const [compTeam, setCompTeam] = useState("home");
    const [compMarkerName, setCompMarkerName] = useState("");
    const [activeCompilation, setActiveCompilation] = useState(null); // { team, markerName, clips: [...] }
    const FREE_SESSION_LIMIT = 5;
    const PREMIUM_SESSION_LIMIT = 50;
    const sessionLimit = isPremium ? PREMIUM_SESSION_LIMIT : FREE_SESSION_LIMIT;
    // Games shared WITH the coach live in someone else's account, so they don't count
    // toward this coach's own saved-game limit.
    const ownedSessionCount = sessions.filter((x) => !x.__shared).length;
    // Over the limit happens after a downgrade (or a limit change). Nothing is deleted:
    // existing games stay viewable/loadable/updatable, only creating NEW ones is blocked.
    const overSessionLimit = ownedSessionCount > sessionLimit;

    const refreshSessions = useCallback(async () => {
        if (!userId) return;
        setLoadingList(true);
        try {
            const { collection, query, orderBy, getDocs, where } = window.firebaseFirestore;
            const personalQ = query(collection(firebaseDb, "users", userId, "gameSessions"), orderBy("updatedAt", "desc"));
            const sharedQ = query(collection(firebaseDb, "sharedSessions"), where("collaborators", "array-contains", userId));
            const [personalSnap, sharedSnap] = await Promise.all([getDocs(personalQ), getDocs(sharedQ)]);
            const personal = personalSnap.docs.map((d) => ({ id: d.id, ...d.data(), __shared: false }));
            const shared = sharedSnap.docs.map((d) => ({ id: d.id, ...d.data(), __shared: true }));
            const merged = [...personal, ...shared].sort((a, b) => {
                const at = a.updatedAt?.toMillis ? a.updatedAt.toMillis() : 0;
                const bt = b.updatedAt?.toMillis ? b.updatedAt.toMillis() : 0;
                return bt - at;
            });
            setSessions(merged);
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingList(false);
        }
    }, [userId]);

    useEffect(() => {
        if (userId) refreshSessions();
    }, [userId, refreshSessions]);

    const handleSaveClick = () => {
        if (!userId || !videoFile) { setStatus("Need a logged-in user and a loaded video"); return; }
        if (currentSession) { setShowSaveChoice(true); return; }
        saveAsNew();
    };

    const saveAsNew = async () => {
        setShowSaveChoice(false);
        if (!userId || !videoFile) { setStatus("Need a logged-in user and a loaded video"); return; }
        if (!sessionName.trim()) { setStatus("Give this session a name"); return; }
        if (includeScore && (homeScore === "" || awayScore === "")) { setStatus("Enter both scores, or turn off score tracking"); return; }

        if (ownedSessionCount >= sessionLimit) {
            if (!isPremium) {
                setShowPremiumModal(true);
            } else {
                setStatus(`You've reached the ${PREMIUM_SESSION_LIMIT}-session limit. Delete an old session to save a new one.`);
            }
            return;
        }

        try {
            const { doc, setDoc, collection, serverTimestamp } = window.firebaseFirestore;
            const { ref, uploadBytesResumable } = window.firebaseStorage;

            // Reuse the id this video was assigned the moment it was picked
            // (see handleVideoChange / draftSessionIdRef) instead of minting a
            // new one — that id is also the Storage folder the video already
            // lives in (or is about to, below), so the session's video path
            // never has to change and the bytes never have to move.
            const sessionId = draftSessionId || doc(collection(firebaseDb, "users", userId, "gameSessions")).id;
            const storageRef = ref(firebaseStorage, `videos/${userId}/${sessionId}/original.mp4`);

            // draftVideoPath is set once autosave has already uploaded this
            // exact video to this exact path — in that case there's nothing
            // left to send. Otherwise (either videoFile is a fresh Blob that
            // hasn't autosaved yet, or — shouldn't happen, but just in case —
            // metadata with nowhere to fetch bytes from) upload it now.
            if (draftVideoPath) {
                setStatus("Saving session data...");
            } else {
                setStatus("Uploading video...");
                setUploadProgress(0);
                await new Promise((resolve, reject) => {
                    const task = uploadBytesResumable(storageRef, videoFile, { contentType: videoFile.type || "video/mp4" });
                    task.on("state_changed",
                        (snap) => setUploadProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
                        reject, resolve);
                });
                setStatus("Saving session data...");
            }
            await setDoc(doc(firebaseDb, "users", userId, "gameSessions", sessionId), {
                name: sessionName || "Unnamed Session",
                opponent: opponent.trim() || null,
                includeScore,
                homeScore: includeScore ? Number(homeScore) : null,
                awayScore: includeScore ? Number(awayScore) : null,
                videoPath: storageRef.fullPath,
                videoName: videoFile.name,
                videoSize: videoFile.size,
                videoType: videoFile.type || "video/mp4",
                homeMarkers,
                awayMarkers,
                homeTables,
                awayTables,
                gameNotes: gameNotes || "",
                settings: {
                    offsetBefore, offsetAfter, offsetMode, categoricalOffsets, playerTaggingEnabled, notesEnabled, eventCounterEnabled, videoSize,
                },
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            }, { merge: true });
            setStatus("Saved!");
            setUploadProgress(null);
            setSessionName("");
            setOpponent("");
            setIncludeScore(false);
            setHomeScore("");
            setAwayScore("");
            setCurrentSession({ id: sessionId, __shared: false, ownerUid: userId });
            refreshSessions();
            // This draft's video is now this session's own permanent video, so
            // there's nothing left to protect in the freeform slot — but
            // rather than just clearing it, repoint it at this session so a
            // refresh while still editing offers to jump straight back in
            // instead of landing on the session list.
            setSessionPointer(userId, { id: sessionId, __shared: false, ownerUid: userId });
        } catch (e) {
            console.error(e);
            setStatus("Error: " + e.message);
        }
    };

    const updateExisting = async () => {
        setShowSaveChoice(false);
        if (!currentSession) { saveAsNew(); return; }
        setSavingExisting(true);
        setStatus("Updating session...");
        try {
            const { doc, updateDoc, serverTimestamp } = window.firebaseFirestore;
            const path = currentSession.__shared
                ? doc(firebaseDb, "sharedSessions", currentSession.id)
                : doc(firebaseDb, "users", userId, "gameSessions", currentSession.id);

            const updates = {
                homeMarkers,
                awayMarkers,
                homeTables,
                awayTables,
                gameNotes: gameNotes || "",
                settings: {
                    offsetBefore, offsetAfter, offsetMode, categoricalOffsets, playerTaggingEnabled, notesEnabled, eventCounterEnabled, videoSize,
                },
                updatedAt: serverTimestamp(),
            };

            // videoFile is a real File/Blob only when the user just picked a new
            // video (via handleVideoChange). When a session is loaded from Saved
            // Games instead, loadSession() sets videoFile to a plain metadata
            // object ({ name, size, type }), since the real bytes already live in
            // Storage. That distinction is what "Update Existing Session" was
            // missing — it only ever wrote markers/stats and never checked
            // whether the video itself had actually changed, so a re-upload was
            // silently dropped with no progress shown for it.
            const isNewVideoFile = videoFile instanceof Blob;
            if (isNewVideoFile) {
                setStatus("Uploading new video...");
                setUploadProgress(0);
                const { ref, uploadBytesResumable } = window.firebaseStorage;
                const ownerUid = currentSession.__shared ? currentSession.ownerUid : userId;
                const storageRef = ref(firebaseStorage, `videos/${ownerUid}/${currentSession.id}/original.mp4`);
                await new Promise((resolve, reject) => {
                    const task = uploadBytesResumable(storageRef, videoFile, { contentType: videoFile.type || "video/mp4" });
                    task.on("state_changed",
                        (snap) => setUploadProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
                        reject, resolve);
                });
                updates.videoPath = storageRef.fullPath;
                updates.videoName = videoFile.name;
                updates.videoSize = videoFile.size;
                updates.videoType = videoFile.type || "video/mp4";
                setStatus("Saving session data...");
            }

            await updateDoc(path, updates);
            setStatus("Updated!");
            refreshSessions();
        } catch (e) {
            console.error(e);
            setStatus("Error updating session: " + e.message);
        } finally {
            setSavingExisting(false);
            setUploadProgress(null);
        }
    };

    const loadSession = async (session) => {
        try {
            setStatus("Loading session...");
            const { getDownloadURL, ref } = window.firebaseStorage;
            const url = await getDownloadURL(ref(firebaseStorage, session.videoPath));
            onLoadSession({
                videoUrl: url,
                videoFile: { name: session.videoName, size: session.videoSize, type: session.videoType },
                homeMarkers: session.homeMarkers || [],
                awayMarkers: session.awayMarkers || [],
                homeTables: session.homeTables,
                awayTables: session.awayTables,
                gameNotes: session.gameNotes || "",
                settings: session.settings || null,
            });
            setCurrentSession({
                id: session.id,
                __shared: !!session.__shared,
                ownerUid: session.__shared ? session.ownerUid : userId,
            });
            setStatus("Loaded!");
            // Opening a real saved game replaces whatever was in the freeform
            // autosave slot — that draft (and its video) is now stale, so
            // discard both rather than let it resurface as a "resume unsaved
            // game?" prompt later. Then point the slot at THIS session
            // instead, so a refresh while it's open offers to resume it too.
            discardAutosaveDraft(userId).then(() =>
                setSessionPointer(userId, { id: session.id, __shared: !!session.__shared, ownerUid: session.__shared ? session.ownerUid : userId })
            );
        } catch (e) {
            console.error(e);
            setStatus("Error loading session");
        }
    };

    const deleteSession = async (session) => {
        const { doc, deleteDoc, updateDoc, arrayRemove } = window.firebaseFirestore;
        const { ref, deleteObject } = window.firebaseStorage;

        if (session.__shared && session.ownerUid !== userId) {
            // Non-owner: leave the shared session rather than deleting it for everyone.
            if (!window.confirm(`Remove yourself from "${session.name}"? You'll lose access, but it stays intact for the other coach(es).`)) return;
            try {
                await updateDoc(doc(firebaseDb, "sharedSessions", session.id), { collaborators: arrayRemove(userId) });
                if (currentSession?.id === session.id) setCurrentSession(null);
                refreshSessions();
            } catch (e) {
                console.error(e);
                setStatus("Error leaving shared session");
            }
            return;
        }

        if (session.__shared && session.ownerUid === userId) {
            // Owner deleting a shared session: this removes it -- and the video -- for every collaborator.
            if (!window.confirm(`Delete "${session.name}"? This will delete the video and remove access for everyone this session is shared with. This can't be undone.`)) return;
            try {
                await deleteObject(ref(firebaseStorage, session.videoPath)).catch(() => { });
                await deleteDoc(doc(firebaseDb, "sharedSessions", session.id));
                if (currentSession?.id === session.id) setCurrentSession(null);
                refreshSessions();
            } catch (e) {
                console.error(e);
                setStatus("Error deleting session");
            }
            return;
        }

        // Personal, unshared session.
        if (!window.confirm(`Delete "${session.name}"? This can't be undone.`)) return;
        try {
            await deleteObject(ref(firebaseStorage, session.videoPath)).catch(() => { });
            await deleteDoc(doc(firebaseDb, "users", userId, "gameSessions", session.id));
            if (currentSession?.id === session.id) setCurrentSession(null);
            refreshSessions();
        } catch (e) {
            console.error(e);
            setStatus("Error deleting session");
        }
    };

    // Permanently removes one tagged event from its ORIGINAL saved game (not
    // just from whatever compilation view is currently showing it) — used by
    // the multi-game compilation viewer, which browses events belonging to
    // many different sessions at once rather than one currently-loaded game.
    // Mirrors updateExisting()'s owner-vs-shared doc targeting above.
    const deleteMarkerFromSession = async (session, team, markerId) => {
        const { doc, updateDoc, serverTimestamp } = window.firebaseFirestore;
        const path = session.__shared
            ? doc(firebaseDb, "sharedSessions", session.id)
            : doc(firebaseDb, "users", userId, "gameSessions", session.id);
        const field = team === "home" ? "homeMarkers" : "awayMarkers";
        const updatedMarkers = (session[field] || []).filter((m) => m.id !== markerId);
        await updateDoc(path, { [field]: updatedMarkers, updatedAt: serverTimestamp() });
        // Keep the in-memory sessions list in sync so re-opening the builder
        // (or building another compilation) reflects the deletion right away.
        setSessions((prev) => prev.map((s) => (s.id === session.id ? { ...s, [field]: updatedMarkers } : s)));
    };

    const toggleCompSession = (id) => setCompSelectedIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

    const compSessions = sessions.filter((s) => compSelectedIds.includes(s.id));
    const compMarkerNames = [...new Set(
        compSessions.flatMap((s) => (compTeam === "home" ? s.homeMarkers : s.awayMarkers) || []).map((m) => m.name)
    )].sort();

    // If the selected games/team no longer offer the previously chosen event
    // type (e.g. the coach unchecked the only game with "Penalty" tagged),
    // clear it rather than silently building an empty compilation.
    useEffect(() => {
        if (compMarkerName && !compMarkerNames.includes(compMarkerName)) setCompMarkerName("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [compTeam, compSelectedIds.join(",")]);

    const buildCompilation = () => {
        if (!compMarkerName || compSessions.length === 0) return;
        const clips = compSessions.flatMap((s) =>
            ((compTeam === "home" ? s.homeMarkers : s.awayMarkers) || [])
                .filter((m) => m.name === compMarkerName)
                .map((m) => ({ ...m, sessionId: s.id, sessionName: s.name, __shared: s.__shared, videoPath: s.videoPath, team: compTeam }))
        );
        if (!clips.length) { setStatus("No matching clips in the selected games."); return; }
        setActiveCompilation({ team: compTeam, markerName: compMarkerName, clips });
        setShowCompBuilder(false);
    };


    // Create a standalone share record: session data + a download URL to the video.
    // Nothing is copied yet -- the recipient copies the video into their own storage when they claim it.
    const shareSession = async (session) => {
        setSharingId(session.id);
        setShareStatus("Preparing share code...");
        try {
            const { doc, setDoc, deleteDoc, collection, serverTimestamp } = window.firebaseFirestore;

            if (session.__shared) {
                setShareResult({ code: session.id, sessionName: session.name });
                setShareStatus("");
                return;
            }

            const shareId = session.id;
            await setDoc(doc(firebaseDb, "sharedSessions", shareId), {
                ownerUid: userId,
                collaborators: [userId],
                videoPath: session.videoPath,
                videoName: session.videoName,
                videoSize: session.videoSize,
                videoType: session.videoType,
                name: session.name,
                opponent: session.opponent || null,
                includeScore: session.includeScore || false,
                homeScore: session.includeScore ? session.homeScore : null,
                awayScore: session.includeScore ? session.awayScore : null,
                homeMarkers: session.homeMarkers || [],
                awayMarkers: session.awayMarkers || [],
                homeTables: session.homeTables,
                awayTables: session.awayTables,
                gameNotes: session.gameNotes || "",
                settings: session.settings || null,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });

            // The personal copy is now superseded by the shared doc -- remove it so the
            // session doesn't show up twice, without touching the underlying video.
            await deleteDoc(doc(firebaseDb, "users", userId, "gameSessions", session.id));

            if (currentSession?.id === session.id && !currentSession.__shared) {
                setCurrentSession({ id: shareId, __shared: true, ownerUid: userId });
            }

            setShareResult({ code: shareId, sessionName: session.name });
            setShareStatus("");
            refreshSessions();
        } catch (e) {
            console.error(e);
            setShareStatus("Error creating share code. Try again.");
        } finally {
            setSharingId(null);
        }
    };

    // Claim a shared session: download the source video and save everything as a brand-new,
    // fully independent session owned by the current user.
    const claimSharedSession = async () => {
        if (!isPremium) { setShowPremiumModal(true); return; }
        const code = claimCode.trim();
        if (!code) { setClaimStatus("Paste a share code first."); return; }

        setClaiming(true);
        setClaimStatus("Looking up shared session...");
        try {
            const { doc, getDoc, updateDoc, arrayUnion } = window.firebaseFirestore;
            const sharedRef = doc(firebaseDb, "sharedSessions", code);
            const snap = await getDoc(sharedRef);
            if (!snap.exists()) {
                setClaimStatus("No shared session found for that code. Double check it and try again.");
                setClaiming(false);
                return;
            }
            const shared = snap.data();
            if ((shared.collaborators || []).includes(userId)) {
                setClaimStatus("You already have access to this session.");
                setClaiming(false);
                refreshSessions();
                return;
            }

            await updateDoc(sharedRef, { collaborators: arrayUnion(userId) });

            setClaimStatus("Added to your Saved Games!");
            setClaimCode("");
            refreshSessions();
        } catch (e) {
            console.error(e);
            setClaimStatus("Error joining shared session: " + e.message);
        } finally {
            setClaiming(false);
        }
    };

    const copyShareCode = () => {
        if (!shareResult) return;
        navigator.clipboard.writeText(shareResult.code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const record = computeTeamRecord(sessions);
    const gamesWithScore = record.wins + record.losses + record.ties;
    const winPct = gamesWithScore > 0 ? Math.round((record.wins / gamesWithScore) * 100) : null;
    const seasonStats = aggregateSeasonStats(sessions, statsTeam);

    return (
        <div className="p-6 max-w-5xl mx-auto space-y-6">
            {/* Save current session */}
            <div className="card-base p-6">
                <h2 className="text-lg font-semibold text-[--text-primary] mb-4 flex items-center gap-2">
                    <Save className="w-5 h-5 text-[--accent-home]" />
                    Save Current Session
                </h2>

                {showSaveChoice && (
                    <div className="modal-overlay">
                        <div className="modal">
                            <div className="modal-header">
                                <h2>Update or Save as New?</h2>
                                <button onClick={() => setShowSaveChoice(false)} className="modal-close"><X size={20} /></button>
                            </div>
                            <p className="text-sm text-[--text-secondary] mb-4">
                                You're editing a {currentSession?.__shared ? "shared" : "previously saved"} session.
                                {currentSession?.__shared && " Updating it will change what every coach on this session sees."}
                            </p>
                            <div className="flex flex-col gap-2">
                                <button onClick={updateExisting} className="btn-primary w-full">Update Existing Session</button>
                                <button onClick={saveAsNew} className="btn-secondary w-full">Save as a New Session</button>
                            </div>
                        </div>
                    </div>
                )}

                {currentSession && (
                    <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-[--bg-tertiary] text-sm text-[--text-secondary]">
                        {currentSession.__shared ? <Share2 className="w-4 h-4 text-[--accent-home]" /> : <Save className="w-4 h-4 text-[--text-muted]" />}
                        Editing a {currentSession.__shared ? "shared" : "saved"} session. Saving will let you update it in place or save a separate copy.
                    </div>
                )}

                <div className="grid sm:grid-cols-2 gap-3">
                    <input
                        type="text"
                        value={sessionName}
                        onChange={(e) => setSessionName(e.target.value)}
                        placeholder="Session name (e.g. vs. Eagles)"
                        className="input-base"
                    />
                    <input
                        type="text"
                        value={opponent}
                        onChange={(e) => setOpponent(e.target.value)}
                        placeholder="Opponent (optional)"
                        className="input-base"
                    />
                </div>

                <label className="flex items-center gap-2 mt-4 cursor-pointer select-none w-fit">
                    <input
                        type="checkbox"
                        checked={includeScore}
                        onChange={(e) => setIncludeScore(e.target.checked)}
                        className="w-4 h-4 accent-[--accent-home]"
                    />
                    <span className="text-sm text-[--text-secondary]">Include final score (adds this game to your season record)</span>
                </label>

                {includeScore && (
                    <div className="grid grid-cols-2 gap-3 mt-3 max-w-sm">
                        <div>
                            <label className="block text-xs text-[--text-muted] mb-1">Your Score</label>
                            <input type="number" min="0" value={homeScore} onChange={(e) => setHomeScore(e.target.value)} className="input-base" />
                        </div>
                        <div>
                            <label className="block text-xs text-[--text-muted] mb-1">Opponent Score</label>
                            <input type="number" min="0" value={awayScore} onChange={(e) => setAwayScore(e.target.value)} className="input-base" />
                        </div>
                    </div>
                )}

                <div className="flex items-center gap-3 mt-4">
                    <button onClick={handleSaveClick} className="btn-primary">Save Session</button>
                    {(uploadProgress !== null || savingExisting) && (
                        <div className="flex items-center gap-2 text-sm text-[--text-secondary]">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {uploadProgress !== null ? `${uploadProgress}%` : "Updating..."}
                        </div>
                    )}
                </div>

                {status && <p className="text-sm text-[--text-secondary] mt-2">{status}</p>}

                <p className="text-xs text-[--text-muted] mt-3">
                    {ownedSessionCount}/{sessionLimit} sessions saved{!isPremium && " (upgrade for more)"}
                </p>
                {overSessionLimit && (
                    <div className="flex items-start gap-2 p-3 rounded-lg border border-yellow-500/40 bg-yellow-500/10 text-sm text-yellow-500 mt-3" data-testid="over-session-limit">
                        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                        <span>
                            {isPremium
                                ? `You have ${ownedSessionCount} saved games, over the ${sessionLimit}-game limit. `
                                : `You're on the free plan, which includes ${sessionLimit} saved games, and you have ${ownedSessionCount}. `}
                            Your games are safe and you can still open and edit them. To save a new game, delete some until you're under the limit{!isPremium && ", or resubscribe for more room"}.
                        </span>
                    </div>
                )}
            </div>

            {/* Team record + season stats */}
            <div className="card-base p-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <h2 className="text-lg font-semibold text-[--text-primary] flex items-center gap-2">
                        <Trophy className="w-5 h-5 text-[--accent-home]" />
                        Season Record
                    </h2>
                    <button onClick={() => setShowSeasonStats((v) => !v)} className="btn-secondary text-sm flex items-center gap-1">
                        {showSeasonStats ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        {showSeasonStats ? "Hide" : "Show"} Season Stats
                    </button>
                </div>

                <div className="flex items-center gap-6 mt-4 flex-wrap">
                    <div className="text-3xl font-bold text-[--text-primary]" data-testid="team-record">
                        {record.wins}-{record.losses}{record.ties > 0 ? `-${record.ties}` : ""}
                    </div>
                    <div className="text-sm text-[--text-secondary]">
                        {gamesWithScore > 0 ? (
                            <>Win % <span className="font-semibold text-[--text-primary]">{winPct}%</span> · {gamesWithScore} game{gamesWithScore !== 1 ? "s" : ""} tracked</>
                        ) : (
                            "Add a score when you save a game to start tracking your record."
                        )}
                    </div>
                </div>

                {showSeasonStats && (
                    <div className="mt-6 pt-6 border-t border-[--border-default]">
                        <div className="flex gap-2 mb-4">
                            <button
                                onClick={() => setStatsTeam("home")}
                                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${statsTeam === "home" ? "bg-[--accent-home] text-white" : "border border-[--border-default] text-[--text-secondary]"}`}
                            >
                                Your Team
                            </button>
                            <button
                                onClick={() => setStatsTeam("away")}
                                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${statsTeam === "away" ? "bg-[--accent-away] text-white" : "border border-[--border-default] text-[--text-secondary]"}`}
                            >
                                Opponent
                            </button>
                        </div>

                        {seasonStats.length === 0 ? (
                            <p className="text-sm text-[--text-muted]">No stats saved yet for this team.</p>
                        ) : (
                            <div className="space-y-6">
                                {seasonStats.map((t) => (
                                    <div key={t.name} className="overflow-x-auto rounded-lg border border-[--border-default]">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="bg-[--bg-tertiary]">
                                                    <th className="px-4 py-2 text-left text-xs font-semibold text-[--text-secondary] uppercase tracking-wider whitespace-nowrap">{t.name}</th>
                                                    {t.columns.slice(2).map((col, i) => (
                                                        <th key={i} className="px-3 py-2 text-center text-xs font-semibold text-[--text-secondary] uppercase tracking-wider">{col}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {t.players.map((p, idx) => (
                                                    <tr key={idx} className="border-t border-[--border-default]">
                                                        <td className="px-4 py-2 text-[--text-primary] font-medium whitespace-nowrap">
                                                            {p.number && <span className="text-[--text-muted] mr-2">#{p.number}</span>}{p.name || "—"}
                                                        </td>
                                                        {p.totals.map((val, i) => (
                                                            <td key={i} className="px-3 py-2 text-center text-[--text-secondary]">
                                                                {t.columns[i + 2].includes("%") ? `${val}%` : val}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ))}
                                <p className="text-xs text-[--text-muted]">Totals are summed across saved games; % columns show a simple average across games played.</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Claim a shared session */}
            <div className="card-base p-6">
                <h2 className="text-lg font-semibold text-[--text-primary] mb-4 flex items-center gap-2">
                    <Link2 className="w-5 h-5 text-[--accent-home]" />
                    Claim a Shared Session
                    {!isPremium && <Lock className="w-4 h-4 text-[--text-muted]" />}
                </h2>
                <p className="text-sm text-[--text-secondary] mb-3">Got a share code from another coach? Paste it below to save your own independent copy.</p>
                <div className="flex gap-3 flex-wrap">
                    <input
                        type="text"
                        value={claimCode}
                        onChange={(e) => setClaimCode(e.target.value)}
                        placeholder="Paste share code"
                        className="input-base flex-1 min-w-[220px]"
                    />
                    <button onClick={claimSharedSession} disabled={claiming} className="btn-primary flex items-center gap-2">
                        {claiming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                        {claiming ? "Claiming..." : "Claim"}
                    </button>
                </div>
                {claimStatus && <p className="text-sm text-[--text-secondary] mt-2">{claimStatus}</p>}
            </div>

            {/* Saved sessions list */}
            <div className="card-base overflow-hidden">
                <div className="p-4 border-b border-[--border-default]">
                    <h2 className="text-lg font-semibold text-[--text-primary]">Saved Games</h2>
                </div>

                {loadingList ? (
                    <div className="p-8 text-center text-[--text-muted] flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading...
                    </div>
                ) : sessions.length === 0 ? (
                    <p className="p-8 text-center text-[--text-muted]">No saved sessions yet.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <div className="min-w-[640px]">
                            <div className="grid grid-cols-[1fr_110px_110px_90px_140px] gap-3 px-4 py-2 text-xs font-semibold text-[--text-muted] uppercase tracking-wider border-b border-[--border-default]">
                                <span>Game</span>
                                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Date</span>
                                <span className="text-center">Score</span>
                                <span className="text-center">Result</span>
                                <span className="text-right">Actions</span>
                            </div>
                            {sessions.map((s) => {
                                const r = getSessionResult(s);
                                return (
                                    <div
                                        key={s.id}
                                        className="grid grid-cols-[1fr_110px_110px_90px_140px] gap-3 px-4 py-3 items-center border-b border-[--border-default] last:border-b-0 hover:bg-[--bg-tertiary] transition-colors"
                                    >
                                        <div className="min-w-0">
                                            <p className="text-[--text-primary] font-medium truncate flex items-center gap-1.5">
                                                {s.name}
                                                {s.__shared && (
                                                    <span className="inline-flex items-center gap-1 text-xs font-normal px-1.5 py-0.5 rounded bg-[--accent-home] text-white shrink-0">
                                                        <Users className="w-3 h-3" />
                                                        {s.ownerUid === userId ? "Shared by you" : "Shared with you"}
                                                    </span>
                                                )}
                                            </p>
                                            <p className="text-xs text-[--text-muted] truncate">
                                                {s.opponent ? `vs ${s.opponent} · ` : ""}{(s.videoSize / 1e9).toFixed(2)} GB
                                            </p>
                                        </div>
                                        <span className="text-sm text-[--text-secondary]">{formatSessionDate(s.createdAt)}</span>
                                        <span className="text-sm text-[--text-secondary] text-center">
                                            {s.includeScore ? `${s.homeScore} - ${s.awayScore}` : "—"}
                                        </span>
                                        <span className="text-center">
                                            {r ? (
                                                <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${sessionResultBadgeClass(r)}`}>{r}</span>
                                            ) : (
                                                <span className="text-[--text-muted] text-xs">—</span>
                                            )}
                                        </span>
                                        <div className="flex items-center justify-end gap-1">
                                            <button onClick={() => loadSession(s)} className="btn-secondary text-sm px-3 py-1.5">Open</button>
                                            <button onClick={() => shareSession(s)} disabled={sharingId === s.id} className="text-[--accent-home] hover:opacity-80 p-2 disabled:opacity-50" title={s.__shared ? "Get invite code" : "Share this session"}>
                                                {sharingId === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                                            </button>
                                            <button onClick={() => deleteSession(s)} className="text-red-400 hover:text-red-300 p-2" title={s.__shared && s.ownerUid !== userId ? "Leave this shared session" : "Delete this session"}>
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Multi-game compilations */}
            <div className="card-base p-6">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <h2 className="text-lg font-semibold text-[--text-primary] flex items-center gap-2">
                        <Columns className="w-5 h-5 text-[--accent-home]" />
                        Multi-Game Compilations
                        {!isPremium && <Lock className="w-4 h-4 text-[--text-muted]" />}
                    </h2>
                    <button
                        onClick={() => { if (!isPremium) { setShowPremiumModal(true); return; } setShowCompBuilder((v) => !v); }}
                        className="btn-secondary text-sm"
                        data-testid="toggle-compilation-builder"
                    >
                        {showCompBuilder ? "Cancel" : "Build a Compilation"}
                    </button>
                </div>
                <p className="text-sm text-[--text-secondary] mt-2">
                    Pick several saved games, a team, and an event type — e.g. every Goal from your team across your last 10 games — and browse them together, the same way you switch between clips in one game.
                </p>

                {showCompBuilder && isPremium && (
                    <div className="mt-5 space-y-5">
                        <div>
                            <p className="text-sm font-medium text-[--text-primary] mb-2">1. Select games ({compSelectedIds.length} selected)</p>
                            <div className="flex gap-3 mb-2">
                                <button onClick={() => setCompSelectedIds(sessions.map((s) => s.id))} className="text-xs text-[--accent-home] hover:underline">Select all</button>
                                <button onClick={() => setCompSelectedIds([])} className="text-xs text-[--text-muted] hover:underline">Clear</button>
                            </div>
                            <div className="max-h-56 overflow-y-auto border border-[--border-default] rounded-lg divide-y divide-[--border-default]" data-testid="compilation-game-list">
                                {sessions.map((s) => (
                                    <label key={s.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[--bg-tertiary]">
                                        <input type="checkbox" checked={compSelectedIds.includes(s.id)} onChange={() => toggleCompSession(s.id)} className="w-4 h-4 accent-[--accent-home]" />
                                        <span className="text-sm text-[--text-primary] truncate flex-1">{s.name}</span>
                                        <span className="text-xs text-[--text-muted]">{formatSessionDate(s.createdAt)}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div>
                            <p className="text-sm font-medium text-[--text-primary] mb-2">2. Team</p>
                            <div className="grid grid-cols-2 gap-3 max-w-sm">
                                <button onClick={() => setCompTeam("home")} className={`py-2 rounded-lg border-2 text-sm font-semibold ${compTeam === "home" ? "bg-[--accent-home] border-[--accent-home] text-white" : "border-[--border-default] text-[--text-secondary]"}`}>Your Team</button>
                                <button onClick={() => setCompTeam("away")} className={`py-2 rounded-lg border-2 text-sm font-semibold ${compTeam === "away" ? "bg-[--accent-away] border-[--accent-away] text-white" : "border-[--border-default] text-[--text-secondary]"}`}>Opponent</button>
                            </div>
                        </div>

                        <div>
                            <p className="text-sm font-medium text-[--text-primary] mb-2">3. Event type</p>
                            {compSelectedIds.length === 0 ? (
                                <p className="text-sm text-[--text-muted]">Select at least one game first.</p>
                            ) : compMarkerNames.length === 0 ? (
                                <p className="text-sm text-[--text-muted]">None of the selected games have tagged events for this team.</p>
                            ) : (
                                <div className="flex flex-wrap gap-2">
                                    {compMarkerNames.map((name) => (
                                        <button
                                            key={name}
                                            onClick={() => setCompMarkerName(name)}
                                            className={`px-3 py-1.5 rounded-lg border-2 text-sm ${compMarkerName === name ? "bg-[--accent-home] border-[--accent-home] text-white" : "border-[--border-default] text-[--text-secondary]"}`}
                                        >
                                            {name}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <button
                            onClick={buildCompilation}
                            disabled={!compMarkerName || compSelectedIds.length === 0}
                            className="btn-primary disabled:opacity-50"
                            data-testid="view-compilation"
                        >
                            View Compilation
                        </button>
                    </div>
                )}

                {activeCompilation && (
                    <div className="mt-5">
                        <MultiGameCompilationView
                            compilation={activeCompilation}
                            onClose={() => setActiveCompilation(null)}
                            onDeleteClip={async (clip) => {
                                const session = sessions.find((s) => s.id === clip.sessionId);
                                if (session) await deleteMarkerFromSession(session, clip.team, clip.id);
                            }}
                            onShareClip={(clip) => onShareClip && onShareClip(clip.team, clip.name, [clip], clip.sessionId)}
                            isPremium={isPremium}
                            setShowPremiumModal={setShowPremiumModal}
                        />
                    </div>
                )}
            </div>

            {shareStatus && <p className="text-sm text-[--text-secondary]">{shareStatus}</p>}

            {shareResult && (
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="modal-header">
                            <h2>Session Ready to Share</h2>
                            <button onClick={() => { setShareResult(null); setCopied(false); }} className="modal-close"><X size={20} /></button>
                        </div>
                        <p className="text-sm text-[--text-secondary] mb-3">
                            Send this code to another coach. They can paste it into "Claim a Shared Session" to get their own
                            independent copy of <strong className="text-[--text-primary]">{shareResult.sessionName}</strong> —
                            video and all. Your original session is untouched either way.
                        </p>
                        <div className="flex items-center gap-2 bg-[--bg-tertiary] rounded-lg px-3 py-2">
                            <code className="flex-1 text-sm text-[--text-primary] break-all">{shareResult.code}</code>
                            <button onClick={copyShareCode} className="btn-secondary text-sm flex items-center gap-1 shrink-0">
                                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                {copied ? "Copied" : "Copy"}
                            </button>
                        </div>
                        <div className="modal-buttons mt-4">
                            <button onClick={() => { setShareResult(null); setCopied(false); }} className="btn-primary w-full">Done</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Coach's Team tab: create/manage team, link roster rows to players, share clips.
// Clip sharing works the same way coach-to-coach session sharing does: it grants
// access to the underlying video + clip metadata via Firestore, rather than
// cutting and re-uploading a new video file per share. Because of that, it
// operates on a SAVED game (which has a stable videoPath in Storage) rather
// than whatever's currently loaded in the Marking tab.
const TeamTab = ({ userId, userProfile, isPremium, setShowPremiumModal, homeTables, awayTables, pendingClipShare, onPendingClipShareHandled }) => {
    const [team, setTeam] = useState(null);
    const [loading, setLoading] = useState(true);
    const [teamName, setTeamName] = useState("");
    const [sessions, setSessions] = useState([]);
    const [sessionsLoading, setSessionsLoading] = useState(true);
    const [selectedSessionId, setSelectedSessionId] = useState("");
    const [selectedClips, setSelectedClips] = useState([]);
    const [selectedRecipients, setSelectedRecipients] = useState([]);
    const [note, setNote] = useState("");
    const [sending, setSending] = useState(false);
    const [sendStatus, setSendStatus] = useState("");

    useEffect(() => {
        const load = async () => {
            if (!window.firebaseFirestore) return;
            const { collection, query, where, getDocs } = window.firebaseFirestore;
            const q = query(collection(firebaseDb, "teams"), where("coachUid", "==", userId));
            const snap = await getDocs(q);
            if (!snap.empty) setTeam({ id: snap.docs[0].id, ...snap.docs[0].data() });
            setLoading(false);
        };
        if (userId) load();
    }, [userId]);

    // Load the coach's saved games (personal + shared-with-them) so clips can be
    // picked from an already-saved video, the same source of truth a share code
    // points to for another coach.
    useEffect(() => {
        const loadSessions = async () => {
            if (!window.firebaseFirestore || !userId) return;
            setSessionsLoading(true);
            try {
                const { collection, query, where, orderBy, getDocs } = window.firebaseFirestore;
                const personalQ = query(collection(firebaseDb, "users", userId, "gameSessions"), orderBy("updatedAt", "desc"));
                const sharedQ = query(collection(firebaseDb, "sharedSessions"), where("collaborators", "array-contains", userId));
                const [personalSnap, sharedSnap] = await Promise.all([getDocs(personalQ), getDocs(sharedQ)]);
                const personal = personalSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
                const shared = sharedSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
                setSessions([...personal, ...shared]);
            } catch (e) {
                console.error(e);
            } finally {
                setSessionsLoading(false);
            }
        };
        loadSessions();
    }, [userId]);

    // A "share this clip" click on the Videos tab lands here: once this coach's saved
    // sessions have loaded, find the matching session and pre-select it plus the clips
    // that were clicked, so the coach only has to pick recipients and hit Share.
    useEffect(() => {
        if (!pendingClipShare || sessionsLoading) return;
        const match = sessions.find((s) => s.id === pendingClipShare.sessionId);
        if (match) {
            setSelectedSessionId(match.id);
            const clips = [
                ...(match.homeMarkers || []).map((m) => ({ ...m, team: "home" })),
                ...(match.awayMarkers || []).map((m) => ({ ...m, team: "away" })),
            ];
            const found = clips.filter((c) => pendingClipShare.clips.some((pc) => pc.team === c.team && pc.name === c.name && pc.time === c.time));
            if (found.length) setSelectedClips(found);
        }
        onPendingClipShareHandled();
    }, [pendingClipShare, sessionsLoading, sessions]);

    const handleCreateTeam = async () => {
        if (!teamName.trim()) return;
        const { id, code } = await createTeam(userId, teamName.trim(), userProfile?.sport);
        setTeam({ id, name: teamName.trim(), code, coachUid: userId, players: {} });
    };

    // Map a joined player to a stat-sheet roster row so their stats page knows which row is theirs
    const assignRosterKey = async (playerUid, rosterKey) => {
        const { doc, updateDoc } = window.firebaseFirestore;
        await updateDoc(doc(firebaseDb, "teams", team.id), { [`players.${playerUid}.rosterKey`]: rosterKey });
        setTeam((t) => ({ ...t, players: { ...t.players, [playerUid]: { ...t.players[playerUid], rosterKey } } }));
    };

    const selectedSession = sessions.find((s) => s.id === selectedSessionId) || null;

    const allClips = selectedSession ? [
        ...(selectedSession.homeMarkers || []).map((m) => ({ ...m, team: "home" })),
        ...(selectedSession.awayMarkers || []).map((m) => ({ ...m, team: "away" })),
    ] : [];

    // Clips don't have a stable id, so identify them by a composite key instead
    // of object reference (allClips is rebuilt fresh on every render).
    const clipKey = (c) => `${c.team}|${c.name}|${c.time}`;

    const toggleClip = (marker) => setSelectedClips((prev) =>
        prev.some((c) => clipKey(c) === clipKey(marker)) ? prev.filter((c) => clipKey(c) !== clipKey(marker)) : [...prev, marker]);
    const toggleRecipient = (uid) => setSelectedRecipients((prev) =>
        prev.includes(uid) ? prev.filter((u) => u !== uid) : [...prev, uid]);

    const handleSessionChange = (id) => {
        setSelectedSessionId(id);
        setSelectedClips([]);
    };

    // Non-premium coaches get a monthly cap on clip shares; premium coaches are unlimited
    const checkAndBumpFreeLimit = async () => {
        if (isPremium) return true;
        const { doc, getDoc, setDoc } = window.firebaseFirestore;
        const userSnap = await getDoc(doc(firebaseDb, "users", userId));
        const data = userSnap.data() || {};
        const monthKey = new Date().toISOString().slice(0, 7); // e.g. "2026-08"
        const count = data.clipsSentMonthKey === monthKey ? (data.clipsSentThisMonth || 0) : 0;
        if (count >= FREE_CLIP_LIMIT_PER_MONTH) {
            setShowPremiumModal(true);
            return false;
        }
        await setDoc(doc(firebaseDb, "users", userId), { clipsSentThisMonth: count + 1, clipsSentMonthKey: monthKey }, { merge: true });
        return true;
    };

    // Grants players access to the clip timestamps + the game's existing video
    // (same videoPath the game was saved under) instead of processing and
    // re-uploading a new trimmed file per share. The player's dashboard seeks
    // into the original video for each clip, the same way ClipPlayer already
    // does elsewhere in the app.
    const handleSendClips = async () => {
        if (!selectedSession || !selectedClips.length || !selectedRecipients.length) return;
        const canSend = await checkAndBumpFreeLimit();
        if (!canSend) return;

        setSending(true);
        setSendStatus("Sharing...");
        try {
            const { doc, setDoc, collection, serverTimestamp, arrayUnion } = window.firebaseFirestore;
            await setDoc(doc(collection(firebaseDb, "teamClips")), {
                teamId: team.id,
                coachUid: userId,
                recipientUids: selectedRecipients,
                note,
                sessionName: selectedSession.name || "Game",
                videoPath: selectedSession.videoPath,
                clips: selectedClips.map((c) => ({
                    time: c.time,
                    name: c.name,
                    team: c.team,
                    offsetBefore: c.offsetBefore ?? 5,
                    offsetAfter: c.offsetAfter ?? 5,
                    note: c.note || null,
                })),
                createdAt: serverTimestamp(),
            });

            // Grant these recipients Storage read access to the underlying video.
            // Storage security rules can't run an array-contains query against teamClips,
            // so we keep a small parallel doc, keyed by the same id the video path embeds,
            // that the Storage rules can look up directly.
            await setDoc(doc(firebaseDb, "clipGrants", selectedSession.id), {
                recipientUids: arrayUnion(...selectedRecipients),
            }, { merge: true });

            setSendStatus("Shared!");
            setSelectedClips([]); setSelectedRecipients([]); setNote("");
        } catch (err) {
            console.error("Share clips error:", err);
            setSendStatus("Error sharing clips. Try again.");
        }
        setSending(false);
    };

    if (loading) return <div className="p-6 text-[--text-secondary]">Loading...</div>;

    if (!team) return (
        <div className="p-6 max-w-md mx-auto">
            <div className="card-base p-6">
                <h3 className="heading-secondary text-xl text-[--text-primary] mb-4">Create Your Team</h3>
                <input value={teamName} onChange={(e) => setTeamName(e.target.value)} placeholder="Team name" className="input-base mb-4" />
                <button onClick={handleCreateTeam} className="btn-primary w-full">Create Team</button>
            </div>
        </div>
    );

    const players = Object.entries(team.players || {});
    const rosterOptions = getRosterPlayers([...homeTables, ...awayTables]);

    return (
        <div className="p-6 animate-in space-y-6" data-testid="team-tab">
            <div className="card-base p-6">
                <h3 className="heading-secondary text-xl text-[--text-primary] mb-2">{team.name}</h3>
                <p className="text-sm text-[--text-secondary] mb-2">Share this code with your players:</p>
                <div className="flex items-center gap-3">
                    <code className="text-2xl font-mono tracking-widest text-[--accent-home]">{team.code}</code>
                    <button onClick={() => navigator.clipboard.writeText(team.code)} className="btn-secondary text-sm"><Copy className="w-4 h-4" /></button>
                </div>
                {!isPremium && <p className="text-xs text-[--text-muted] mt-3">Free plan: {FREE_CLIP_LIMIT_PER_MONTH} clip shares/month.</p>}
            </div>

            <div className="card-base p-6">
                <h3 className="heading-secondary text-lg text-[--text-primary] mb-4">Roster ({players.length})</h3>
                {players.length === 0 ? <p className="text-[--text-muted]">No players have joined yet.</p> : (
                    <div className="space-y-3">
                        {players.map(([uid, p]) => (
                            <div key={uid} className="flex items-center justify-between p-3 bg-[--bg-tertiary] rounded-lg">
                                <span className="text-[--text-primary]">{p.displayName}</span>
                                <select value={p.rosterKey || ""} onChange={(e) => assignRosterKey(uid, e.target.value || null)} className="input-base text-sm w-auto">
                                    <option value="">Link to stat row...</option>
                                    {rosterOptions.map((r) => <option key={`${r.number}|${r.name}`} value={`${r.number}|${r.name}`}>#{r.number} {r.name}</option>)}
                                </select>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="card-base p-6">
                <h3 className="heading-secondary text-lg text-[--text-primary] mb-4">Share Clips</h3>

                <p className="text-sm text-[--text-secondary] mb-2">From game:</p>
                {sessionsLoading ? (
                    <p className="text-sm text-[--text-muted] mb-4">Loading your saved games...</p>
                ) : sessions.length === 0 ? (
                    <p className="text-sm text-[--text-muted] mb-4">Save a game in the Games tab first, then come back here to share clips from it.</p>
                ) : (
                    <select value={selectedSessionId} onChange={(e) => handleSessionChange(e.target.value)} className="input-base mb-4">
                        <option value="">Choose a saved game...</option>
                        {sessions.map((s) => <option key={s.id} value={s.id}>{s.name || "Game"}{s.opponent ? ` vs ${s.opponent}` : ""}</option>)}
                    </select>
                )}

                {selectedSession && (
                    <>
                        <div className="grid gap-2 mb-4 max-h-80 overflow-y-auto">
                            {allClips.length === 0 ? (
                                <p className="text-sm text-[--text-muted]">No clips logged in this game.</p>
                            ) : allClips.map((c, i) => {
                                const checked = selectedClips.some((sc) => clipKey(sc) === clipKey(c));
                                const duration = (c.offsetBefore ?? 5) + (c.offsetAfter ?? 5);
                                return (
                                    <label key={i} className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer border-2 transition-colors ${checked ? "border-[--accent-home] bg-[--accent-home]/10" : "border-transparent bg-[--bg-tertiary]"}`}>
                                        <input type="checkbox" className="mt-1" checked={checked} onChange={() => toggleClip(c)} />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.team === "home" ? "bg-[--accent-home]/20 text-[--accent-home]" : "bg-[--accent-away]/20 text-[--accent-away]"}`}>
                                                    {c.team === "home" ? "Your Team" : "Opponent"}
                                                </span>
                                                <span className="text-sm font-semibold text-[--text-primary]">{c.name}</span>
                                                {c.players?.length > 0 && (
                                                    <span className="text-xs text-[--text-muted]">
                                                        {c.players.map((p) => `#${p.number || "-"} ${p.name || "Unnamed"}`).join(", ")}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-[--text-muted] mt-1">
                                                At {formatClipTime(c.time)} &middot; {duration}s clip ({c.offsetBefore ?? 5}s before / {c.offsetAfter ?? 5}s after)
                                            </p>
                                            {c.note && <p className="text-xs text-[--text-secondary] mt-1 italic truncate">"{c.note}"</p>}
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                        <p className="text-sm text-[--text-secondary] mb-2">Share with:</p>
                        <div className="flex flex-wrap gap-2 mb-4">
                            {players.map(([uid, p]) => (
                                <button key={uid} onClick={() => toggleRecipient(uid)} className={`px-3 py-1.5 rounded-full text-sm border-2 ${selectedRecipients.includes(uid) ? "bg-[--accent-home] border-[--accent-home] text-white" : "border-[--border-default] text-[--text-secondary]"}`}>{p.displayName}</button>
                            ))}
                        </div>
                        <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note for the player(s)..." className="input-base mb-4" rows={3} />
                        <button onClick={handleSendClips} disabled={sending || !selectedClips.length || !selectedRecipients.length} className="btn-primary w-full flex items-center justify-center gap-2">
                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                            {sending ? sendStatus : "Share Clips"}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

// Player's locked-down dashboard: season stats, per-game performances, team record, clips from coach
const PlayerDashboard = ({ userId, userProfile, onProfileChange }) => {
    const [teams, setTeams] = useState([]);
    const [sessions, setSessions] = useState({});
    const [clips, setClips] = useState([]);
    const [clipVideoUrls, setClipVideoUrls] = useState({}); // videoPath -> resolved download URL
    const [activeClipIndex, setActiveClipIndex] = useState({}); // clip-share doc id -> which clip within it is showing
    const [archivedIds, setArchivedIds] = useState(new Set()); // clip-share doc ids this player has archived
    const [showArchive, setShowArchive] = useState(false);
    const [loading, setLoading] = useState(true);
    const [expandedGameKey, setExpandedGameKey] = useState(null);

    useEffect(() => {
        const load = async () => {
            if (!window.firebaseFirestore) return;
            const { doc, getDoc, collection, query, where, orderBy, getDocs } = window.firebaseFirestore;

            // Drop references to teams whose coach deleted them (see pruneDeletedTeams)
            try {
                const pruned = await pruneDeletedTeams(userId, userProfile);
                if (pruned && onProfileChange) onProfileChange(pruned);
            } catch (e) { console.warn("Could not prune deleted teams:", e?.code || e); }

            const teamDocs = await Promise.all(
                (userProfile?.teamIds || []).map((id) => getDoc(doc(firebaseDb, "teams", id)))
            );
            const teamList = teamDocs.filter((d) => d.exists()).map((d) => ({
                id: d.id, name: d.data().name, coachUid: d.data().coachUid,
                rosterKey: d.data().players?.[userId]?.rosterKey || null,
            }));
            setTeams(teamList);

            const sessionEntries = await Promise.all(teamList.map(async (t) => {
                const q = query(collection(firebaseDb, "users", t.coachUid, "gameSessions"), orderBy("createdAt", "desc"));
                const snap = await getDocs(q);
                return [t.id, snap.docs.map((d) => ({ id: d.id, ...d.data() }))];
            }));
            setSessions(Object.fromEntries(sessionEntries));

            const clipQ = query(collection(firebaseDb, "teamClips"), where("recipientUids", "array-contains", userId), orderBy("createdAt", "desc"));
            const clipSnap = await getDocs(clipQ);
            const clipDocs = clipSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setClips(clipDocs);

            // Which of these clips this player has tucked into their archive — kept per-player
            // (not on the teamClips doc itself) since a clip can be shared with several players
            // who each want to archive it independently.
            const archiveDoc = await getDoc(doc(firebaseDb, "playerClipArchive", userId));
            setArchivedIds(new Set(archiveDoc.exists() ? (archiveDoc.data().ids || []) : []));

            // Clip shares point at the game's existing video (videoPath) instead of a
            // separately-processed file, so resolve a playable URL per unique path —
            // the same lookup used when a coach opens a saved/shared session.
            const { ref, getDownloadURL } = window.firebaseStorage;
            const uniquePaths = [...new Set(clipDocs.map((c) => c.videoPath).filter(Boolean))];
            const urlEntries = await Promise.all(uniquePaths.map(async (path) => {
                try {
                    return [path, await getDownloadURL(ref(firebaseStorage, path))];
                } catch (e) {
                    console.error("Could not resolve clip video:", e);
                    return [path, null];
                }
            }));
            setClipVideoUrls(Object.fromEntries(urlEntries));

            setLoading(false);
        };
        if (userId) load();
    }, [userId, userProfile]);

    // Move a clip in/out of the archive — updates the UI right away and persists in the
    // background so a slow connection doesn't make the button feel unresponsive.
    const setClipArchived = async (clipId, archived) => {
        setArchivedIds((prev) => {
            const next = new Set(prev);
            if (archived) next.add(clipId); else next.delete(clipId);
            return next;
        });
        if (!userId || !window.firebaseFirestore) return;
        try {
            const { doc, setDoc, arrayUnion, arrayRemove } = window.firebaseFirestore;
            await setDoc(doc(firebaseDb, "playerClipArchive", userId), {
                ids: archived ? arrayUnion(clipId) : arrayRemove(clipId),
            }, { merge: true });
        } catch (e) {
            console.error("Could not update clip archive:", e);
        }
    };

    if (loading) return <div className="p-6 text-[--text-secondary]">Loading your dashboard...</div>;

    const activeClips = clips.filter((c) => !archivedIds.has(c.id));
    const archivedClips = clips.filter((c) => archivedIds.has(c.id));
    // Group archived clips by game so they're easy to find once there are a lot of them
    const archivedByGame = archivedClips.reduce((acc, c) => {
        const key = c.sessionName || "Game";
        if (!acc[key]) acc[key] = [];
        acc[key].push(c);
        return acc;
    }, {});

    const ClipShareCard = ({ c }) => {
        const isArchived = archivedIds.has(c.id);
        const videoUrl = clipVideoUrls[c.videoPath];
        const shareClips = c.clips || [];
        const idx = Math.min(activeClipIndex[c.id] || 0, Math.max(0, shareClips.length - 1));
        const clip = shareClips[idx];
        return (
            <div className="bg-[--bg-tertiary] rounded-lg overflow-hidden">
                <div className="p-3 pb-0 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <p className="text-[--text-primary] font-medium truncate">{c.sessionName || "Game"}</p>
                        {c.note && <p className="text-sm text-[--text-secondary] mt-1">{c.note}</p>}
                    </div>
                    <button
                        onClick={() => setClipArchived(c.id, !isArchived)}
                        className="btn-secondary text-xs px-2 py-1 flex items-center gap-1 shrink-0"
                        title={isArchived ? "Move back to feed" : "Move to archive"}
                        data-testid={`${isArchived ? "unarchive" : "archive"}-clip-${c.id}`}
                    >
                        {isArchived ? <ArchiveRestore className="w-3 h-3" /> : <Archive className="w-3 h-3" />}
                        {isArchived ? "Restore" : "Archive"}
                    </button>
                </div>
                {!shareClips.length ? (
                    <p className="p-3 text-xs text-[--text-muted]">No clips in this share.</p>
                ) : videoUrl === undefined ? (
                    <p className="p-3 text-xs text-[--text-muted]">Loading video...</p>
                ) : videoUrl === null ? (
                    <p className="p-3 text-xs text-[--text-muted]">Couldn't load this video. Ask your coach to re-share it.</p>
                ) : (
                    <>
                        <div className="p-3 pt-2">
                            <ClipPlayer
                                videoUrl={videoUrl}
                                startTime={clip.time}
                                offsetBefore={clip.offsetBefore ?? 5}
                                offsetAfter={clip.offsetAfter ?? 5}
                                clipNumber={idx + 1}
                                totalClips={shareClips.length}
                            />
                        </div>
                        <div className="px-3 pb-3 flex items-center justify-between">
                            <p className="text-xs text-[--text-muted]">
                                {clip.name}{clip.team ? ` — ${clip.team === "home" ? "Your Team" : "Opponent"}` : ""}
                            </p>
                            {shareClips.length > 1 && (
                                <div className="flex items-center gap-2">
                                    <button onClick={() => setActiveClipIndex((m) => ({ ...m, [c.id]: Math.max(0, idx - 1) }))} disabled={idx === 0} className="btn-secondary text-xs px-2 py-1 disabled:opacity-50"><SkipBack className="w-3 h-3" /></button>
                                    <button onClick={() => setActiveClipIndex((m) => ({ ...m, [c.id]: Math.min(shareClips.length - 1, idx + 1) }))} disabled={idx === shareClips.length - 1} className="btn-secondary text-xs px-2 py-1 disabled:opacity-50"><SkipForward className="w-3 h-3" /></button>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        );
    };

    return (
        <div className="p-6 max-w-3xl mx-auto space-y-6 animate-in" data-testid="player-dashboard">
            <h1 className="heading-primary text-2xl text-[--text-primary]">Hey {userProfile?.first}</h1>

            {teams.map((t) => {
                const teamSessions = sessions[t.id] || [];
                const record = computeTeamRecord(teamSessions);
                const season = aggregateSeasonStats(teamSessions, "home"); // adjust "home"/"away" to match how this coach records their own team
                const myRow = (table) => table.players.find((p) => `${p.number}|${p.name}` === t.rosterKey);

                return (
                    <div key={t.id} className="card-base p-6">
                        <h2 className="heading-secondary text-xl text-[--text-primary] mb-2">{t.name}</h2>
                        <p className="text-[--text-secondary] mb-4">Team record: {record.wins}-{record.losses}-{record.ties}</p>

                        {!t.rosterKey ? (
                            <p className="text-[--text-muted] text-sm">Your coach hasn't linked your stats yet.</p>
                        ) : (
                            <>
                                <h3 className="font-semibold text-[--text-primary] mt-4 mb-2">Season Totals</h3>
                                {season.map((table) => {
                                    const row = myRow(table);
                                    if (!row) return null;
                                    return (
                                        <div key={table.name} className="mb-3">
                                            <p className="text-sm text-[--text-muted] mb-1">{table.name}</p>
                                            <div className="flex flex-wrap gap-3">
                                                {table.columns.slice(2).map((col, i) => (
                                                    <div key={col} className="bg-[--bg-tertiary] px-3 py-2 rounded-lg text-center">
                                                        <p className="text-xs text-[--text-muted]">{col}</p>
                                                        <p className="text-[--text-primary] font-semibold">{row.totals[i]}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}

                                <h3 className="font-semibold text-[--text-primary] mt-4 mb-2">Per-Game</h3>
                                <div className="space-y-2">
                                    {teamSessions.map((s) => {
                                        const r = getSessionResult(s);
                                        const gameKey = `${t.id}|${s.id}`;
                                        const isOpen = expandedGameKey === gameKey;
                                        // Same side ("home") the coach's team is aggregated under above —
                                        // this is that specific game's tables, not the season totals.
                                        const gameTables = s.homeTables || [];
                                        const myGameRow = (table) => table.rows.find(
                                            (row) => `${(row.values[0] || "").trim()}|${(row.values[1] || "").trim()}` === t.rosterKey
                                        );

                                        return (
                                            <div key={s.id} className="bg-[--bg-tertiary] rounded-lg overflow-hidden">
                                                <button
                                                    onClick={() => setExpandedGameKey(isOpen ? null : gameKey)}
                                                    className="w-full flex items-center justify-between gap-3 p-3 text-sm text-left"
                                                >
                                                    <div className="min-w-0">
                                                        <p className="text-[--text-primary] font-medium truncate">
                                                            {s.name || "Game"}
                                                            {s.opponent && <span className="text-[--text-muted] font-normal"> vs {s.opponent}</span>}
                                                        </p>
                                                        <p className="text-xs text-[--text-muted]">{formatSessionDate(s.createdAt)}</p>
                                                    </div>
                                                    <div className="flex items-center gap-2 shrink-0">
                                                        <span className="text-[--text-secondary]">{s.includeScore ? `${s.homeScore} - ${s.awayScore}` : "—"}</span>
                                                        {r ? (
                                                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${sessionResultBadgeClass(r)}`}>{r}</span>
                                                        ) : (
                                                            <span className="text-[--text-muted] text-xs">—</span>
                                                        )}
                                                        {isOpen ? <ChevronUp className="w-4 h-4 text-[--text-muted]" /> : <ChevronDown className="w-4 h-4 text-[--text-muted]" />}
                                                    </div>
                                                </button>
                                                {isOpen && (
                                                    <div className="px-3 pb-3 space-y-3 border-t border-[--border-default] pt-3">
                                                        {gameTables.length === 0 ? (
                                                            <p className="text-xs text-[--text-muted]">No stats recorded for this game.</p>
                                                        ) : gameTables.map((table) => {
                                                            const row = myGameRow(table);
                                                            if (!row) return null;
                                                            return (
                                                                <div key={table.id}>
                                                                    <p className="text-xs text-[--text-muted] mb-1">{table.name}</p>
                                                                    <div className="flex flex-wrap gap-2">
                                                                        {table.columns.slice(2).map((col, i) => (
                                                                            <div key={col} className="bg-[--bg-card] px-2 py-1 rounded text-center">
                                                                                <p className="text-[10px] text-[--text-muted]">{col}</p>
                                                                                <p className="text-[--text-primary] text-sm font-semibold">{row.values[i + 2]}</p>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                );
            })}

            <div className="card-base p-6">
                <h2 className="heading-secondary text-xl text-[--text-primary] mb-4">Clips from Your Coach</h2>
                {clips.length === 0 ? (
                    <p className="text-[--text-muted]">Nothing yet.</p>
                ) : activeClips.length === 0 ? (
                    <p className="text-[--text-muted]">Nothing new right now — check your archive below.</p>
                ) : (
                    <div className="space-y-4">
                        {activeClips.map((c) => <ClipShareCard key={c.id} c={c} />)}
                    </div>
                )}
            </div>

            {clips.length > 0 && (
                <div className="card-base p-6">
                    <button
                        onClick={() => setShowArchive((v) => !v)}
                        className="w-full flex items-center justify-between gap-2"
                        data-testid="archive-toggle"
                    >
                        <h2 className="heading-secondary text-xl text-[--text-primary] flex items-center gap-2">
                            <Archive className="w-5 h-5" />Archive{archivedClips.length > 0 ? ` (${archivedClips.length})` : ""}
                        </h2>
                        {showArchive ? <ChevronUp className="w-5 h-5 text-[--text-muted]" /> : <ChevronDown className="w-5 h-5 text-[--text-muted]" />}
                    </button>

                    {showArchive && (
                        archivedClips.length === 0 ? (
                            <p className="text-[--text-muted] mt-4">No archived clips yet. Use "Archive" on a clip above to tuck it away here — it'll still be here whenever you want to watch it again.</p>
                        ) : (
                            <div className="mt-4 space-y-6">
                                {Object.entries(archivedByGame).map(([gameName, gameClips]) => (
                                    <div key={gameName}>
                                        <p className="text-sm font-semibold text-[--text-secondary] mb-3 flex items-center gap-2">
                                            <Calendar className="w-4 h-4 text-[--text-muted]" />{gameName}
                                        </p>
                                        <div className="space-y-4">
                                            {gameClips.map((c) => <ClipShareCard key={c.id} c={c} />)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    )}
                </div>
            )}
        </div>
    );
};

// Small two-tab nav for player accounts (Dashboard / Profile) — deliberately
// lighter-weight than TabNav since players don't get the coach's marking/stats tools.
const PlayerTabNav = ({ activeTab, onTabChange }) => {
    const tabs = [{ id: "dashboard", label: "Dashboard", icon: BarChart3 }, { id: "profile", label: "Profile", icon: User }, { id: "feedback", label: "Feedback", icon: MessageSquare }];
    return (
        <nav className="flex gap-2 px-4 py-3 border-b border-[--border-default] overflow-x-auto" data-testid="player-tab-nav">
            {tabs.map((tab) => <button key={tab.id} onClick={() => onTabChange(tab.id)} className={`tab-base flex items-center gap-2 whitespace-nowrap ${activeTab === tab.id ? "tab-active" : ""}`} data-testid={`player-tab-${tab.id}`}><tab.icon className="w-5 h-5" />{tab.label}</button>)}
        </nav>
    );
};

// Player's account tab: their info, the teams they've joined (with room to add more,
// up to MAX_PLAYER_TEAMS), and account deletion.
const PlayerProfileTab = ({ userId, userProfile, onTeamJoined, onDeleteAccount }) => {
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const teamIds = userProfile?.teamIds || [];

    useEffect(() => {
        const load = async () => {
            if (!window.firebaseFirestore) return;
            const { doc, getDoc } = window.firebaseFirestore;
            const teamDocs = await Promise.all(teamIds.map((id) => getDoc(doc(firebaseDb, "teams", id))));
            setTeams(teamDocs.filter((d) => d.exists()).map((d) => ({ id: d.id, name: d.data().name })));
            setLoading(false);
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, JSON.stringify(teamIds)]);

    return (
        <div className="p-6 max-w-2xl mx-auto space-y-6 animate-in" data-testid="player-profile">
            <h1 className="heading-primary text-2xl text-[--text-primary]">Your Profile</h1>

            <div className="card-base p-6 space-y-3">
                <h2 className="heading-secondary text-lg text-[--text-primary] mb-2">Account Info</h2>
                <div className="flex items-center gap-4 p-4 bg-[--bg-tertiary] rounded-lg"><User className="w-5 h-5 text-[--text-muted]" /><div><p className="text-sm text-[--text-muted]">First Name</p><p className="text-[--text-primary] font-medium">{userProfile?.first || "Not set"}</p></div></div>
                <div className="flex items-center gap-4 p-4 bg-[--bg-tertiary] rounded-lg"><User className="w-5 h-5 text-[--text-muted]" /><div><p className="text-sm text-[--text-muted]">Last Name</p><p className="text-[--text-primary] font-medium">{userProfile?.last || "Not set"}</p></div></div>
                <div className="flex items-center gap-4 p-4 bg-[--bg-tertiary] rounded-lg"><Mail className="w-5 h-5 text-[--text-muted]" /><div><p className="text-sm text-[--text-muted]">Email</p><p className="text-[--text-primary] font-medium">{userProfile?.email || "Not set"}</p></div></div>
            </div>

            <div className="card-base p-6 space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="heading-secondary text-lg text-[--text-primary]">Your Teams</h2>
                    <span className="text-sm text-[--text-muted]">{teamIds.length}/{MAX_PLAYER_TEAMS} joined</span>
                </div>

                {loading ? (
                    <p className="text-[--text-muted] text-sm">Loading your teams...</p>
                ) : teams.length === 0 ? (
                    <p className="text-[--text-muted] text-sm">You haven't joined any teams yet.</p>
                ) : (
                    <div className="space-y-2">
                        {teams.map((t) => (
                            <div key={t.id} className="flex items-center gap-3 p-3 bg-[--bg-tertiary] rounded-lg">
                                <Users className="w-4 h-4 text-[--text-muted]" />
                                <p className="text-[--text-primary] font-medium">{t.name}</p>
                            </div>
                        ))}
                    </div>
                )}

                {teamIds.length < MAX_PLAYER_TEAMS ? (
                    <AddTeamForm userId={userId} userProfile={userProfile} onJoined={onTeamJoined} />
                ) : (
                    <p className="text-xs text-[--text-muted]">You've reached the max of {MAX_PLAYER_TEAMS} teams.</p>
                )}
            </div>

            <div className="card-base p-6">
                <h2 className="heading-secondary text-lg text-red-400 mb-2">Account Deletion</h2>
                <p className="text-[--text-secondary] text-sm mb-4">Deleting your account is permanent and cannot be undone.</p>
                <button onClick={onDeleteAccount} className="btn-secondary text-red-400 border-red-400/30 hover:bg-red-400/10">Delete Account</button>
            </div>
        </div>
    );
};

// Drops just the autosave pointer doc, leaving its video in Storage intact.
// Used when a draft is being promoted into a real, permanent game session —
// the draft's video path becomes that session's own permanent video, so it
// must survive this cleanup rather than being deleted with the pointer.
const clearAutosaveDoc = async (uid) => {
    if (!uid) return;
    try {
        const { doc, deleteDoc } = window.firebaseFirestore;
        await deleteDoc(doc(firebaseDb, "users", uid, "autosave", "current"));
    } catch (e) { /* doc may not exist */ }
};

// Drops the autosave pointer doc AND its draft video — used when the draft is
// genuinely being abandoned rather than promoted (explicit discard, video
// removed, or a different saved game opened instead). Reads the pointer doc
// first to learn which draft folder to delete, since each draft now gets its
// own id/path rather than sharing one fixed "_autosave" path. Errors are
// swallowed throughout: a slot that's already gone (or was never created) is
// not a failure. Note this is also safe to call on a session-pointer doc (see
// setSessionPointer below) — those never have a draftSessionId field, so it
// just clears the pointer without touching Storage.
const discardAutosaveDraft = async (uid) => {
    if (!uid) return;
    let draftId = null;
    try {
        const { doc, getDoc, deleteDoc } = window.firebaseFirestore;
        const docRef = doc(firebaseDb, "users", uid, "autosave", "current");
        const snap = await getDoc(docRef);
        if (snap.exists()) draftId = snap.data().draftSessionId || null;
        await deleteDoc(docRef);
    } catch (e) { /* doc may not exist */ }
    if (!draftId) return;
    try {
        const { ref, deleteObject } = window.firebaseStorage;
        await deleteObject(ref(firebaseStorage, `videos/${uid}/${draftId}/original.mp4`));
    } catch (e) { /* video may not exist */ }
};

// Points the per-user autosave slot at a real, already-saved session instead
// of clearing it — used whenever currentSession becomes a real session
// (right after a manual save, or right after loading one from Saved Games).
// The session's own doc is what actually keeps getting autosaved from here
// on (see the tick's `if (d.currentSession)` branch), so this pointer is
// deliberately lightweight: just enough for login to recognize "a session was
// open last time" and offer to jump back into it, rather than the user
// landing on the session list and wondering whether their edits since the
// last save survived. Distinguished from a freeform draft doc by having a
// `sessionId` field instead of `videoPath`/`draftSessionId`.
const setSessionPointer = async (uid, session) => {
    if (!uid || !session) return;
    try {
        const { doc, setDoc, serverTimestamp } = window.firebaseFirestore;
        await setDoc(doc(firebaseDb, "users", uid, "autosave", "current"), {
            sessionId: session.id,
            sessionShared: !!session.__shared,
            sessionOwnerUid: session.__shared ? session.ownerUid : uid,
            updatedAt: serverTimestamp(),
        });
    } catch (e) { /* non-critical */ }
};

// Self-heal for players whose coach deleted their account/team. Firestore
// rules don't let one user edit another's profile doc, so a coach's deletion
// can't clean up players' teamIds/coachUids -- the player's own client does it
// the next time the dashboard loads. Without this, dead teams keep counting
// toward MAX_PLAYER_TEAMS and stale coachUids linger. Returns the patch to
// apply to local state, or null if nothing was stale.
const pruneDeletedTeams = async (userId, userProfile) => {
    const teamIds = userProfile?.teamIds || [];
    if (!userId || !teamIds.length || !window.firebaseFirestore) return null;
    const { doc, getDoc, updateDoc, arrayRemove } = window.firebaseFirestore;
    const snaps = await Promise.all(teamIds.map((id) => getDoc(doc(firebaseDb, "teams", id))));
    const deadIds = teamIds.filter((_, i) => !snaps[i].exists());
    if (!deadIds.length) return null;
    const liveTeamIds = teamIds.filter((_, i) => snaps[i].exists());
    const liveCoachUids = [...new Set(snaps.filter((s) => s.exists()).map((s) => s.data().coachUid).filter(Boolean))];
    await updateDoc(doc(firebaseDb, "users", userId), { teamIds: arrayRemove(...deadIds), coachUids: liveCoachUids });
    return { teamIds: liveTeamIds, coachUids: liveCoachUids };
};

// Main App
function App() {
    const [authState, setAuthState] = useState("loading");
    const [user, setUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [needsLegalReaccept, setNeedsLegalReaccept] = useState(false);
    const [maintenance, setMaintenance] = useState({ enabled: false, message: "" });
    const [showStaffLogin, setShowStaffLogin] = useState(false);
    const [announcement, setAnnouncement] = useState({ enabled: false, message: "", id: "" });
    const [dismissedAnnouncementId, setDismissedAnnouncementId] = useState(() => {
        try { return localStorage.getItem(DISMISSED_ANNOUNCEMENT_KEY) || ""; } catch (e) { return ""; }
    });
    const [activeTab, setActiveTab] = useState("marking");
    const [authError, setAuthError] = useState("");
    const [pendingRole, setPendingRole] = useState(null); // "coach" | "player", chosen right after signup
    const [markers, setMarkers] = useState(SPORT_MARKERS["Ice Hockey"]);
    const [sport, setSport] = useState("Ice Hockey");
    const [theme, setTheme] = useState("dark");
    // Starts false: the cached value is only trusted once we know WHICH user is
    // signed in (see the cache lookup in onAuthStateChanged). Previously the cache
    // was a bare true/false, so it leaked across accounts and logouts.
    const [isPremium, setIsPremium] = useState(false);
    const [premiumLoaded, setPremiumLoaded] = useState(false);

    const [videoUrl, setVideoUrl] = useState(null);
    const [videoFile, setVideoFile] = useState(null);
    const [currentSession, setCurrentSession] = useState(null);
    const videoRef = useRef(null);
    // Tracks the main video's current playback position so it can be included
    // in autosave and restored after a refresh. A ref (not state) since it
    // updates on every timeupdate tick and shouldn't trigger re-renders.
    const videoPositionRef = useRef(0);
    // Holds a playback position to seek to once the video element has loaded
    // enough of a freshly-restored src to be seekable (set by autosave restore).
    const pendingSeekTimeRef = useRef(null);
    const [homeMarkers, setHomeMarkers] = useState([]);
    const [awayMarkers, setAwayMarkers] = useState([]);
    // Freeform notes about the whole game (not tied to any one clip), saved with the game session
    const [gameNotes, setGameNotes] = useState("");
    const [offsetBefore, setOffsetBefore] = useState(5);
    const [offsetAfter, setOffsetAfter] = useState(5);
    const [offsetMode, setOffsetMode] = useState("classic"); // "classic" | "general" | "individual" | "categorical"
    // Per-event-type before/after durations for "categorical" mode, e.g. { "Goal": { before: 6, after: 4 } }.
    // A marker name with no entry here just falls back to offsetBefore/offsetAfter.
    const [categoricalOffsets, setCategoricalOffsets] = useState({});
    const [videoSize, setVideoSize] = useState(75);
    const [playerTab, setPlayerTab] = useState("dashboard");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showPremiumModal, setShowPremiumModal] = useState(false);
    const [deletePassword, setDeletePassword] = useState("");
    const [deleteLoading, setDeleteLoading] = useState(false);
    // Sport-change confirmation modal (replaces a native window.confirm popup)
    const [pendingSportChange, setPendingSportChange] = useState(null); // { newSport, resolve }
    // Carries a "share this clip" request from the Videos tab over to the Team tab
    const [pendingClipShare, setPendingClipShare] = useState(null); // { sessionId, clips: [{team,name,time}] }

    // Roster (lifted up so both Stats and Marking tabs can read/write it)
    const [homeTables, setHomeTables] = useState(() => getDefaultTables("Ice Hockey"));
    const [awayTables, setAwayTables] = useState(() => getDefaultTables("Ice Hockey"));
    const [hasRoster, setHasRoster] = useState(false);

    // Premium: tag events to specific roster players
    const [playerTaggingEnabled, setPlayerTaggingEnabled] = useState(false);
    // Premium: attach a short note to a clip when marking it
    const [notesEnabled, setNotesEnabled] = useState(false);
    const [eventCounterEnabled, setEventCounterEnabled] = useState(false);
    // Free feature (not premium-gated): pause the video whenever a marker is logged in the Marking tab
    const [pauseOnMarkEnabled, setPauseOnMarkEnabled] = useState(false);
    // Saved preferences for premium features are kept as-is (so they come back if the user
    // resubscribes), but everything the UI and clip logic reads goes through these
    // premium-aware values so a free account never behaves as if they were on.
    const effPlayerTaggingEnabled = isPremium && playerTaggingEnabled;
    const effNotesEnabled = isPremium && notesEnabled;
    const effEventCounterEnabled = isPremium && eventCounterEnabled;
    const effOffsetMode = isPremium ? offsetMode : "classic";

    // Autosave: protects against a refresh (or crash/close) wiping out marking/
    // stat progress the user hasn't manually saved yet. "idle" = nothing to
    // protect (no video loaded), "saving"/"saved"/"error" reflect the last tick.
    const [autosaveStatus, setAutosaveStatus] = useState("idle");
    // Set (non-null) when login finds a leftover autosave slot from a session
    // that never got a chance to be saved or discarded — e.g. the tab was
    // closed or refreshed mid-game. Holds the raw autosave doc data until the
    // user chooses to restore or discard it.
    const [pendingAutosaveRestore, setPendingAutosaveRestore] = useState(null);
    // Set (non-null) when login instead finds the autosave slot pointing at a
    // real, already-saved session that was still open last time (see
    // setSessionPointer). Holds just { sessionId, sessionShared,
    // sessionOwnerUid } until the user chooses to jump back in or dismiss.
    const [pendingSessionResume, setPendingSessionResume] = useState(null);
    // Id assigned to the current in-progress (not-yet-saved) video the moment
    // it's picked — see handleVideoChange. It doubles as both the Storage
    // folder name the video is uploaded to AND the eventual gameSessions doc
    // id if the user saves it, so the video only ever needs to be uploaded
    // once, no matter whether autosave or a manual "Save" writes it first.
    // Null when there's no unsaved video (nothing picked, a URL was pasted
    // instead of a file, or a real saved session is currently loaded).
    const draftSessionIdRef = useRef(null);
    // Storage path of the video currently backing the FREEFORM autosave slot
    // (i.e. when there's no currentSession yet). Null means either no video
    // has been uploaded to the slot yet, or the loaded video belongs to a real
    // Games-tab session instead, in which case autosave never touches Storage.
    const autosaveVideoPathRef = useRef(null);
    // Guards against two autosave ticks overlapping if one is still writing
    // (e.g. a slow connection) when the next interval fires.
    const autosaveTickingRef = useRef(false);
    const deletingAccountRef = useRef(false); // true while handleConfirmedDelete runs; stops autosave re-creating docs
    // Always holds the latest values autosave needs, without making the
    // interval-effect below depend on them — depending on markers/tables
    // directly would tear down and restart the timer on every tap, and an
    // actively-marking user would never actually hit a quiet 20-30s window.
    const autosaveDataRef = useRef({});

    // Apply theme
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
    }, [theme]);

    // Apply sport theme
    useEffect(() => {
        document.documentElement.setAttribute('data-sport', sport);
    }, [sport]);

    // Firebase init
    useEffect(() => {
        const init = async () => {
            const firebase = await initFirebase();
            if (firebase) {
                // Live maintenance flag: flipping appConfig/maintenance.enabled locks or
                // unlocks the site for everyone (except the bypass account) immediately,
                // no reload needed. Readable while signed out so the login page honors it.
                // On error/missing doc we fail open (site stays usable).
                try {
                    const { doc, onSnapshot } = window.firebaseFirestore;
                    onSnapshot(
                        doc(firebase.db, "appConfig", "maintenance"),
                        (snap) => {
                            const d = snap.exists() ? snap.data() : null;
                            setMaintenance({ enabled: d?.enabled === true, message: d?.message || "" });
                        },
                        (err) => console.error("Maintenance flag listener error:", err)
                    );
                    onSnapshot(
                        doc(firebase.db, "appConfig", "announcement"),
                        (snap) => {
                            const d = snap.exists() ? snap.data() : null;
                            const message = d?.message || "";
                            setAnnouncement({ enabled: d?.enabled === true, message, id: String(d?.id || message) });
                        },
                        (err) => console.error("Announcement listener error:", err)
                    );
                } catch (e) { console.error("Maintenance flag setup failed:", e); }
                const { onAuthStateChanged } = window.firebaseAuth;
                onAuthStateChanged(firebase.auth, async (user) => {
                    if (user) {
                        setUser(user);
                        let loadedSport = "Ice Hockey";
                        // Use the cached plan only if it belongs to THIS user, so the UI
                        // doesn't flash free while the status check runs and so a failed
                        // check can't hand one account another account's premium.
                        let cachedPremium = false;
                        try {
                            const cached = JSON.parse(localStorage.getItem("snipshotPremium"));
                            if (cached && cached.uid === user.uid) cachedPremium = !!cached.premium;
                        } catch (e) { /* old-format or corrupt value: ignore it */ }
                        setIsPremium(cachedPremium);
                        try {
                            const idToken = await user.getIdToken();
                            const res = await fetch(
                                `${BACKEND_URL}/api/user-status/${user.uid}`,
                                {
                                    headers: {
                                        "Authorization": `Bearer ${idToken}`
                                    }
                                }
                            );

                            if (!res.ok) {
                                throw new Error(`user-status returned ${res.status}`);
                            }

                            const data = await res.json();

                            setIsPremium(data.premium);
                            console.log("API returned:", data.premium);

                            localStorage.setItem(
                                "snipshotPremium",
                                JSON.stringify({ uid: user.uid, premium: !!data.premium })
                            );

                        } catch (err) {

                            console.error("Premium status check failed:", err);

                        } finally {

                            setPremiumLoaded(true);
                        }

                        try {
                            const { doc, getDoc } = window.firebaseFirestore;
                            const userDoc = await getDoc(doc(firebase.db, "users", user.uid));
                            if (userDoc.exists()) {
                                const userData = userDoc.data();
                                loadedSport = userData.sport || "Ice Hockey";
                                setUserProfile(userData);
                                // Re-gate if the stored acceptance is missing or for an older
                                // LEGAL_VERSION. Runs before setAuthState("authenticated") so the
                                // app never flashes before the prompt. If the read fails we let
                                // the user through rather than lock everyone out.
                                try {
                                    const legalSnap = await getDoc(doc(firebase.db, "legalAcceptance", user.uid));
                                    const a = legalSnap.exists() ? legalSnap.data() : null;
                                    setNeedsLegalReaccept(!a || a.tosVersion !== LEGAL_VERSION || a.privacyVersion !== LEGAL_VERSION);
                                } catch (e) { console.error("Legal acceptance check failed:", e); }
                                setSport(loadedSport);
                                // Default the stat sheet to THIS account's sport. Previously this stayed
                                // hardcoded to the Ice Hockey tables from initial state until the roster
                                // fetch below ran, so a reload with no saved roster yet — e.g. bouncing
                                // back from the Stripe checkout page after buying premium — would show
                                // hockey columns for a basketball (or any non-hockey) account.
                                setHomeTables(getDefaultTables(loadedSport));
                                setAwayTables(getDefaultTables(loadedSport));
                                setAuthState("authenticated");
                                const settingsDoc = await getDoc(doc(firebase.db, "settings", user.uid));
                                if (settingsDoc.exists()) {
                                    const s = settingsDoc.data();
                                    if (typeof s.eventCounterEnabled === "boolean") setEventCounterEnabled(s.eventCounterEnabled);
                                    if (typeof s.playerTaggingEnabled === "boolean") setPlayerTaggingEnabled(s.playerTaggingEnabled);
                                    if (typeof s.notesEnabled === "boolean") setNotesEnabled(s.notesEnabled);
                                    if (typeof s.pauseOnMarkEnabled === "boolean") setPauseOnMarkEnabled(s.pauseOnMarkEnabled);
                                    if (s.markers) setMarkers(s.markers);
                                    if (s.offsetBefore) setOffsetBefore(s.offsetBefore);
                                    else if (s.offset) setOffsetBefore(s.offset);
                                    if (s.offsetAfter) setOffsetAfter(s.offsetAfter);
                                    else if (s.offset) setOffsetAfter(s.offset);
                                    if (s.offsetMode) setOffsetMode(s.offsetMode);
                                    if (s.categoricalOffsets) setCategoricalOffsets(s.categoricalOffsets);
                                    if (s.size) setVideoSize(s.size);
                                    if (s.theme) setTheme(s.theme);
                                }
                            } else setAuthState("role");
                        } catch (e) { setAuthState("role"); }

                        try {
                            const { doc, getDoc } = window.firebaseFirestore;
                            const rosterDoc = await getDoc(doc(firebase.db, "rosters", user.uid));
                            if (rosterDoc.exists()) {
                                const data = rosterDoc.data();
                                // Rosters saved before a sport switch carry the OLD sport's columns
                                // (e.g. hockey's G/A/PIM). Only auto-apply it if it's tagged for the
                                // sport we just loaded — a mismatch here is exactly what silently
                                // left stale columns in place after switching sports. Untagged
                                // rosters predate this check and are trusted as before.
                                const rosterSport = data.sport;
                                if (!rosterSport || rosterSport === loadedSport) {
                                    if (data.homeTables) setHomeTables(data.homeTables);
                                    if (data.awayTables) setAwayTables(data.awayTables);
                                }
                                setHasRoster(true);
                            }
                        } catch (e) { console.error("Error loading roster:", e); }

                        try {
                            const { doc, getDoc } = window.firebaseFirestore;
                            const autosaveDoc = await getDoc(doc(firebase.db, "users", user.uid, "autosave", "current"));
                            if (autosaveDoc.exists()) {
                                const autosaveData = autosaveDoc.data();
                                if (autosaveData.sessionId) {
                                    // A real, already-saved session was still open last
                                    // time — offer to jump back into it. Its data was
                                    // never at risk (autosave writes straight into the
                                    // session doc), this is purely about not leaving the
                                    // user to wonder where their edits went.
                                    setPendingSessionResume(autosaveData);
                                } else {
                                    // Don't auto-load into the marking view — ask first, since
                                    // silently replacing whatever the user expects to see on
                                    // login would be its own kind of data-loss surprise.
                                    setPendingAutosaveRestore(autosaveData);
                                }
                            }
                        } catch (e) { console.error("Error checking autosave:", e); }
                    } else {
                        setIsPremium(false);
                        try { localStorage.removeItem("snipshotPremium"); } catch (e) { }
                        setUser(null); setUserProfile(null); setAuthState("login");
                    }
                });
            } else setAuthState("login");
        };
        init();
    }, []);

    const handleVideoChange = (url, file) => {
        // If a saved session is currently open, a new pick here means "replace
        // this session's video" (the only way to do that is Remove -> pick a
        // new file), not "abandon this session and start a freeform draft".
        // Clearing currentSession unconditionally used to sever that link
        // right as the new file was chosen, so Save could never route back to
        // "Update Existing Session" — it silently fell through to creating a
        // brand-new session (or an orphaned freeform draft) instead, leaving
        // the session you meant to update either untouched or, worse,
        // pointing at a video that later got cleaned up as stale. Only clear
        // it when there was no session open to begin with.
        const replacingSessionVideo = !!currentSession;
        // Each draft now gets its own unique Storage path rather than sharing
        // one fixed slot, so — unlike before — a new pick no longer overwrites
        // (and thereby cleans up) whatever the previous unsaved draft left
        // behind. Discard it explicitly before starting the new one. Safe/no-op
        // if there was no prior draft. Skip this when we're replacing a saved
        // session's video, though — there's no freeform draft to clean up, and
        // this would otherwise delete the "resume last session" pointer.
        if (user && !replacingSessionVideo) discardAutosaveDraft(user.uid);
        setVideoUrl(url); setVideoFile(file); setGameNotes("");
        if (!replacingSessionVideo) setCurrentSession(null);
        // A freshly-picked video hasn't been uploaded anywhere yet, even if a
        // different video's path was sitting here a moment ago.
        autosaveVideoPathRef.current = null;
        // Only a real file needs a Storage home — a pasted video URL has
        // nothing for us to upload, so there's no draft id for it. And if
        // we're replacing a session's video, its Storage home is that
        // session's own path (handled by Update Existing Session), not a new
        // freeform draft id.
        if (file instanceof Blob && user && !replacingSessionVideo) {
            const { doc, collection } = window.firebaseFirestore;
            draftSessionIdRef.current = doc(collection(firebaseDb, "users", user.uid, "gameSessions")).id;
        } else {
            draftSessionIdRef.current = null;
        }
        videoPositionRef.current = 0;
    };
    // Keeps videoPositionRef current as the video plays, so autosave can pick
    // up the latest position without needing it in React state (state would
    // re-render on every timeupdate tick, which fires several times a second).
    const handleVideoPositionChange = useCallback((t) => { videoPositionRef.current = t; }, []);
    // After an autosave restore points the video element at a new (Storage)
    // src, seek to the saved position as soon as the browser has loaded
    // enough metadata to make that seek possible.
    useEffect(() => {
        const video = videoRef.current;
        if (!video || pendingSeekTimeRef.current == null) return;
        const seek = () => {
            if (pendingSeekTimeRef.current != null) {
                video.currentTime = pendingSeekTimeRef.current;
                videoPositionRef.current = pendingSeekTimeRef.current;
                pendingSeekTimeRef.current = null;
            }
        };
        if (video.readyState >= 1) seek();
        else {
            video.addEventListener("loadedmetadata", seek, { once: true });
            return () => video.removeEventListener("loadedmetadata", seek);
        }
    }, [videoUrl]);
    const handleRemoveVideo = useCallback(() => {
        // Only revoke if this was actually a blob URL — videoUrl can also be
        // a Firebase Storage https:// URL (a loaded session's video), and
        // revoking a non-blob URL is a no-op anyway but shouldn't be relied on.
        if (videoUrl && videoUrl.startsWith("blob:")) URL.revokeObjectURL(videoUrl);
        // Removing the video to pick a replacement for an open saved session
        // is not the same as abandoning that session — keep currentSession
        // set so the pending re-pick (handleVideoChange) and the eventual
        // Save still know this is an in-place video swap for THAT session,
        // not a freeform draft. Only clear it when there was no session open.
        const hadSession = !!currentSession;
        setVideoUrl(null);
        setVideoFile(null);
        setHomeMarkers([]);
        setAwayMarkers([]);
        setGameNotes("");
        if (!hadSession) setCurrentSession(null);
        autosaveVideoPathRef.current = null;
        draftSessionIdRef.current = null;
        setAutosaveStatus("idle");
        // The user explicitly cleared the video with no session open, so
        // there's nothing left worth resuming — discard the freeform draft
        // (doc + its video) rather than leaving a stale one that would
        // otherwise offer to restore itself on the next login. Skip this when
        // a saved session is still open: its "resume" pointer is the session
        // itself, not a freeform draft, and there's no video to delete here.
        if (user && !hadSession) discardAutosaveDraft(user.uid);
    }, [videoUrl, user, currentSession]);
    const handleAddMarker = (team, marker) => { if (team === "home") setHomeMarkers([...homeMarkers, marker]); else setAwayMarkers([...awayMarkers, marker]); };
    const handleDeleteMarker = (team, id) => { if (team === "home") setHomeMarkers(homeMarkers.filter((m) => m.id !== id)); else setAwayMarkers(awayMarkers.filter((m) => m.id !== id)); };
    // Replaces one existing clip in place (matched by id). Autosave picks the change up
    // like any other marker edit, so it also reaches a saved/shared session.
    const handleUpdateMarker = (team, id, updated) => {
        const apply = (list) => list.map((m) => (m.id === id ? updated : m));
        if (team === "home") setHomeMarkers(apply); else setAwayMarkers(apply);
    };
    // Jumps to a clip's moment in the Marking tab. MarkingTab stays mounted (just hidden)
    // while other tabs are open, so the video element and its loaded src are still there.
    const handleGoToTimestamp = (time) => {
        setActiveTab("marking");
        const video = videoRef.current;
        if (!video) return;
        video.pause();
        video.currentTime = time;
        // Wait a tick for the tab to become visible, then bring the player on screen
        setTimeout(() => video.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
    };

    // Keep a ref mirror of everything autosave needs, refreshed every render
    // (cheap — just an object assignment) so the interval effect further down
    // doesn't need any of this in its dependency array.
    useEffect(() => {
        autosaveDataRef.current = {
            videoUrl, videoFile, currentSession,
            homeMarkers, awayMarkers, homeTables, awayTables, gameNotes,
            offsetBefore, offsetAfter, offsetMode, categoricalOffsets, playerTaggingEnabled, notesEnabled, eventCounterEnabled, videoSize,
        };
    });

    // Autosave tick, on a timer rather than on every change, to keep Firestore/
    // Storage request volume down. Two modes:
    //  - a real Games-tab session is loaded/saved (currentSession set): just
    //    rewrite that session's markers/stats/notes/settings via the same
    //    update-in-place path "Update Existing Session" uses — never re-uploads
    //    the video, since the session's video already lives in Storage.
    //  - no saved session yet: use a dedicated per-user autosave slot. The
    //    video uploads once, the first tick after a new file is picked; every
    //    tick after that only rewrites the small JSON-able fields.
    useEffect(() => {
        if (!user) return;
        const AUTOSAVE_INTERVAL_MS = 25000; // within the 20-30s window

        const tick = async () => {
            if (autosaveTickingRef.current || deletingAccountRef.current) return;
            const d = autosaveDataRef.current;
            if (!d.videoFile || !d.videoUrl) return; // nothing loaded yet — nothing to protect

            autosaveTickingRef.current = true;
            setAutosaveStatus("saving");
            try {
                const { doc, setDoc, updateDoc, serverTimestamp } = window.firebaseFirestore;
                const commonFields = {
                    homeMarkers: d.homeMarkers, awayMarkers: d.awayMarkers,
                    homeTables: d.homeTables, awayTables: d.awayTables,
                    gameNotes: d.gameNotes || "",
                    currentTime: videoPositionRef.current || 0,
                    settings: {
                        offsetBefore: d.offsetBefore, offsetAfter: d.offsetAfter, offsetMode: d.offsetMode, categoricalOffsets: d.categoricalOffsets,
                        playerTaggingEnabled: d.playerTaggingEnabled, notesEnabled: d.notesEnabled,
                        eventCounterEnabled: d.eventCounterEnabled, videoSize: d.videoSize,
                    },
                    updatedAt: serverTimestamp(),
                };

                if (d.currentSession) {
                    const path = d.currentSession.__shared
                        ? doc(firebaseDb, "sharedSessions", d.currentSession.id)
                        : doc(firebaseDb, "users", user.uid, "gameSessions", d.currentSession.id);
                    await updateDoc(path, commonFields);
                } else {
                    let videoPath = autosaveVideoPathRef.current;
                    const isNewVideoFile = d.videoFile instanceof Blob;

                    if (isNewVideoFile && !videoPath && draftSessionIdRef.current) {
                        const { ref, uploadBytesResumable } = window.firebaseStorage;
                        const storageRef = ref(firebaseStorage, `videos/${user.uid}/${draftSessionIdRef.current}/original.mp4`);
                        await new Promise((resolve, reject) => {
                            const task = uploadBytesResumable(storageRef, d.videoFile, { contentType: d.videoFile.type || "video/mp4" });
                            task.on("state_changed", null, reject, resolve);
                        });
                        videoPath = storageRef.fullPath;
                        autosaveVideoPathRef.current = videoPath;
                    }

                    // videoPath can still be null here if the "video" is actually
                    // just metadata from somewhere that never went through Storage —
                    // shouldn't happen in practice, but skip the write rather than
                    // save a slot with no way to fetch its video back.
                    if (videoPath) {
                        await setDoc(doc(firebaseDb, "users", user.uid, "autosave", "current"), {
                            videoPath,
                            // The id this draft's video is stored under — carried in
                            // the pointer doc so a later restore knows which folder
                            // to keep autosaving into, and so it can be found again
                            // if the draft is ever abandoned rather than saved.
                            draftSessionId: draftSessionIdRef.current,
                            videoName: d.videoFile.name,
                            videoSize: d.videoFile.size,
                            videoType: d.videoFile.type || "video/mp4",
                            ...commonFields,
                        });
                    }
                }
                setAutosaveStatus("saved");
            } catch (e) {
                console.error("Autosave failed:", e);
                setAutosaveStatus("error");
            } finally {
                autosaveTickingRef.current = false;
            }
        };

        const interval = setInterval(tick, AUTOSAVE_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [user]);

    // Offered by the "Resume unsaved game?" modal after login finds a leftover
    // autosave slot (e.g. the tab was closed or refreshed mid-game last time).
    const handleRestoreAutosave = useCallback(async () => {
        const data = pendingAutosaveRestore;
        if (!data || !user) { setPendingAutosaveRestore(null); return; }
        try {
            const { getDownloadURL, ref } = window.firebaseStorage;
            const url = await getDownloadURL(ref(firebaseStorage, data.videoPath));
            if (videoUrl && videoUrl.startsWith("blob:")) URL.revokeObjectURL(videoUrl);
            setVideoUrl(url);
            setVideoFile({ name: data.videoName, size: data.videoSize, type: data.videoType });
            setHomeMarkers(data.homeMarkers || []);
            setAwayMarkers(data.awayMarkers || []);
            if (data.homeTables) setHomeTables(data.homeTables);
            if (data.awayTables) setAwayTables(data.awayTables);
            setGameNotes(data.gameNotes || "");
            const s = data.settings;
            if (s) {
                if (typeof s.offsetBefore === "number") setOffsetBefore(s.offsetBefore);
                if (typeof s.offsetAfter === "number") setOffsetAfter(s.offsetAfter);
                if (s.offsetMode) setOffsetMode(s.offsetMode);
                if (s.categoricalOffsets) setCategoricalOffsets(s.categoricalOffsets);
                if (typeof s.playerTaggingEnabled === "boolean") setPlayerTaggingEnabled(s.playerTaggingEnabled);
                if (typeof s.notesEnabled === "boolean") setNotesEnabled(s.notesEnabled);
                if (typeof s.eventCounterEnabled === "boolean") setEventCounterEnabled(s.eventCounterEnabled);
                if (typeof s.videoSize === "number") setVideoSize(s.videoSize);
            }
            // This is the freeform slot, not a real Games-tab session, so
            // currentSession stays null — but the video is already sitting in
            // Storage, so later autosave ticks (and a later manual save) must
            // reuse that path/id instead of uploading it all over again.
            setCurrentSession(null);
            autosaveVideoPathRef.current = data.videoPath;
            draftSessionIdRef.current = data.draftSessionId || null;
            pendingSeekTimeRef.current = typeof data.currentTime === "number" ? data.currentTime : null;
        } catch (e) {
            console.error("Error restoring autosave:", e);
        } finally {
            setPendingAutosaveRestore(null);
        }
    }, [pendingAutosaveRestore, user, videoUrl]);

    const handleDiscardAutosave = useCallback(() => {
        const uid = user?.uid;
        setPendingAutosaveRestore(null);
        if (uid) discardAutosaveDraft(uid);
    }, [user]);

    // Offered by the "Continue your last game?" modal after login finds the
    // autosave slot pointing at a real session rather than a freeform draft
    // (see setSessionPointer). Refetches the session doc fresh — it may have
    // kept autosaving after the pointer was last written — and loads it the
    // same way opening it from Saved Games would.
    const handleResumeSession = useCallback(async () => {
        const pointer = pendingSessionResume;
        if (!pointer || !user) { setPendingSessionResume(null); return; }
        try {
            const { doc, getDoc } = window.firebaseFirestore;
            const { getDownloadURL, ref } = window.firebaseStorage;
            const sessionOwnerUid = pointer.sessionShared ? pointer.sessionOwnerUid : user.uid;
            const path = pointer.sessionShared
                ? doc(firebaseDb, "sharedSessions", pointer.sessionId)
                : doc(firebaseDb, "users", sessionOwnerUid, "gameSessions", pointer.sessionId);
            const snap = await getDoc(path);
            if (!snap.exists()) { setPendingSessionResume(null); return; }
            const session = snap.data();
            const url = await getDownloadURL(ref(firebaseStorage, session.videoPath));
            if (videoUrl && videoUrl.startsWith("blob:")) URL.revokeObjectURL(videoUrl);
            setVideoUrl(url);
            setVideoFile({ name: session.videoName, size: session.videoSize, type: session.videoType });
            setHomeMarkers(session.homeMarkers || []);
            setAwayMarkers(session.awayMarkers || []);
            if (session.homeTables) setHomeTables(session.homeTables);
            if (session.awayTables) setAwayTables(session.awayTables);
            setGameNotes(session.gameNotes || "");
            const s = session.settings;
            if (s) {
                if (typeof s.offsetBefore === "number") setOffsetBefore(s.offsetBefore);
                if (typeof s.offsetAfter === "number") setOffsetAfter(s.offsetAfter);
                if (s.offsetMode) setOffsetMode(s.offsetMode);
                if (s.categoricalOffsets) setCategoricalOffsets(s.categoricalOffsets);
                if (typeof s.playerTaggingEnabled === "boolean") setPlayerTaggingEnabled(s.playerTaggingEnabled);
                if (typeof s.notesEnabled === "boolean") setNotesEnabled(s.notesEnabled);
                if (typeof s.eventCounterEnabled === "boolean") setEventCounterEnabled(s.eventCounterEnabled);
                if (typeof s.videoSize === "number") setVideoSize(s.videoSize);
            }
            setCurrentSession({ id: pointer.sessionId, __shared: !!pointer.sessionShared, ownerUid: sessionOwnerUid });
            // This session's video already lives at its own permanent path —
            // no freeform-draft bookkeeping applies to it.
            autosaveVideoPathRef.current = null;
            draftSessionIdRef.current = null;
            pendingSeekTimeRef.current = typeof session.currentTime === "number" ? session.currentTime : null;
            setActiveTab("marking");
        } catch (e) {
            console.error("Error resuming session:", e);
        } finally {
            setPendingSessionResume(null);
        }
    }, [pendingSessionResume, user, videoUrl]);

    // Dismissing just hides the prompt — the session itself was already saved
    // safely (autosave has been writing straight into its doc), so there's
    // nothing here to discard. Leaves the pointer in place; it'll offer to
    // resume again next login if the session is still the one last worked on.
    const handleDismissSessionResume = useCallback(() => {
        setPendingSessionResume(null);
    }, []);

    const persistSetting = useCallback(async (key, value) => {
        if (!user) return;
        try {
            const { doc, setDoc } = window.firebaseFirestore;
            await setDoc(doc(firebaseDb, "settings", user.uid), { [key]: value }, { merge: true });
        } catch (e) {
            console.error(`Failed to save "${key}" setting:`, e);
        }
    }, [user]);

    const persistEventCounterEnabled = useCallback((value) => {
        setEventCounterEnabled(value);
        persistSetting("eventCounterEnabled", value);
    }, [persistSetting]);

    const persistPlayerTaggingEnabled = useCallback((value) => {
        setPlayerTaggingEnabled(value);
        persistSetting("playerTaggingEnabled", value);
    }, [persistSetting]);

    const persistNotesEnabled = useCallback((value) => {
        setNotesEnabled(value);
        persistSetting("notesEnabled", value);
    }, [persistSetting]);

    const persistPauseOnMarkEnabled = useCallback((value) => {
        setPauseOnMarkEnabled(value);
        persistSetting("pauseOnMarkEnabled", value);
    }, [persistSetting]);

    const persistOffsetBefore = useCallback((value) => {
        setOffsetBefore(value);
        persistSetting("offsetBefore", value);
    }, [persistSetting]);

    const persistOffsetAfter = useCallback((value) => {
        setOffsetAfter(value);
        persistSetting("offsetAfter", value);
    }, [persistSetting]);

    const persistOffsetMode = useCallback((value) => {
        setOffsetMode(value);
        persistSetting("offsetMode", value);
    }, [persistSetting]);

    const persistCategoricalOffsets = useCallback((value) => {
        setCategoricalOffsets(value);
        persistSetting("categoricalOffsets", value);
    }, [persistSetting]);

    const persistVideoSize = useCallback((value) => {
        setVideoSize(value);
        persistSetting("size", value);
    }, [persistSetting]);

    const persistTheme = useCallback((value) => {
        setTheme(value);
        persistSetting("theme", value);
    }, [persistSetting]);

    const persistMarkers = useCallback((value) => {
        setMarkers(value);
        persistSetting("markers", value);
    }, [persistSetting]);

    const handleLogin = useCallback(async (email, password, isGoogle = false) => {
        setAuthError("");
        try {
            if (isGoogle) { const { GoogleAuthProvider, signInWithPopup } = window.firebaseAuth; await signInWithPopup(firebaseAuth, new GoogleAuthProvider()); }
            else { const { signInWithEmailAndPassword } = window.firebaseAuth; await signInWithEmailAndPassword(firebaseAuth, email, password); }
        } catch (e) { setAuthError(e.code === 'auth/invalid-credential' ? "Invalid email or password" : "Login failed"); }
    }, []);

    // Records ToS/Privacy acceptance in its own collection — deliberately NOT on
    // users/{uid}. The onAuthStateChanged listener treats users/{uid} existing as
    // "this account has finished onboarding" and skips straight to authenticated,
    // so writing there at signup time broke the role/survey flow. This collection
    // is separate to avoid that. It's copied onto the profile doc once that's
    // created (see handleSurveyComplete / PlayerOnboardForm) so it ends up
    // queryable alongside the rest of the profile too.
    const recordLegalAcceptance = useCallback(async (uid) => {
        try {
            const { doc, setDoc, serverTimestamp } = window.firebaseFirestore;
            await setDoc(doc(firebaseDb, "legalAcceptance", uid), {
                tosVersion: LEGAL_VERSION,
                privacyVersion: LEGAL_VERSION,
                acceptedAt: serverTimestamp(),
            });
        } catch (e) { console.error("Failed to record legal acceptance:", e); }
    }, []);

    // Called from the re-acceptance prompt after a LEGAL_VERSION bump.
    const handleAcceptLegal = useCallback(async () => {
        if (!user) return;
        const { doc, setDoc, updateDoc, arrayUnion, serverTimestamp } = window.firebaseFirestore;
        // Throws on failure so the modal can show an error and let them retry.
        await setDoc(doc(firebaseDb, "legalAcceptance", user.uid), {
            tosVersion: LEGAL_VERSION,
            privacyVersion: LEGAL_VERSION,
            acceptedAt: serverTimestamp(),
            acceptedVersions: arrayUnion(LEGAL_VERSION), // lightweight history of every version accepted
        }, { merge: true });
        // Mirror onto the profile like signup does; best-effort.
        updateDoc(doc(firebaseDb, "users", user.uid), {
            legal: { tosVersion: LEGAL_VERSION, privacyVersion: LEGAL_VERSION, acceptedAt: serverTimestamp() },
        }).catch((e) => console.warn("Profile legal mirror failed:", e));
        setNeedsLegalReaccept(false);
    }, [user]);

    const handleSignup = useCallback(async (email, password, isGoogle = false) => {
        setAuthError("");
        try {
            if (isGoogle) {
                const { GoogleAuthProvider, signInWithPopup } = window.firebaseAuth;
                const result = await signInWithPopup(firebaseAuth, new GoogleAuthProvider());
                const { doc, getDoc } = window.firebaseFirestore;
                const userDoc = await getDoc(doc(firebaseDb, "users", result.user.uid));
                if (!userDoc.exists()) { await recordLegalAcceptance(result.user.uid); setAuthState("role"); }
            } else {
                const { createUserWithEmailAndPassword } = window.firebaseAuth;
                const result = await createUserWithEmailAndPassword(firebaseAuth, email, password);
                await recordLegalAcceptance(result.user.uid);
                setAuthState("role");
            }
        } catch (e) { setAuthError(e.code === 'auth/email-already-in-use' ? "Email already exists" : e.code === 'auth/weak-password' ? "Password must be 6+ characters" : "Signup failed"); }
    }, [recordLegalAcceptance]);

    // Sends a reset email regardless of whether the address is registered, so the
    // UI response can't be used to enumerate accounts. Errors are swallowed for the
    // same reason (the LoginForm always shows the same confirmation message).
    const handleForgotPassword = useCallback(async (email) => {
        try {
            const { sendPasswordResetEmail } = window.firebaseAuth;
            await sendPasswordResetEmail(firebaseAuth, email);
        } catch (e) {
            console.error("Password reset email error:", e);
        }
    }, []);

    // Changing your password while signed in requires a fresh reauth first
    // (Firebase rejects updatePassword on a stale session). Returns { error }
    // on failure so the ChangePasswordCard can show it inline.
    const handleChangePassword = useCallback(async (currentPassword, newPassword) => {
        if (!user) return { error: "Not signed in" };
        try {
            const { EmailAuthProvider, reauthenticateWithCredential, updatePassword } = window.firebaseAuth;
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user, credential);
            await updatePassword(user, newPassword);
            return {};
        } catch (e) {
            if (e.code === 'auth/invalid-credential' || e.code === 'auth/wrong-password') return { error: "Current password is incorrect" };
            if (e.code === 'auth/weak-password') return { error: "Password must be 6+ characters" };
            if (e.code === 'auth/too-many-requests') return { error: "Too many attempts. Try again later" };
            console.error("Change password error:", e);
            return { error: "Couldn't update password. Try again" };
        }
    }, [user]);

    // After signup, the person picks coach or player. Coaches continue to the sport survey;
    // players skip straight to entering a team join code (they never touch markers/stats setup).
    const handleRoleSelect = useCallback((role) => {
        setPendingRole(role);
        setAuthState(role === "player" ? "joinTeam" : "survey");
    }, []);

    const handlePlayerOnboardComplete = useCallback((profileData) => {
        setUserProfile(profileData);
        setAuthState("authenticated");
    }, []);

    // A player joined an additional team from their Profile tab — mirror the Firestore
    // arrayUnion locally so the dashboard/profile re-render with the new team right away.
    const handleTeamJoined = useCallback((teamId, coachUid) => {
        setUserProfile((prev) => ({
            ...prev,
            teamIds: [...new Set([...(prev?.teamIds || []), teamId])],
            coachUids: [...new Set([...(prev?.coachUids || []), coachUid])],
        }));
    }, []);

    const handleSurveyComplete = useCallback(async (firstName, lastName, selectedSport) => {
        try {
            const { doc, getDoc, setDoc, serverTimestamp } = window.firebaseFirestore;
            // Pull the acceptance recorded at signup into the profile doc, best-effort —
            // a missing/failed read here shouldn't block onboarding.
            let legal;
            try {
                const legalDoc = await getDoc(doc(firebaseDb, "legalAcceptance", user.uid));
                if (legalDoc.exists()) legal = legalDoc.data();
            } catch (e) { console.error("Failed to read legal acceptance:", e); }
            const profileData = { first: firstName, last: lastName, sport: selectedSport, email: user.email, role: "coach", createdAt: serverTimestamp(), ...(legal && { legal }) };
            await setDoc(doc(firebaseDb, "users", user.uid), profileData, { merge: true });
            const sportMarkers = SPORT_MARKERS[selectedSport] || SPORT_MARKERS["Ice Hockey"];
            await setDoc(doc(firebaseDb, "settings", user.uid), { offsetBefore: 5, offsetAfter: 5, offsetMode: "classic", categoricalOffsets: {}, size: 75, markers: sportMarkers, theme: "dark", eventCounterEnabled: false, playerTaggingEnabled: false, notesEnabled: false });
            setUserProfile(profileData); setMarkers(sportMarkers); setSport(selectedSport); setAuthState("authenticated");
            setHomeTables(getDefaultTables(selectedSport)); setAwayTables(getDefaultTables(selectedSport));
        } catch (e) { console.error("Survey error:", e); }
    }, [user]);

    const handleSportChange = useCallback(async (newSport) => {
        if (newSport === sport) return;

        // Stat columns are sport-specific, so the current tables always need rebuilding
        // for the new sport. Previously this only reset the tables when no roster had
        // ever been saved, so a coach switching sports after saving a roster kept the
        // OLD sport's columns silently mismatched against the new sport's markers
        // everywhere (Marking, Stats, and every game saved from then on). Warn first
        // whenever there's actually player data on the sheet that this would restructure.
        const hasData = getRosterPlayers(homeTables).length > 0 || getRosterPlayers(awayTables).length > 0;
        if (hasData) {
            const confirmed = await new Promise((resolve) => setPendingSportChange({ newSport, resolve }));
            if (!confirmed) return;
        }

        const newMarkers = SPORT_MARKERS[newSport] || SPORT_MARKERS["Ice Hockey"];
        setSport(newSport);
        setMarkers(newMarkers);
        setHomeTables(remapTablesToSport(homeTables, newSport));
        setAwayTables(remapTablesToSport(awayTables, newSport));
        if (user && window.firebaseFirestore) {
            try {
                const { doc, updateDoc } = window.firebaseFirestore;
                await updateDoc(doc(firebaseDb, "users", user.uid), { sport: newSport });
                await updateDoc(doc(firebaseDb, "settings", user.uid), { markers: newMarkers });
                setUserProfile(prev => ({ ...prev, sport: newSport }));
            } catch (e) { console.error("Sport change error:", e); }
        }
    }, [user, sport, homeTables, awayTables]);

    // Lets the Videos tab (or the Games tab's multi-game compilation view) hand
    // off "share this clip" to the Team tab, which owns the actual share flow
    // (it needs a saved session with a stable video in Storage). Defaults to
    // the currently loaded game's session id, but a multi-game compilation
    // clip can come from a different saved game entirely, hence the override.
    const handleShareClipRequest = useCallback((team, markerName, clips, sessionIdOverride) => {
        const targetSessionId = sessionIdOverride || currentSession?.id;
        if (!targetSessionId) {
            alert("Save this game in the Games tab first, then you can share its clips with your players.");
            return;
        }
        setPendingClipShare({
            sessionId: targetSessionId,
            clips: clips.map((c) => ({ team, name: markerName, time: c.time })),
        });
        setActiveTab("team");
    }, [currentSession]);

    const handleLogout = useCallback(async () => {
        try {
            const { signOut } = window.firebaseAuth;
            await signOut(firebaseAuth);
            setIsPremium(false);
            try { localStorage.removeItem("snipshotPremium"); } catch (e) { }
            setUser(null); setUserProfile(null); setNeedsLegalReaccept(false); setShowStaffLogin(false); setAuthState("login");
            // Reset local state only — deliberately not discarding the autosave
            // draft here. Logging out is not the same as discarding: the whole
            // point of the autosave slot is that it survives a closed
            // tab/browser, and that should include a logout/login cycle, not
            // just a refresh while still signed in.
            if (videoUrl && videoUrl.startsWith("blob:")) URL.revokeObjectURL(videoUrl);
            setVideoUrl(null);
            setVideoFile(null);
            setHomeMarkers([]);
            setAwayMarkers([]);
            setGameNotes("");
            setCurrentSession(null);
            autosaveVideoPathRef.current = null;
            draftSessionIdRef.current = null;
            setAutosaveStatus("idle");
        } catch (e) { }
    }, [videoUrl]);

    const handleDeleteAccount = useCallback(async () => {
        if (!user) return;
        setShowDeleteModal(true);
    }, [user]);

    const handleConfirmedDelete = useCallback(async () => {

        if (!user) return;

        setDeleteLoading(true);

        try {

            const {
                EmailAuthProvider,
                GoogleAuthProvider,
                reauthenticateWithCredential,
                reauthenticateWithPopup,
                deleteUser
            } = window.firebaseAuth;

            const providerId =
                user.providerData[0]?.providerId;


            // --------------------
            // Reauthenticate
            // --------------------

            if (providerId === "password") {

                const credential =
                    EmailAuthProvider.credential(
                        user.email,
                        deletePassword
                    );

                await reauthenticateWithCredential(
                    user,
                    credential
                );

            }
            else if (providerId === "google.com") {

                await reauthenticateWithPopup(
                    user,
                    new GoogleAuthProvider()
                );

            }


            // --------------------
            // Backend
            // --------------------

            const idToken = await user.getIdToken();
            const res = await fetch(
                `${BACKEND_URL}/api/delete-account`,
                {
                    method: "DELETE",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${idToken}`
                    },
                    body: JSON.stringify({
                        uid: user.uid
                    })
                }
            );

            const data = await res.json();

            if (!res.ok || !data.success) {
                throw new Error("Backend deletion failed");
            }


            // --------------------
            // Firestore
            // --------------------

            deletingAccountRef.current = true; // pause autosave so it can't re-create docs mid-delete

            const {
                doc, deleteDoc, updateDoc, collection, query, where,
                getDocs, getDoc, arrayRemove, deleteField
            } = window.firebaseFirestore;
            const { ref, deleteObject } = window.firebaseStorage;

            // Read role/teamIds fresh from Firestore instead of trusting the
            // userProfile captured by this callback -- a stale (or null)
            // closure value used to make the coach/player cleanup silently skip.
            const profileSnap = await getDoc(doc(firebaseDb, "users", user.uid));
            const profile = profileSnap.exists() ? profileSnap.data() : {};
            const role = profile.role || userProfile?.role;
            const teamIds = profile.teamIds || userProfile?.teamIds || [];

            // videoPath looks like videos/{ownerUid}/{sessionId}/original.mp4
            const sessionIdFromVideoPath = (p) =>
                typeof p === "string" ? (p.split("/")[2] || null) : null;
            const warn = (label) => (e) => console.warn(`Delete cleanup (${label}) skipped:`, e?.code || e);

            // 1) Fetch the session snapshots first -- nothing is deleted yet.
            const gameSessionsSnap = await getDocs(
                collection(firebaseDb, "users", user.uid, "gameSessions")
            );
            const ownedSharedSnap = await getDocs(
                query(collection(firebaseDb, "sharedSessions"), where("ownerUid", "==", user.uid))
            );
            const ownedSessionIds = [
                ...gameSessionsSnap.docs.map((d) => d.id),
                ...ownedSharedSnap.docs.map((d) => d.id),
            ];

            // 2) COACH: tear down the team and every route players have to it.
            //    Must run BEFORE sessions are deleted / the coach leaves any
            //    sharedSessions, because the clipGrants rules call
            //    canShareSession(), which needs those docs to still exist.
            if (role === "coach") {
                const sentClipsSnap = await getDocs(
                    query(collection(firebaseDb, "teamClips"), where("coachUid", "==", user.uid))
                );
                const ownedTeamsSnap = await getDocs(
                    query(collection(firebaseDb, "teams"), where("coachUid", "==", user.uid))
                );

                // Grants this coach handed out for sessions they DON'T own
                // (e.g. games they collaborate on) outlive the account unless
                // revoked here. Grants for sessions they own are deleted in step 3.
                const ownedSet = new Set(ownedSessionIds);
                const revoke = {}; // sessionId -> Set(recipient uids)
                sentClipsSnap.docs.forEach((d) => {
                    const data = d.data();
                    const sid = sessionIdFromVideoPath(data.videoPath);
                    if (!sid || ownedSet.has(sid)) return;
                    if (!revoke[sid]) revoke[sid] = new Set();
                    (data.recipientUids || []).forEach((u) => revoke[sid].add(u));
                });
                await Promise.all(
                    Object.entries(revoke).map(([sid, uids]) =>
                        updateDoc(doc(firebaseDb, "clipGrants", sid), { recipientUids: arrayRemove(...uids) })
                            .catch(warn("revoke clipGrant"))
                    )
                );

                await Promise.all(sentClipsSnap.docs.map((d) => deleteDoc(d.ref)));
                await Promise.all(ownedTeamsSnap.docs.map((d) => deleteDoc(d.ref)));
                // Players' own profile docs (teamIds / coachUids) can't be edited
                // from here -- each player's dashboard prunes them itself the
                // next time it loads (see pruneDeletedTeams).
            }

            // 3) PLAYER: remove them from every team, clip share and grant.
            if (role === "player") {
                const receivedClipsSnap = await getDocs(
                    query(collection(firebaseDb, "teamClips"), where("recipientUids", "array-contains", user.uid))
                );

                // Storage-access grants (best effort -- a missing grant doc is fine)
                const grantSessionIds = new Set(
                    receivedClipsSnap.docs.map((d) => sessionIdFromVideoPath(d.data().videoPath)).filter(Boolean)
                );
                await Promise.all(
                    [...grantSessionIds].map((sid) =>
                        updateDoc(doc(firebaseDb, "clipGrants", sid), { recipientUids: arrayRemove(user.uid) })
                            .catch(warn("clipGrant self-remove"))
                    )
                );

                // Clip shares sent to them (needs the new teamClips self-removal rule)
                await Promise.all(
                    receivedClipsSnap.docs.map((d) =>
                        updateDoc(d.ref, { recipientUids: arrayRemove(user.uid) })
                    )
                );

                // Team rosters. Skip teams that no longer exist, but let any
                // real failure (e.g. a rules problem) abort instead of hiding it.
                await Promise.all(
                    teamIds.map(async (teamId) => {
                        const teamRef = doc(firebaseDb, "teams", teamId);
                        const teamSnap = await getDoc(teamRef);
                        if (!teamSnap.exists()) return;
                        await updateDoc(teamRef, { [`players.${user.uid}`]: deleteField() });
                    })
                );
            }

            // 4) clipGrants for sessions this user owns (own gameSessions +
            //    owned sharedSessions share the clipGrants doc id).
            await Promise.all(
                ownedSessionIds.map((sessionId) =>
                    deleteDoc(doc(firebaseDb, "clipGrants", sessionId)).catch(() => { })
                )
            );

            // 5) Personal game sessions (+ their videos)
            await Promise.all(
                gameSessionsSnap.docs.map(async (sessionDoc) => {
                    const videoPath = sessionDoc.data().videoPath;
                    if (videoPath) {
                        await deleteObject(ref(firebaseStorage, videoPath)).catch(() => { });
                    }
                    await deleteDoc(sessionDoc.ref);
                })
            );

            // 6) Shared sessions this user owns (+ their videos)
            await Promise.all(
                ownedSharedSnap.docs.map(async (sharedDoc) => {
                    const videoPath = sharedDoc.data().videoPath;
                    if (videoPath) {
                        await deleteObject(ref(firebaseStorage, videoPath)).catch(() => { });
                    }
                    await deleteDoc(sharedDoc.ref);
                })
            );

            // 7) Shared sessions this user collaborates on but doesn't own
            const collabSharedSnap = await getDocs(
                query(collection(firebaseDb, "sharedSessions"), where("collaborators", "array-contains", user.uid))
            );
            await Promise.all(
                collabSharedSnap.docs
                    .filter((sharedDoc) => sharedDoc.data().ownerUid !== user.uid)
                    .map((sharedDoc) => updateDoc(sharedDoc.ref, { collaborators: arrayRemove(user.uid) }))
            );

            // 8) Autosave pointer doc (+ any orphaned draft video)
            await discardAutosaveDraft(user.uid);

            // 9) Per-user top-level docs. playerClipArchive was missing from
            //    the original delete flow and was left behind forever.
            await deleteDoc(doc(firebaseDb, "playerClipArchive", user.uid));

            // Feedback this user submitted (contains their uid + email).
            // Best effort; needs the updated feedback rules.
            try {
                const feedbackSnap = await getDocs(
                    query(collection(firebaseDb, "feedback"), where("userId", "==", user.uid))
                );
                await Promise.all(feedbackSnap.docs.map((d) => deleteDoc(d.ref)));
            } catch (e) { console.warn("Delete cleanup (feedback) skipped:", e?.code || e); }

            await deleteDoc(doc(firebaseDb, "users", user.uid));
            await deleteDoc(doc(firebaseDb, "settings", user.uid));
            await deleteDoc(doc(firebaseDb, "rosters", user.uid));

            // legalAcceptance had NO rule in the old firestore.rules, so this
            // delete was permission-denied and aborted the whole flow. The new
            // rules fix that; it is best-effort so it can never strand a delete.
            await deleteDoc(doc(firebaseDb, "legalAcceptance", user.uid))
                .catch(warn("legalAcceptance"));


            // --------------------
            // Firebase Auth
            // --------------------

            await deleteUser(user);


            handleRemoveVideo();

            setUser(null);
            setUserProfile(null);
            setNeedsLegalReaccept(false);
            setAuthState("login");

            setShowDeleteModal(false);

        }
        catch (error) {

            deletingAccountRef.current = false;
            console.error("Delete error:", error);
            console.error("Code:", error.code);
            console.error("Message:", error.message);

            if (
                error.code ===
                "auth/wrong-password"
            ) {

                alert("Incorrect password.");

            }
            else {

                alert(
                    error.message
                );

            }

        }
        finally {

            setDeleteLoading(false);

        }

    }, [user, userProfile, deletePassword, handleRemoveVideo]);

    const handleSubscribe = async () => {

        const user = firebaseAuth.currentUser;

        if (!user) {
            alert("Please login first");
            return;
        }

        try {
            const idToken = await user.getIdToken();
            const res = await fetch(
                `${BACKEND_URL}/api/create-checkout-session`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${idToken}`
                    },
                    body: JSON.stringify({
                        firebase_uid: user.uid,
                        email: user.email
                    })
                }
            );

            const data = await res.json();

            if (data.url) {
                window.location.href = data.url;
            }
            else {
                throw new Error("No checkout URL");
            }

        } catch (err) {
            console.error(err);
        }
    }

    const handleManageSubscription = async () => {
        if (!user) return;

        const idToken = await user.getIdToken();
        const res = await fetch(
            `${BACKEND_URL}/api/create-portal-session`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${idToken}`
                },
                body: JSON.stringify({
                    uid: user.uid
                })
            }
        );

        const data = await res.json();

        window.location.href = data.url;
    };

    const handleLoadSession = (data) => {
        if (videoUrl && videoUrl.startsWith("blob:")) URL.revokeObjectURL(videoUrl);
        setVideoUrl(data.videoUrl);
        setVideoFile(data.videoFile);
        setHomeMarkers(data.homeMarkers);
        setAwayMarkers(data.awayMarkers);
        setHomeTables(data.homeTables);
        setAwayTables(data.awayTables);
        setGameNotes(data.gameNotes || "");
        // This session's video already lives at its own permanent path — no
        // draft bookkeeping applies to it.
        autosaveVideoPathRef.current = null;
        draftSessionIdRef.current = null;

        const s = data.settings;
        if (s) {
            if (typeof s.offsetBefore === "number") setOffsetBefore(s.offsetBefore);
            if (typeof s.offsetAfter === "number") setOffsetAfter(s.offsetAfter);
            if (s.offsetMode) setOffsetMode(s.offsetMode);
            if (s.categoricalOffsets) setCategoricalOffsets(s.categoricalOffsets);
            setPlayerTaggingEnabled(typeof s.playerTaggingEnabled === "boolean" ? s.playerTaggingEnabled : false);
            setNotesEnabled(typeof s.notesEnabled === "boolean" ? s.notesEnabled : false);
            setEventCounterEnabled(typeof s.eventCounterEnabled === "boolean" ? s.eventCounterEnabled : false);
            if (typeof s.videoSize === "number") setVideoSize(s.videoSize);
        }

        setActiveTab("marking");
    };

    // Shared between the player and coach layouts below, since both let the user delete their account.
    const deleteAccountModal = showDeleteModal && (
        <div className="modal-overlay">
            <div className="modal">

                <div className="modal-header">
                    <h2>Delete Account</h2>

                    <button onClick={() => {
                        setShowDeleteModal(false);
                        setDeletePassword("");
                    }} className="modal-close">
                        <X size={20} />
                    </button>
                </div>

                {user?.providerData[0]?.providerId === "password" ? (
                    <>
                        <p>Enter your password to continue.</p>

                        <input
                            type="password"
                            value={deletePassword}
                            onChange={(e) =>
                                setDeletePassword(e.target.value)
                            }
                            placeholder="Password"
                        />
                    </>
                ) : (
                    <p>
                        Google will ask you to verify your account.
                    </p>
                )}

                <div
                    style={{
                        display: "flex",
                        gap: "10px",
                        marginTop: "20px"
                    }}
                >
                    <button
                        onClick={handleConfirmedDelete}
                        disabled={deleteLoading}
                        style={{
                            color: "rgb(211, 47, 47)"
                        }}
                    >
                        {deleteLoading
                            ? "Deleting..."
                            : "Delete"}
                    </button>

                </div>

            </div>
        </div>
    );

    if (authState === "loading") return <div className="min-h-screen flex items-center justify-center"><div className="text-center"><div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[var(--accent-home)] to-[color-mix(in_srgb,var(--accent-home)_80%,black)] flex items-center justify-center pulse"><Play className="w-8 h-8 text-white" /></div><p className="text-[--text-secondary]">Loading...</p></div></div>;

    // Announcement banner: shown only if enabled and not already dismissed on this device.
    const visibleAnnouncement = announcement.enabled && announcement.id !== dismissedAnnouncementId ? announcement : null;
    const dismissAnnouncement = () => {
        setDismissedAnnouncementId(announcement.id);
        try { localStorage.setItem(DISMISSED_ANNOUNCEMENT_KEY, announcement.id); } catch (e) { /* storage unavailable — hides for this session only */ }
    };

    // Maintenance lock: everyone except MAINTENANCE_BYPASS_UID sees the lock screen while
    // appConfig/maintenance.enabled is true. The only path to a login form while locked is
    // the small "Staff login" link (login only, no signup).
    if (maintenance.enabled && user?.uid !== MAINTENANCE_BYPASS_UID) {
        if (!user && showStaffLogin) {
            return <div className="min-h-screen flex flex-col"><Header user={null} theme={theme} setTheme={persistTheme} /><main className="flex-1 flex items-center justify-center p-6"><LoginForm onSwitchToSignup={() => setShowStaffLogin(false)} onLogin={handleLogin} onForgotPassword={handleForgotPassword} error={authError} /></main></div>;
        }
        return <MaintenanceScreen message={maintenance.message} signedIn={!!user} user={user} onLogout={handleLogout} onStaffLogin={() => { setAuthError(""); setShowStaffLogin(true); }} theme={theme} setTheme={persistTheme} />;
    }


    if (authState === "login" || authState === "signup") return <div className="min-h-screen flex flex-col"><Header user={null} theme={theme} setTheme={persistTheme} /><main className="flex-1 flex items-center justify-center p-6">{authState === "login" ? <LoginForm onSwitchToSignup={() => { setAuthState("signup"); setAuthError(""); }} onLogin={handleLogin} onForgotPassword={handleForgotPassword} error={authError} /> : <SignupForm onSwitchToLogin={() => { setAuthState("login"); setAuthError(""); }} onSignup={handleSignup} error={authError} />}</main><AuthFooter /></div>;

    if (authState === "survey") return <div className="min-h-screen flex flex-col"><Header user={null} theme={theme} setTheme={persistTheme} /><main className="flex-1 flex items-center justify-center p-6"><SurveyForm onComplete={handleSurveyComplete} /></main></div>;

    if (authState === "role") return <div className="min-h-screen flex flex-col"><Header user={null} theme={theme} setTheme={persistTheme} /><main className="flex-1 flex items-center justify-center p-6"><RoleForm onSelect={handleRoleSelect} /></main></div>;

    if (authState === "joinTeam") return <div className="min-h-screen flex flex-col"><Header user={null} theme={theme} setTheme={persistTheme} /><main className="flex-1 flex items-center justify-center p-6"><PlayerOnboardForm userId={user?.uid} userEmail={user?.email} onComplete={handlePlayerOnboardComplete} /></main></div>;

    // Terms/Privacy re-acceptance gate. Structural, like the player layout below: none of
    // the app renders until the current LEGAL_VERSION is accepted (or the user logs out).
    if (authState === "authenticated" && needsLegalReaccept) {
        return (
            <div className="min-h-screen flex flex-col">
                <Header user={user} onLogout={handleLogout} theme={theme} setTheme={persistTheme} />
                <main className="flex-1">
                    <LegalReacceptModal onAccept={handleAcceptLegal} onLogout={handleLogout} />
                </main>
            </div>
        );
    }

    // Player accounts get a locked-down layout — no full TabNav, no marking/stats/videos
    // tools. This is a structural gate, not CSS hiding: those components never even render
    // for a player. They do get a small Dashboard/Profile nav of their own.
    if (authState === "authenticated" && userProfile?.role === "player") {
        return (
            <div className="min-h-screen flex flex-col" data-testid="main-app">
                <AnnouncementBanner announcement={visibleAnnouncement} onDismiss={dismissAnnouncement} />
                <Header user={user} onLogout={handleLogout} theme={theme} setTheme={persistTheme} />
                <PlayerTabNav activeTab={playerTab} onTabChange={setPlayerTab} />
                <main className="flex-1">
                    {playerTab === "dashboard" && <PlayerDashboard userId={user.uid} userProfile={userProfile} onProfileChange={(patch) => setUserProfile((prev) => ({ ...prev, ...patch }))} />}
                    {playerTab === "profile" && <PlayerProfileTab userId={user.uid} userProfile={userProfile} onTeamJoined={handleTeamJoined} onDeleteAccount={handleDeleteAccount} />}
                    {playerTab === "feedback" && <FeedbackTab userId={user.uid} userEmail={user.email} />}
                </main>
                {deleteAccountModal}
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col" data-testid="main-app">
            <AnnouncementBanner announcement={visibleAnnouncement} onDismiss={dismissAnnouncement} />
            <Header user={user} onLogout={handleLogout} theme={theme} setTheme={persistTheme} autosaveStatus={autosaveStatus} />
            <TabNav activeTab={activeTab} onTabChange={setActiveTab} role={userProfile?.role} />
            <main className="flex-1">
                <div style={{ display: activeTab === "marking" ? "block" : "none" }}>
                    <MarkingTab markers={markers} videoUrl={videoUrl} videoFile={videoFile} onVideoChange={handleVideoChange} onRemoveVideo={handleRemoveVideo} homeMarkers={homeMarkers} awayMarkers={awayMarkers} onAddMarker={handleAddMarker} videoRef={videoRef} setShowPremiumModal={setShowPremiumModal} isPremium={isPremium} playerTaggingEnabled={effPlayerTaggingEnabled} homeTables={homeTables} awayTables={awayTables} setHomeTables={setHomeTables} setAwayTables={setAwayTables} notesEnabled={effNotesEnabled} offsetMode={effOffsetMode} offsetBefore={offsetBefore} offsetAfter={offsetAfter} categoricalOffsets={categoricalOffsets} eventCounterEnabled={effEventCounterEnabled} gameNotes={gameNotes} setGameNotes={setGameNotes} pauseOnMarkEnabled={pauseOnMarkEnabled} onPositionChange={handleVideoPositionChange} />
                </div>
                {activeTab === "live" && <LiveTab markers={markers} homeMarkers={homeMarkers} awayMarkers={awayMarkers} onAddMarker={handleAddMarker} onVideoChange={handleVideoChange} offsetBefore={offsetBefore} offsetAfter={offsetAfter} offsetMode={effOffsetMode} categoricalOffsets={categoricalOffsets} isPremium={isPremium} playerTaggingEnabled={effPlayerTaggingEnabled} notesEnabled={effNotesEnabled} homeTables={homeTables} awayTables={awayTables} setHomeTables={setHomeTables} setAwayTables={setAwayTables} eventCounterEnabled={effEventCounterEnabled} gameNotes={gameNotes} setGameNotes={setGameNotes} />}
                {activeTab === "team" && <TeamTab userId={user?.uid} userProfile={userProfile} isPremium={isPremium} setShowPremiumModal={setShowPremiumModal} homeTables={homeTables} awayTables={awayTables} pendingClipShare={pendingClipShare} onPendingClipShareHandled={() => setPendingClipShare(null)} />}
                {activeTab === "stats" && <StatsTab userId={user?.uid} sport={sport} setShowPremiumModal={setShowPremiumModal} isPremium={isPremium} homeTables={homeTables} setHomeTables={setHomeTables} awayTables={awayTables} setAwayTables={setAwayTables} hasRoster={hasRoster} setHasRoster={setHasRoster} />}
                {activeTab === "videos" && <VideosTab homeMarkers={homeMarkers} awayMarkers={awayMarkers} videoUrl={videoUrl} videoFile={videoFile} offsetBefore={offsetBefore} offsetAfter={offsetAfter} setShowPremiumModal={setShowPremiumModal} isPremium={isPremium} playerTaggingEnabled={effPlayerTaggingEnabled} onShareClip={handleShareClipRequest} onDeleteMarker={handleDeleteMarker} notesEnabled={effNotesEnabled} offsetMode={effOffsetMode} categoricalOffsets={categoricalOffsets} homeTables={homeTables} awayTables={awayTables} setHomeTables={setHomeTables} setAwayTables={setAwayTables} onUpdateMarker={handleUpdateMarker} onGoToTimestamp={handleGoToTimestamp} />}
                {activeTab === "settings" && <SettingsTab userProfile={userProfile} setShowPremiumModal={setShowPremiumModal} markers={markers} setMarkers={persistMarkers} offsetBefore={offsetBefore} setOffsetBefore={persistOffsetBefore} offsetAfter={offsetAfter} setOffsetAfter={persistOffsetAfter} offsetMode={effOffsetMode} setOffsetMode={persistOffsetMode} categoricalOffsets={categoricalOffsets} setCategoricalOffsets={persistCategoricalOffsets} videoSize={videoSize} setVideoSize={persistVideoSize} theme={theme} setTheme={persistTheme} sport={sport} onSportChange={handleSportChange} onDeleteAccount={handleDeleteAccount} onSubscribe={handleSubscribe} onManageSubscription={handleManageSubscription} isPremium={isPremium} playerTaggingEnabled={effPlayerTaggingEnabled} setPlayerTaggingEnabled={persistPlayerTaggingEnabled} homeTables={homeTables} awayTables={awayTables} notesEnabled={effNotesEnabled} setNotesEnabled={persistNotesEnabled} eventCounterEnabled={effEventCounterEnabled} setEventCounterEnabled={persistEventCounterEnabled} pauseOnMarkEnabled={pauseOnMarkEnabled} setPauseOnMarkEnabled={persistPauseOnMarkEnabled} authProviderId={user?.providerData[0]?.providerId} onChangePassword={handleChangePassword} />}
                {activeTab === "games" && <GameSessionsTab userId={user?.uid} videoFile={videoFile} draftSessionId={draftSessionIdRef.current} draftVideoPath={autosaveVideoPathRef.current} homeMarkers={homeMarkers} awayMarkers={awayMarkers} homeTables={homeTables} awayTables={awayTables} isPremium={isPremium} setShowPremiumModal={setShowPremiumModal} onLoadSession={handleLoadSession} offsetBefore={offsetBefore} offsetAfter={offsetAfter} offsetMode={offsetMode} categoricalOffsets={categoricalOffsets} playerTaggingEnabled={playerTaggingEnabled} notesEnabled={notesEnabled} eventCounterEnabled={eventCounterEnabled} videoSize={videoSize} currentSession={currentSession} setCurrentSession={setCurrentSession} gameNotes={gameNotes} onShareClip={handleShareClipRequest} />}
                {activeTab === "feedback" && <FeedbackTab userId={user?.uid} userEmail={user?.email} />}

            </main>

            {deleteAccountModal}

            {pendingSportChange && (
                <div className="modal-overlay" data-testid="sport-change-modal">
                    <div className="modal">
                        <div className="modal-header">
                            <h2>Switch to {pendingSportChange.newSport}?</h2>
                            <button onClick={() => { pendingSportChange.resolve(false); setPendingSportChange(null); }} className="modal-close">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-[--bg-tertiary]">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[--accent-home]/15">
                                <Trophy className="w-5 h-5 text-[--accent-home]" />
                            </div>
                            <div>
                                <p className="text-[--text-primary] font-semibold">{sport} <ChevronRight className="inline w-4 h-4 text-[--text-muted]" /> {pendingSportChange.newSport}</p>
                                <p className="text-xs text-[--text-muted]">Markers, colors, and stat columns will update</p>
                            </div>
                        </div>

                        <p className="text-sm text-[--text-secondary]">
                            This will rebuild your current stat sheet with {pendingSportChange.newSport}'s columns. Player names and numbers carry over, but the stat values you've entered won't.
                        </p>

                        <div className="modal-buttons flex gap-2 mt-4">
                            <button
                                onClick={() => { pendingSportChange.resolve(true); setPendingSportChange(null); }}
                                className="btn-primary flex-1"
                                data-testid="sport-change-confirm"
                            >
                                Switch to {pendingSportChange.newSport}
                            </button>
                            <button
                                onClick={() => { pendingSportChange.resolve(false); setPendingSportChange(null); }}
                                className="btn-secondary"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {pendingAutosaveRestore && (
                <div className="modal-overlay" data-testid="autosave-restore-modal">
                    <div className="modal">
                        <div className="modal-header">
                            <h2>Resume unsaved game?</h2>
                            <button onClick={handleDiscardAutosave} className="modal-close">
                                <X size={20} />
                            </button>
                        </div>

                        <p className="text-sm text-[--text-secondary]">
                            It looks like a game wasn't saved before your last session ended. We kept an autosaved copy of the video, markers, and stats — want to pick up where you left off?
                        </p>

                        <div className="modal-buttons flex gap-2 mt-4">
                            <button onClick={handleRestoreAutosave} className="btn-primary flex-1" data-testid="autosave-restore-confirm">
                                Restore
                            </button>
                            <button onClick={handleDiscardAutosave} className="btn-secondary" data-testid="autosave-restore-discard">
                                Discard
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {pendingSessionResume && (
                <div className="modal-overlay" data-testid="session-resume-modal">
                    <div className="modal">
                        <div className="modal-header">
                            <h2>Continue your last game?</h2>
                            <button onClick={handleDismissSessionResume} className="modal-close">
                                <X size={20} />
                            </button>
                        </div>

                        <p className="text-sm text-[--text-secondary]">
                            You had a saved game open when your last session ended. It's already saved — just pick up where you left off, or dismiss this and open it later from the Games tab.
                        </p>

                        <div className="modal-buttons flex gap-2 mt-4">
                            <button onClick={handleResumeSession} className="btn-primary flex-1" data-testid="session-resume-confirm">
                                Continue
                            </button>
                            <button onClick={handleDismissSessionResume} className="btn-secondary" data-testid="session-resume-dismiss">
                                Dismiss
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showPremiumModal && (
                <div className="modal-overlay">
                    <div className="modal">

                        <div className="modal-header">
                            <h2>Premium Feature</h2>

                            <button onClick={() => {
                                setShowPremiumModal(false);
                            }} className="modal-close">
                                <X size={20} />
                            </button>
                        </div>

                        <p>
                            Upgrade to SnipShot+ to use this feature.
                        </p>

                        <div className="modal-buttons">

                            <button
                                className="w-full bg-[--accent-home] text-white py-3 rounded-lg font-semibold"
                                onClick={() => {
                                    setShowPremiumModal(false);
                                    handleSubscribe();
                                }}
                            >
                                Upgrade
                            </button>

                        </div>

                    </div>
                </div>
            )}
        </div>
    );
}

export default App;